import { parseReview, capacityLabel, preciseLocation } from '../lib/review-evidence';
import { pipelineCapacityDisplay } from '../lib/pipeline-capacity';
import { pendingFemaLabel, pendingFemaLabels, pendingFloodLayers, PENDING_FEMA_COLOR } from '../lib/fema-pending';
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { createRoot, Root } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import type { FilterSpecification } from 'mapbox-gl';
import { SavePinPopup } from './SavePinPopup';
import { getMapboxToken, getSlopeTilesUrl, getSlope1mTilesUrl } from '../lib/config';
import turfCircle from '@turf/circle';
import turfDistance from '@turf/distance';
import {
  fetchPipelinesGeoJSON,
  fetchElectricTerritoriesGeoJSON, fetchFederalLandsGeoJSON, fetchCrownPineTractsGeoJSON, fetchAssemblyGroupsGeoJSON,
  fetchPowerGeoJSON, fetchOsmSwitchyardsGeoJSON, fetchQueueHeatmapGeoJSON, fetchCongestionHeatmapGeoJSON, fetchAirportsGeoJSON,
  fetchFiberGeoJSON, fetchCarrierRoutesGeoJSON, fetchFiberRowPipelinesGeoJSON, fetchIxpFacilitiesGeoJSON, fetchWaterGeoJSON, fetchAquifersGeoJSON,
  fetchFloodZonesGeoJSON, fetchPendingFloodZonesGeoJSON, fetchTwdbFloodHazardsGeoJSON, fetchHighwaysGeoJSON, fetchRoadsGeoJSON, fetchOsmPipelineCandidatesGeoJSON, fetchRailroadsGeoJSON,
  roadSurfaceShort, truckShareWords, trafficWord,
  fetchDataCentersGeoJSON, fetchWellsGeoJSON, fetchStateTrustLandsGeoJSON, fetchProtectedAreasGeoJSON, fetchOperatorAoisGeoJSON, fetchLandListingsGeoJSON, fetchGridListingsGeoJSON,
  fetchGasPlantsGeoJSON, fetchGasInfraGeoJSON, fetchEpaZonesGeoJSON, fetchEjScreenGeoJSON,
  fetchCriticalHabitatGeoJSON, fetchSeismicRiskGeoJSON, fetchUrbanAreasGeoJSON, fetchWildfireGeoJSON, fetchWaterRegulatoryZonesGeoJSON, fetchTwdbWellsGeoJSON, fetchMineSubsidenceGeoJSON, fetchAmlSitesGeoJSON,
  fetchBrackishZonesGeoJSON, fetchTdsSamplesGeoJSON,
  getParcelTileUrl, getWetlandsTileUrl,
  type Filters, type PipelineGroup, type PipelineFilters,
} from '../lib/api';
import { designTokens } from '../styles/design-tokens';
import { useMemphisResearchMap, hasMemphisResearchHit } from './memphis/memphis-map';
import { EMPTY_MEMPHIS_LAYERS, type MemphisLayers } from '../data/memphis-research';
import { useEtResearchMap, hasEtResearchHit } from './et/et-map';
import { EMPTY_ET_LAYERS, type EtLayers, type EtResearchPayload } from '../data/et-research';
import { GEORGIA_SITES } from '../data/georgia-sites';
import { GEORGIA_EVIDENCE } from '../data/georgia-evidence';
import { useTheme } from '../context/ThemeContext';
import { useMeasurement, formatDistance } from '../hooks/useMeasurement';
import { useMapMeasurement } from '../hooks/useMapMeasurement';
import { useListingLayer } from '../hooks/useListingLayer';
import { Ruler, SquareDashed, X } from 'lucide-react';
import type { PipelineProperties } from './PipelineDetail';
import type { DataCenterProperties } from './DataCenterDetail';
import type { LandListingProperties } from './LandListingDetail';
import type { FloodZoneProperties } from './FloodZoneDetail';
import type { MineSubsidenceProperties } from './MineSubsidenceDetail';
import type { AmlSiteProperties } from './AmlSiteDetail';
import type { WildfireProperties } from './WildfireDetail';
import type { UrbanAreaProperties } from './UrbanAreaDetail';
import type { SeismicRiskProperties } from './SeismicRiskDetail';
import type { WaterRegZoneProperties } from './WaterRegZoneDetail';
import type { GasInfraProperties } from './GasInfraDetail';
import type { IxpFacilityProperties } from './IxpFacilityDetail';
import type { SubstationProperties } from './SubstationDetail';
import type { AirportProperties } from './AirportDetail';
import type { OsmSwitchyardProperties } from './OsmSwitchyardDetail';
import type { QueueCongestionProperties } from './QueueCongestionDetail';
import type { BrackishZoneProperties } from './BrackishZoneDetail';
import type { TdsSampleProperties } from './TdsSampleDetail';
import type { WaterWellProperties } from './WaterWellDetail';
import { COUNTIES, COUNTY_BOUNDS } from '../data/counties';

const NTIA_CARRIER_NAMES = ['Concho Valley Electric', 'ENMR Telephone', 'Kansas DOC', 'Pima County'] as const;
const NTIA_CARRIER_COLOR = '#e11d48';

export interface OperatorInfo {
  name: string;
  count: number;
}

export type PipelineLoadingState = Record<PipelineGroup, boolean>;

interface MapViewProps {
  filters: Filters;
  pipelineFilters: PipelineFilters;
  selectedParcelId: string | null;
  show3DTerrain: boolean;
  onTerrainChange: (enabled: boolean) => void;
  onSelectParcel: (id: string | null) => void;
  onSelectPipeline: (pipeline: PipelineProperties | null) => void;
  onSelectCarrierFiber: (carrier: string | null) => void;
  onSelectRailroad?: (rr: import('./RailroadDetail').RailroadProperties | null) => void;
  onParcelCountChange: (count: number) => void;
  onParcelLoadingChange?: (loading: boolean) => void;
  showParcels: boolean;
  showFederalLands: boolean;
  showCrownPineTracts: boolean;
  showSlopeDrape: boolean;
  showSlope1m: boolean;
  slopeDrapeOpacity: number;
  showAssemblyGroups: boolean;
  showGathering: boolean;
  showGasTransmission: boolean;
  showLiquidsTransmission: boolean;
  showDesertSw: boolean;
  onOperatorsLoaded: (operators: OperatorInfo[]) => void;
  // Company attribution for the pipeline popup (pipeline company registry via the catalog).
  companyLabelFor?: (operator: string) => string | null;
  hiddenOperators: string[];
  onPipelineLoadingChange?: (loading: PipelineLoadingState) => void;
  onPipelineCountChange?: (count: number) => void;
  showPowerInfra: boolean;
  onPowerLoadingChange?: (loading: boolean) => void;
  showOsmSwitchyards: boolean;
  osmSwitchyardsHifldNewOnly: boolean;
  onOsmSwitchyardsLoadingChange?: (loading: boolean) => void;
  onSelectOsmSwitchyard?: (sw: OsmSwitchyardProperties | null) => void;
  showGridCapacity: boolean;
  onGridCapacityLoadingChange?: (loading: boolean) => void;
  gridCapacityTiers: Set<string>;
  onSelectSubstation?: (sub: SubstationProperties | null) => void;
  showQueueHeatmap: boolean;
  onQueueHeatmapLoadingChange?: (loading: boolean) => void;
  showCongestionHeatmap: boolean;
  onCongestionHeatmapLoadingChange?: (loading: boolean) => void;
  onSelectCongestionCounty?: (cc: import('./TransmissionCongestionDetail').TransmissionCongestionProperties | null) => void;
  onSelectQueueCounty?: (qc: QueueCongestionProperties | null) => void;
  showElectricTerritories: boolean;
  onSelectElectricTerritory?: (t: import('./ElectricTerritoryDetail').ElectricTerritoryProperties | null) => void;
  showAirportZones: boolean;
  onAirportLoadingChange?: (loading: boolean) => void;
  airportFilter: 'all' | 'commercial';
  onSelectAirport?: (ap: AirportProperties | null) => void;
  showFiber: boolean;
  onFiberLoadingChange?: (loading: boolean) => void;
  onFiberCountChange?: (count: number) => void;
  showSegra: boolean;
  showZayo: boolean;
  showFiberLight: boolean;
  showNtiaMiddleMile: boolean;
  showFiberRowPipelines: boolean;
  showIxpFacilities: boolean;
  showWater: boolean;
  onWaterLoadingChange?: (loading: boolean) => void;
  showFloodZones: boolean;
  onFloodZoneLoadingChange?: (loading: boolean) => void;
  onSelectFloodZone?: (fz: FloodZoneProperties | null) => void;
  showTwdbFloodHazards: boolean;
  onTwdbFloodHazardsLoadingChange?: (loading: boolean) => void;
  onSelectMineSubsidence?: (z: MineSubsidenceProperties | null) => void;
  onSelectAmlSite?: (s: AmlSiteProperties | null) => void;
  onSelectWildfireCounty?: (c: WildfireProperties | null) => void;
  onSelectUrbanArea?: (a: UrbanAreaProperties | null) => void;
  onSelectSeismicPoint?: (p: SeismicRiskProperties | null) => void;
  showHighways: boolean;
  onHighwayLoadingChange?: (loading: boolean) => void;
  showRoads: boolean;
  onRoadsLoadingChange?: (loading: boolean) => void;
  showOsmPipelineCandidates?: boolean;
  onOsmPipelineCandidatesLoadingChange?: (loading: boolean) => void;
  memphisLayers?: MemphisLayers;
  etLayers?: EtLayers;
  etData?: EtResearchPayload | null;
  showGeorgiaSites?: boolean;
  showGeorgiaEvidence?: boolean;
  showRailroads: boolean;
  onRailroadLoadingChange?: (loading: boolean) => void;
  showDataCenters: boolean;
  onDataCenterLoadingChange?: (loading: boolean) => void;
  onSelectDataCenter?: (dc: DataCenterProperties | null) => void;
  showGasWells: boolean;
  showOilWells: boolean;
  onWellsLoadingChange?: (loading: boolean) => void;
  onWellsCountChange?: (count: number) => void;
  onSelectWell?: (wellId: number | null, flaringMcfDay?: number) => void;
  showStateTrustLands: boolean;
  onTrustLandLoadingChange?: (loading: boolean) => void;
  showProtectedAreas: boolean;
  showOperatorAois: boolean;
  showLandListings: boolean;
  onListingsLoadingChange?: (loading: boolean) => void;
  showGridListings: boolean;
  onGridListingsLoadingChange?: (loading: boolean) => void;
  onSelectListing?: (listing: LandListingProperties | null) => void;
  showGasPlants: boolean;
  onGasPlantLoadingChange?: (loading: boolean) => void;
  showCompressors: boolean;
  showReceiptDelivery: boolean;
  showInterconnects: boolean;
  showGasStorage: boolean;
  onGasInfraLoadingChange?: (loading: boolean) => void;
  onSelectGasInfra?: (gi: GasInfraProperties | null) => void;
  onSelectIxpFacility?: (fac: IxpFacilityProperties | null) => void;
  onSelectWaterRegZone?: (zone: WaterRegZoneProperties | null) => void;
  onSelectBrackishZone?: (zone: BrackishZoneProperties | null) => void;
  onSelectTdsSample?: (sample: TdsSampleProperties | null) => void;
  onSelectWaterWell?: (well: WaterWellProperties | null) => void;
  showEpaZones: boolean;
  onEpaZoneLoadingChange?: (loading: boolean) => void;
  showEjScreen: boolean;
  onEjScreenLoadingChange?: (loading: boolean) => void;
  showCriticalHabitat: boolean;
  showWetlands: boolean;
  showSeismicRisk: boolean;
  showUrbanAreas: boolean;
  showWildfire: boolean;
  onWildfireLoadingChange?: (loading: boolean) => void;
  showMineSubsidence: boolean;
  showAmlSites: boolean;
  showWaterRegulatoryZones: boolean;
  showTwdbWells: boolean;
  showBrackish: boolean;
  showTdsSamples: boolean;
  parcelCount: number;
  pipelineCount: number;
  wellCount: number;
  fiberCount: number;
  measurementActive: boolean;
  onMeasurementActiveChange: (active: boolean) => void;
  areaActive: boolean;
  onAreaActiveChange: (active: boolean) => void;
  isSatellite: boolean;
  onToggleSatellite: () => void;
  showCountyLines: boolean;
  onToggleCountyLines: () => void;
  onCursorMove?: (coords: { lat: number; lng: number } | null) => void;
  onZoomChange?: (zoom: number) => void;
  selectedParcelCounty?: string | null;
  pendingCountyZoom?: [number, number, number, number] | null;
  onCountyZoomConsumed?: () => void;
  pendingFlyTo?: [number, number] | null;
  // [west, south, east, north] — fit the map to an extent (operator focus "zoom to")
  pendingFitBounds?: [number, number, number, number] | null;
  onFitBoundsConsumed?: () => void;
  onFlyToConsumed?: () => void;
  searchPin?: [number, number] | null;
  onSearchPinClear?: () => void;
  showSavedLocations: boolean;
  savedLocations: import('../lib/api').SavedLocation[];
  onSaveLocation: (input: import('../lib/api').SaveLocationInput) => Promise<import('../lib/api').SavedLocation>;
  pendingSavedFlyTo: { lat: number; lng: number; source_type: string | null; source_id: string | null } | null;
  onSavedFlyToHandled: () => void;
}

const MAP_CENTER: [number, number] = [-99.5, 31.3];  // Texas center
const INITIAL_ZOOM = 6;

const MIN_PARCEL_ZOOM = 6;
const MIN_PARCEL_POLYGON_ZOOM = 8;
const MIN_WELLS_ZOOM = 8;
const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
const ET_TYPE_COLORS: Record<string, string> = { 'IOU': '#d3102f', 'COOP': '#64b5f3', 'MUNI': '#f59e0b' };

let countyBoundaryCache: { boundaries: GeoJSON.FeatureCollection; centroids: GeoJSON.FeatureCollection } | null = null;

function polyBboxCenter(geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): [number, number] {
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  const visit = (a: unknown): void => {
    if (typeof (a as number[][])[0]?.[0] === 'number') {
      for (const c of a as number[][]) {
        if (c[0] < minLng) minLng = c[0];
        if (c[0] > maxLng) maxLng = c[0];
        if (c[1] < minLat) minLat = c[1];
        if (c[1] > maxLat) maxLat = c[1];
      }
    } else { for (const item of a as unknown[]) visit(item); }
  };
  visit(geometry.coordinates);
  return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}

// Roads layer ids, bottom of the stack first. Each is inserted before 'highways-line',
// so listing them in this order leaves the primary roads on top of the county ones.
const ROADS_LAYERS = ['roads-county-uncertified', 'roads-county', 'roads-fm', 'roads-primary'];

// OSM pipeline candidates — the separately attributed, non-scoring overlay (migration 192).
// Dashed line plus an along-line operator label; both hidden until the toggle turns them on.
const OSM_PIPELINE_CANDIDATE_LAYERS = ['osm-pipeline-candidates-line', 'osm-pipeline-candidates-label'];

// Georgia research site outline (static county polygon from src/data/georgia-sites.ts).
const GEORGIA_SITE_LAYERS = ['georgia-sites-fill', 'georgia-sites-outline', 'georgia-sites-label'];
// Located diameter / capacity / valve / meter evidence pins (static, src/data/georgia-evidence.ts).
const GEORGIA_EVIDENCE_LAYERS = ['georgia-evidence-circle', 'georgia-evidence-label'];

const PIPELINE_LAYERS: { group: PipelineGroup; sourceId: string; layerId: string; color: string; width: number; label: string; minzoom: number }[] = [
  { group: 'gathering', sourceId: 'pipelines-gathering', layerId: 'pipelines-gathering-line', color: designTokens.colors.pipelineGathering, width: 2, label: 'Gathering', minzoom: 9 },
  { group: 'gas_transmission', sourceId: 'pipelines-gas-transmission', layerId: 'pipelines-gas-transmission-line', color: designTokens.colors.pipelineGasTransmission, width: 3, label: 'Gas Transmission', minzoom: 4 },
  { group: 'liquids_transmission', sourceId: 'pipelines-liquids-transmission', layerId: 'pipelines-liquids-transmission-line', color: designTokens.colors.pipelineLiquidsTransmission, width: 3, label: 'Liquids Transmission', minzoom: 9 },
  { group: 'desert_sw', sourceId: 'pipelines-desert-sw', layerId: 'pipelines-desert-sw-line', color: '#C4705A', width: 5, label: 'Desert Southwest', minzoom: 4 },
];
// Lowest zoom at which any pipeline layer draws.
const PIPELINE_MIN_ZOOM = Math.min(...PIPELINE_LAYERS.map(pl => pl.minzoom));

export const MapView: React.FC<MapViewProps> = ({
  filters, pipelineFilters, selectedParcelId, onSelectParcel, onSelectPipeline, onSelectCarrierFiber, onSelectRailroad, onParcelCountChange, onParcelLoadingChange,
  showParcels,
  show3DTerrain, onTerrainChange,
  showFederalLands,
  showCrownPineTracts,
  showSlopeDrape, showSlope1m, slopeDrapeOpacity,
  showAssemblyGroups,
  showGathering, showGasTransmission, showLiquidsTransmission, showDesertSw,
  onOperatorsLoaded, companyLabelFor, hiddenOperators, onPipelineLoadingChange, onPipelineCountChange,
  showPowerInfra, onPowerLoadingChange,
  showOsmSwitchyards, osmSwitchyardsHifldNewOnly, onOsmSwitchyardsLoadingChange, onSelectOsmSwitchyard,
  showGridCapacity, onGridCapacityLoadingChange, gridCapacityTiers, onSelectSubstation,
  showQueueHeatmap, onQueueHeatmapLoadingChange, onSelectQueueCounty,
  showCongestionHeatmap, onCongestionHeatmapLoadingChange, onSelectCongestionCounty,
  showElectricTerritories, onSelectElectricTerritory,
  showAirportZones, onAirportLoadingChange, airportFilter, onSelectAirport,
  showFiber, onFiberLoadingChange, onFiberCountChange,
  showSegra, showZayo, showFiberLight, showNtiaMiddleMile,
  showFiberRowPipelines,
  showIxpFacilities,
  showWater, onWaterLoadingChange,
  showFloodZones, onFloodZoneLoadingChange, onSelectFloodZone,
  showTwdbFloodHazards, onTwdbFloodHazardsLoadingChange,
  onSelectMineSubsidence, onSelectAmlSite, onSelectWildfireCounty, onSelectUrbanArea, onSelectSeismicPoint,
  showHighways, onHighwayLoadingChange,
  showRoads, onRoadsLoadingChange,
  showOsmPipelineCandidates = false, onOsmPipelineCandidatesLoadingChange,
  memphisLayers = EMPTY_MEMPHIS_LAYERS,
  etLayers = EMPTY_ET_LAYERS,
  etData = null,
  showGeorgiaSites = false,
  showGeorgiaEvidence = false,
  showRailroads, onRailroadLoadingChange,
  showDataCenters, onDataCenterLoadingChange, onSelectDataCenter,
  showGasWells, showOilWells, onWellsLoadingChange, onWellsCountChange,
  onSelectWell,
  showStateTrustLands, onTrustLandLoadingChange, showProtectedAreas, showOperatorAois,
  showLandListings, onListingsLoadingChange, showGridListings, onGridListingsLoadingChange, onSelectListing,
  showGasPlants, onGasPlantLoadingChange,
  showCompressors, showReceiptDelivery, showInterconnects, showGasStorage, onGasInfraLoadingChange, onSelectGasInfra, onSelectIxpFacility, onSelectWaterRegZone,
  onSelectBrackishZone, onSelectTdsSample, onSelectWaterWell,
  showEpaZones, onEpaZoneLoadingChange,
  showEjScreen, onEjScreenLoadingChange,
  showCriticalHabitat, showWetlands, showSeismicRisk, showUrbanAreas, showWildfire, onWildfireLoadingChange, showMineSubsidence, showAmlSites, showWaterRegulatoryZones, showTwdbWells,
  showBrackish, showTdsSamples,
  parcelCount, pipelineCount, wellCount, fiberCount,
  measurementActive, onMeasurementActiveChange,
  areaActive, onAreaActiveChange,
  isSatellite, onToggleSatellite, showCountyLines, onToggleCountyLines,
  onCursorMove, onZoomChange, selectedParcelCounty,
  pendingCountyZoom, onCountyZoomConsumed, pendingFlyTo, onFlyToConsumed, pendingFitBounds, onFitBoundsConsumed, searchPin, onSearchPinClear,
  showSavedLocations, savedLocations, onSaveLocation,
  pendingSavedFlyTo, onSavedFlyToHandled,
}) => {
  const { isDark } = useTheme();
  const countyLabel = filters.county ? COUNTIES.find(c => c.value === filters.county)?.label : undefined;
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const initialMapLoadCompleteRef = useRef(false);
  const appliedTerrainModeRef = useRef<boolean | null>(null);
  // Bumped after a basemap swap has re-added our own sources/layers, so the
  // shared measurement hook knows to re-add its area layers on top of them.
  const [styleEpoch, setStyleEpoch] = useState(0);
  // East TX relief is real (Tyler/Jasper/Polk avg 4%+ slope) but shallow — ~200 ft
  // over a 5-mile view reads as flat at 1x. 5x is the default that makes it legible.
  const [terrainExaggeration, setTerrainExaggeration] = useState(5);
  const [zoomLevel, setZoomLevel] = useState(INITIAL_ZOOM);
  const [pendingFloodLabels, setPendingFloodLabels] = useState<string[]>([]);
  const [floodTruncated, setFloodTruncated] = useState(false);
  const [wellsTruncated, setWellsTruncated] = useState(false);
  const [fiberTruncated, setFiberTruncated] = useState(false);
  const electricTerritoriesAbortRef = useRef<AbortController | null>(null);
  const federalLandsAbortRef = useRef<AbortController | null>(null);
  const crownPineTractsAbortRef = useRef<AbortController | null>(null);
  const assemblyGroupsAbortRef = useRef<AbortController | null>(null);
  const powerAbortRef = useRef<AbortController | null>(null);
  const osmSwitchyardsAbortRef = useRef<AbortController | null>(null);
  const gridCapacityAbortRef = useRef<AbortController | null>(null);
  const queueHeatmapAbortRef = useRef<AbortController | null>(null);
  const congestionHeatmapAbortRef = useRef<AbortController | null>(null);
  const airportAbortRef = useRef<AbortController | null>(null);
  const fiberAbortRef = useRef<AbortController | null>(null);
  const carrierRoutesAbortRef = useRef<AbortController | null>(null);
  const fiberRowPipelinesAbortRef = useRef<AbortController | null>(null);
  const ixpAbortRef = useRef<AbortController | null>(null);
  const waterAbortRef = useRef<AbortController | null>(null);
  const floodZoneAbortRef = useRef<AbortController | null>(null);
  const twdbFloodHazardsAbortRef = useRef<AbortController | null>(null);
  const highwayAbortRef = useRef<AbortController | null>(null);
  const roadsAbortRef = useRef<AbortController | null>(null);
  const osmPipelineCandidatesAbortRef = useRef<AbortController | null>(null);
  const railroadAbortRef = useRef<AbortController | null>(null);
  const dcAbortRef = useRef<AbortController | null>(null);
  const wellsAbortRef = useRef<AbortController | null>(null);
  const trustLandAbortRef = useRef<AbortController | null>(null);
  const protectedAreasAbortRef = useRef<AbortController | null>(null);
  const operatorAoisAbortRef = useRef<AbortController | null>(null);
  const operatorAoisLoadedRef = useRef(false);
  const gasPlantAbortRef = useRef<AbortController | null>(null);
  const gasInfraAbortRefs = useRef<Record<string, AbortController | null>>({});
  const gasInfraLoadingCount = useRef(0);
  const epaZoneAbortRef = useRef<AbortController | null>(null);
  const ejScreenAbortRef = useRef<AbortController | null>(null);
  const criticalHabitatAbortRef = useRef<AbortController | null>(null);
  const seismicRiskAbortRef = useRef<AbortController | null>(null);
  const urbanAreasAbortRef = useRef<AbortController | null>(null);
  const wildfireAbortRef = useRef<AbortController | null>(null);
  const mineSubsidenceAbortRef = useRef<AbortController | null>(null);
  const amlSitesAbortRef = useRef<AbortController | null>(null);
  const waterRegZonesAbortRef = useRef<AbortController | null>(null);
  // Active save-pin popup cleanup — dismisses the previous popup on rapid right-click
  // so we don't stack mounted React roots over orphaned Mapbox popups.
  const activeSavePopupCleanupRef = useRef<(() => void) | null>(null);
  const twdbWellsAbortRef = useRef<AbortController | null>(null);
  const brackishAbortRef = useRef<AbortController | null>(null);
  const tdsSamplesAbortRef = useRef<AbortController | null>(null);
  const pipelineAbortRefs = useRef<Partial<Record<PipelineGroup, AbortController | null>>>({});
  // listing layer abort refs managed by useListingLayer hook
  const prevCountyRef = useRef<string | undefined>(filters.county);
  const addSourcesAndLayersRef = useRef<(() => void) | null>(null);
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;
  const minAcreageRef = useRef(filters.min_acreage);
  minAcreageRef.current = filters.min_acreage;

  // Measurement tool — destructure stable callbacks to avoid object identity churn
  const measurement = useMeasurement();
  const {
    measurementsRef: mMeasurementsRef, currentPointsRef: mCurrentPointsRef,
    measurementCount, handleMapClick: mHandleClick, finishMeasurement: mFinish,
    undoLastPoint: mUndo, clearAll: mClearAll,
    getLineGeoJSON: mGetLines, getPointGeoJSON: mGetPoints, getPreviewGeoJSON: mGetPreview,
  } = measurement;
  // Suppression guard read by every parcel/pipeline/well click + hover handler.
  // Folds both measure modes into one flag so a single assignment keeps them all
  // quiet while either tool is armed.
  const measurementActiveRef = useRef(measurementActive);
  measurementActiveRef.current = measurementActive || areaActive;
  useMemphisResearchMap(map, mapLoaded, styleEpoch, memphisLayers, measurementActiveRef);
  useEtResearchMap(map, mapLoaded, styleEpoch, etLayers, measurementActiveRef, etData);
  // Distance mode only — the ruler's own click/dblclick/mousemove handlers and
  // its keyboard shortcuts must stay inert while the area tool is armed.
  const distanceActiveRef = useRef(measurementActive);
  distanceActiveRef.current = measurementActive;
  const areaActiveRef = useRef(areaActive);
  areaActiveRef.current = areaActive;
  measurement.activeRef.current = measurementActive;
  const measurementMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const measurementCursorMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const measurementSegmentTotalRef = useRef(0);
  // Drag-a-rectangle acreage tool, on the same shared hook the briefing and
  // portfolio maps use. Distance stays on this file's own wiring above, so the
  // hook is attached with active:false and only its measurement-area source and
  // the two area layers are new — its addSource/addLayer calls are existence-
  // guarded, so the three measurement-* sources created here are reused.
  const disarmArea = useCallback(() => onAreaActiveChange(false), [onAreaActiveChange]);
  useMapMeasurement({
    mapRef: map,
    ready: mapLoaded,
    styleEpoch,
    active: false,
    enabled: true,
    areaActive,
    onExitArea: disarmArea,
  });
  const searchPinMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const onSearchPinClearRef = useRef(onSearchPinClear);
  onSearchPinClearRef.current = onSearchPinClear;

  // Track per-group operator counts for aggregation
  const pipelineOpCountsRef = useRef<Record<string, Record<string, number>>>({});
  // Latest company lookup for the pipeline popup, which is registered once at map load.
  const companyLabelForRef = useRef(companyLabelFor);
  companyLabelForRef.current = companyLabelFor;
  const pipelineLoadingRef = useRef<PipelineLoadingState>({ gathering: false, gas_transmission: false, liquids_transmission: false });

  const emitOperators = useCallback(() => {
    const merged: Record<string, number> = {};
    let totalSegments = 0;
    for (const groupCounts of Object.values(pipelineOpCountsRef.current)) {
      for (const [op, count] of Object.entries(groupCounts)) {
        merged[op] = (merged[op] || 0) + count;
        totalSegments += count;
      }
    }
    const sorted = Object.entries(merged)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
    onOperatorsLoaded(sorted);
    onPipelineCountChange?.(totalSegments);
  }, [onOperatorsLoaded, onPipelineCountChange]);

  // Zoom to county when county filter changes, zoom out when cleared
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const prev = prevCountyRef.current;
    prevCountyRef.current = filters.county;
    if (filters.county) {
      const bounds = COUNTY_BOUNDS[filters.county];
      if (bounds) map.current.fitBounds(bounds, { padding: 40, duration: 1000 });
    } else if (prev) {
      map.current.flyTo({ center: MAP_CENTER, zoom: INITIAL_ZOOM, duration: 1000 });
    }
  }, [filters.county, mapLoaded]);

  // Helper: create a styled distance-label marker at a point
  const createDistanceMarker = useCallback((m: mapboxgl.Map, lngLat: [number, number], text: string): mapboxgl.Marker => {
    const el = document.createElement('div');
    el.style.cssText = `
      font-family: ${designTokens.typography.fontFamily.mono};
      font-size: ${designTokens.typography.fontSize.sm};
      font-weight: ${designTokens.typography.fontWeight.medium};
      color: ${designTokens.colors.measurementLabel};
      background: rgba(58, 70, 80, 0.92);
      padding: 2px 6px;
      border-radius: ${designTokens.borderRadius.sm};
      border: 1px solid ${designTokens.colors.measurement};
      pointer-events: none;
      white-space: nowrap;
    `;
    el.textContent = text;
    return new mapboxgl.Marker({ element: el, anchor: 'left', offset: [8, 0] })
      .setLngLat(lngLat)
      .addTo(m);
  }, []);

  // Helper: update measurement map sources + distance labels
  const updateMeasurementSources = useCallback((m: mapboxgl.Map) => {
    const lineSrc = m.getSource('measurement-lines') as mapboxgl.GeoJSONSource;
    const ptSrc = m.getSource('measurement-points') as mapboxgl.GeoJSONSource;
    if (lineSrc) lineSrc.setData(mGetLines());
    if (ptSrc) ptSrc.setData(mGetPoints());

    // Clear old markers
    measurementMarkersRef.current.forEach(marker => marker.remove());
    measurementMarkersRef.current = [];

    // Add distance label markers for completed measurements
    for (const meas of mMeasurementsRef.current) {
      if (meas.points.length >= 2) {
        measurementMarkersRef.current.push(
          createDistanceMarker(m, meas.points[meas.points.length - 1], formatDistance(meas.total))
        );
      }
    }

    // Add label for in-progress measurement
    const pts = mCurrentPointsRef.current;
    if (pts.length >= 2) {
      let total = 0;
      for (let i = 1; i < pts.length; i++) {
        total += turfDistance(pts[i - 1], pts[i], { units: 'miles' });
      }
      measurementMarkersRef.current.push(
        createDistanceMarker(m, pts[pts.length - 1], formatDistance(total))
      );
    }
  }, [mGetLines, mGetPoints, createDistanceMarker]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    mapboxgl.accessToken = getMapboxToken();

    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: MAP_CENTER,
      zoom: INITIAL_ZOOM,
      attributionControl: false,
    });

    m.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Zoom level indicator — sits right below the +/- buttons
    const zoomEl = document.createElement('div');
    zoomEl.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    zoomEl.style.cssText = 'padding:2px 0;text-align:center;font:11px monospace;color:#8A9CAA;pointer-events:none;';
    zoomEl.textContent = `z${INITIAL_ZOOM.toFixed(1)}`;
    m.on('zoom', () => {
      zoomEl.textContent = `z${m.getZoom().toFixed(1)}`;
      onZoomChange?.(m.getZoom());
    });
    let lastCursorEmit = 0;
    m.on('mousemove', (e) => {
      const now = performance.now();
      if (now - lastCursorEmit < 100) return;
      lastCursorEmit = now;
      onCursorMove?.({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });
    m.on('mouseout', () => {
      onCursorMove?.(null);
    });
    m.addControl({ onAdd: () => zoomEl, onRemove: () => { zoomEl.remove(); } }, 'top-right');

    m.addControl(new mapboxgl.ScaleControl(), 'bottom-right');

    const addSourcesAndLayers = () => {
      if (m.getSource('parcels')) return; // Guard against duplicate style.load handler fire
      const haloColor = isDarkRef.current ? 'rgba(46,56,64,0.7)' : 'rgba(228,230,232,0.7)';
      m.addSource('parcels', {
        type: 'vector',
        tiles: [getParcelTileUrl(minAcreageRef.current)],
        minzoom: 6,
        maxzoom: 14,
      });
      PIPELINE_LAYERS.forEach(pl => {
        m.addSource(pl.sourceId, { type: 'geojson', data: EMPTY_FC, tolerance: 1 });
      });
      m.addSource('federal-lands', { type: 'geojson', data: EMPTY_FC });
      m.addSource('crownpine-tracts', { type: 'geojson', data: EMPTY_FC });
      m.addSource('assembly-groups', { type: 'geojson', data: EMPTY_FC });
      m.addSource('state-trust-lands', { type: 'geojson', data: EMPTY_FC });
      m.addSource('protected-areas', { type: 'geojson', data: EMPTY_FC });
      m.addSource('electric-territories', { type: 'geojson', data: EMPTY_FC });
      m.addSource('power-infra', { type: 'geojson', data: EMPTY_FC });
      m.addSource('osm-switchyards', { type: 'geojson', data: EMPTY_FC });
      m.addSource('grid-capacity', { type: 'geojson', data: EMPTY_FC });
      m.addSource('queue-heatmap', { type: 'geojson', data: EMPTY_FC });
      m.addSource('congestion-heatmap', { type: 'geojson', data: EMPTY_FC });
      m.addSource('airports', { type: 'geojson', data: EMPTY_FC });
      m.addSource('airport-zones', { type: 'geojson', data: EMPTY_FC });
      m.addSource('fiber-coverage', { type: 'geojson', data: EMPTY_FC });
      m.addSource('carrier-routes', { type: 'geojson', data: EMPTY_FC });
      m.addSource('fiber-row-pipelines', { type: 'geojson', data: EMPTY_FC });
      m.addSource('ixp-facilities', { type: 'geojson', data: EMPTY_FC });
      m.addSource('water-infra', { type: 'geojson', data: EMPTY_FC });
      m.addSource('aquifer-boundaries', { type: 'geojson', data: EMPTY_FC });
      m.addSource('twdb-flood-hazards', { type: 'geojson', data: EMPTY_FC });
      m.addSource('flood-zones', { type: 'geojson', data: EMPTY_FC });
      m.addSource('pending-flood-zones', { type: 'geojson', data: EMPTY_FC });
      m.addSource('highways', { type: 'geojson', data: EMPTY_FC });
      m.addSource('roads', { type: 'geojson', data: EMPTY_FC });
      m.addSource('osm-pipeline-candidates', { type: 'geojson', data: EMPTY_FC, attribution: '© OpenStreetMap contributors' });
      m.addSource('georgia-sites', { type: 'geojson', data: GEORGIA_SITES });
      m.addSource('georgia-evidence', { type: 'geojson', data: GEORGIA_EVIDENCE });
      m.addSource('railroads', { type: 'geojson', data: EMPTY_FC });
      m.addSource('data-centers', { type: 'geojson', data: EMPTY_FC });
      m.addSource('wells-data', { type: 'geojson', data: EMPTY_FC });
      m.addSource('well-laterals', { type: 'geojson', data: EMPTY_FC });
      m.addSource('land-listings', { type: 'geojson', data: EMPTY_FC });
      m.addSource('grid-listings', { type: 'geojson', data: EMPTY_FC });
      m.addSource('gas-plants', { type: 'geojson', data: EMPTY_FC });
      m.addSource('gas-infra-compressors', { type: 'geojson', data: EMPTY_FC });
      m.addSource('gas-infra-receipt-delivery', { type: 'geojson', data: EMPTY_FC });
      m.addSource('gas-infra-interconnects', { type: 'geojson', data: EMPTY_FC });
      m.addSource('gas-infra-storage', { type: 'geojson', data: EMPTY_FC });
      m.addSource('epa-zones', { type: 'geojson', data: EMPTY_FC });
      m.addSource('ejscreen', { type: 'geojson', data: EMPTY_FC });
      m.addSource('critical-habitat', { type: 'geojson', data: EMPTY_FC });
      m.addSource('operator-aois', { type: 'geojson', data: EMPTY_FC });
      m.addSource('mine-subsidence', { type: 'geojson', data: EMPTY_FC });
      m.addSource('aml-sites', { type: 'geojson', data: EMPTY_FC });
      m.addSource('wetlands', {
        type: 'vector',
        tiles: [getWetlandsTileUrl()],
        minzoom: 8,
        maxzoom: 14,
      });
      m.addSource('seismic-risk', { type: 'geojson', data: EMPTY_FC });
      m.addSource('urban-areas', { type: 'geojson', data: EMPTY_FC });
      m.addSource('wildfire', { type: 'geojson', data: EMPTY_FC });
      m.addSource('water-regulatory-zones', { type: 'geojson', data: EMPTY_FC });
      m.addSource('twdb-wells', { type: 'geojson', data: EMPTY_FC });
      m.addSource('brackish-zones', { type: 'geojson', data: EMPTY_FC });
      m.addSource('tds-samples', { type: 'geojson', data: EMPTY_FC });
      m.addSource('county-boundaries', { type: 'geojson', data: EMPTY_FC });
      m.addSource('county-centroids', { type: 'geojson', data: EMPTY_FC });

      // Mapbox terrain DEM for hillshade visual context
      if (!m.getSource('mapbox-dem')) {
        m.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        });
      }
      if (!m.getLayer('hillshade')) {
        m.addLayer({
          id: 'hillshade',
          type: 'hillshade',
          source: 'mapbox-dem',
          paint: {
            'hillshade-exaggeration': 0.3,
            'hillshade-shadow-color': '#5a3d1a',
            'hillshade-illumination-direction': 315,
          },
        });
      }
      // Horizon for the pitched 3D-terrain view; invisible at pitch 0.
      if (!m.getLayer('sky')) {
        m.addLayer({
          id: 'sky',
          type: 'sky',
          paint: { 'sky-type': 'atmosphere', 'sky-atmosphere-sun-intensity': 5 },
        });
      }

      // East TX slope drape — USGS 3DEP 10m, classed at the scoring breaks
      // (<2 / <5 / <10 / knockout). Added here so it sits under every vector
      // overlay; bounds match the source raster so no tiles are requested
      // outside East TX. nearest resampling keeps the classes crisp past z14.
      if (!m.getSource('etx-slope')) {
        m.addSource('etx-slope', {
          type: 'raster',
          tiles: [`${getSlopeTilesUrl()}/{z}/{x}/{y}.png`],
          tileSize: 256,
          minzoom: 8,
          maxzoom: 14,
          bounds: [-96.0006, 29.9994, -91.9994, 32.0006],
          attribution: 'Slope: USGS 3DEP 10m',
        });
      }
      if (!m.getLayer('etx-slope')) {
        m.addLayer({
          id: 'etx-slope',
          type: 'raster',
          source: 'etx-slope',
          layout: { visibility: 'none' },
          paint: { 'raster-opacity': 0.55, 'raster-resampling': 'nearest' },
        });
      }

      // 1 m per-site drape, from USGS 3DEP lidar over the shortlist sites.
      // Added after the 10 m layer so local detail is visible when both are on.
      if (!m.getSource('site-slope-1m')) {
        m.addSource('site-slope-1m', {
          type: 'raster',
          tiles: [`${getSlope1mTilesUrl()}/{z}/{x}/{y}.png`],
          tileSize: 256,
          minzoom: 13,
          maxzoom: 17,
          bounds: [-95.6, 30.29, -93.88, 31.8],
          attribution: 'Slope: USGS 3DEP 1m lidar',
        });
      }
      if (!m.getLayer('site-slope-1m')) {
        m.addLayer({
          id: 'site-slope-1m',
          type: 'raster',
          source: 'site-slope-1m',
          layout: { visibility: 'none' },
          paint: { 'raster-opacity': 0.55, 'raster-resampling': 'nearest' },
        });
      }

      // County boundary lines (Census TIGER data, togglable)
      m.addLayer({
        id: 'county-lines',
        type: 'line',
        source: 'county-boundaries',
        layout: { visibility: 'none' },
        paint: {
          'line-color': isDarkRef.current ? 'rgba(138,156,170,0.35)' : 'rgba(62,78,92,0.30)',
          'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 10, 1.2],
        },
        minzoom: 4,
      });
      m.addLayer({
        id: 'county-labels',
        type: 'symbol',
        source: 'county-centroids',
        layout: {
          visibility: 'none',
          'text-field': ['get', 'name'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 5, 9, 9, 12],
          'text-max-width': 8,
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': isDarkRef.current ? 'rgba(200,212,220,0.85)' : 'rgba(40,52,64,0.80)',
          'text-halo-color': isDarkRef.current ? 'rgba(20,28,36,0.7)' : 'rgba(255,255,255,0.7)',
          'text-halo-width': 1.5,
        },
        minzoom: 5,
      });

      // Electric service territories — per-utility colors from PUC storm map renderer
      // COOPs: per-company solid fill at 50% opacity, gray outline
      m.addLayer({
        id: 'electric-territories-coop-fill',
        type: 'fill',
        source: 'electric-territories',
        filter: ['==', ['get', 'company_type'], 'COOP'],
        paint: {
          'fill-color': ['coalesce', ['get', 'fill_color'], '#64b5f3'],
          'fill-opacity': 0.4,
        },
      });
      m.addLayer({
        id: 'electric-territories-coop-border',
        type: 'line',
        source: 'electric-territories',
        filter: ['==', ['get', 'company_type'], 'COOP'],
        paint: {
          'line-color': '#6e6e6e',
          'line-width': 0.7,
          'line-opacity': 0.6,
        },
      });
      // MUNIs: per-company colored fill, dashed outline (approximates source hatch)
      m.addLayer({
        id: 'electric-territories-muni-fill',
        type: 'fill',
        source: 'electric-territories',
        filter: ['==', ['get', 'company_type'], 'MUNI'],
        paint: {
          'fill-color': ['coalesce', ['get', 'fill_color'], '#f59e0b'],
          'fill-opacity': 0.3,
        },
      });
      m.addLayer({
        id: 'electric-territories-muni-border',
        type: 'line',
        source: 'electric-territories',
        filter: ['==', ['get', 'company_type'], 'MUNI'],
        paint: {
          'line-color': ['coalesce', ['get', 'fill_color'], '#b45309'],
          'line-width': 1,
          'line-dasharray': [4, 3],
          'line-opacity': 0.8,
        },
      });
      // IOUs: no fill, bold per-company colored outline
      m.addLayer({
        id: 'electric-territories-fill',
        type: 'fill',
        source: 'electric-territories',
        filter: ['==', ['get', 'company_type'], 'IOU'],
        paint: {
          'fill-color': ['coalesce', ['get', 'fill_color'], '#444444'],
          'fill-opacity': 0,
        },
      });
      m.addLayer({
        id: 'electric-territories-border',
        type: 'line',
        source: 'electric-territories',
        filter: ['==', ['get', 'company_type'], 'IOU'],
        paint: {
          'line-color': ['coalesce', ['get', 'fill_color'], '#444444'],
          'line-width': 2,
          'line-opacity': 0.9,
        },
      });

      // Federal lands fill (rendered at bottom, colored by category)
      m.addLayer({
        id: 'federal-lands-fill',
        type: 'fill',
        source: 'federal-lands',
        paint: {
          'fill-color': [
            'match', ['get', 'category'],
            'BLM', '#b45309',
            'USFS', '#166534',
            'NPS', '#92400e',
            'State', '#7c3aed',
            'Private', '#059669',
            'Other Federal', '#dc2626',
            '#6b7280',
          ],
          'fill-opacity': 0.15,
        },
      });
      m.addLayer({
        id: 'federal-lands-border',
        type: 'line',
        source: 'federal-lands',
        paint: {
          'line-color': [
            'match', ['get', 'category'],
            'BLM', '#b45309',
            'USFS', '#166534',
            'NPS', '#92400e',
            'State', '#7c3aed',
            'Private', '#059669',
            'Other Federal', '#dc2626',
            '#6b7280',
          ],
          'line-width': 1.5,
          'line-dasharray': [4, 2],
          'line-opacity': 0.6,
        },
      });

      // Crown Pine LRM tracts (the landowner's own inventory), colored by fund.
      // Rust/amber so it can't be read as USFS green — CP land abuts the
      // Angelina / Sabine / Davy Crockett national forests.
      m.addLayer({
        id: 'crownpine-tracts-fill',
        type: 'fill',
        source: 'crownpine-tracts',
        paint: {
          'fill-color': ['match', ['get', 'fund'], 'CPT5-Crown Pine Timber 5 LP', '#d97706', '#9a3412'],
          'fill-opacity': 0.22,
        },
      });
      m.addLayer({
        id: 'crownpine-tracts-border',
        type: 'line',
        source: 'crownpine-tracts',
        paint: {
          'line-color': ['match', ['get', 'fund'], 'CPT5-Crown Pine Timber 5 LP', '#d97706', '#9a3412'],
          'line-width': 1,
          'line-opacity': 0.8,
        },
      });
      m.addLayer({
        id: 'crownpine-tracts-label',
        type: 'symbol',
        source: 'crownpine-tracts',
        minzoom: 11,
        layout: {
          'text-field': ['get', 'tract_name'],
          'text-size': 10,
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        },
        paint: { 'text-color': '#7c2d12', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.2 },
      });

      // Assembly groups fill (color by size tier)
      m.addLayer({
        id: 'assembly-groups-fill',
        type: 'fill',
        source: 'assembly-groups',
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.06,  // Near-invisible wash — border does the work
        },
      });
      m.addLayer({
        id: 'assembly-groups-border',
        type: 'line',
        source: 'assembly-groups',
        paint: {
          'line-color': '#2563eb',
          'line-width': 3,
          'line-dasharray': [4, 2],
          'line-opacity': 0.85,
        },
      });

      // State trust lands fill (rendered just above federal lands)
      m.addLayer({
        id: 'state-trust-fill',
        type: 'fill',
        source: 'state-trust-lands',
        paint: {
          'fill-color': '#16a34a',
          'fill-opacity': 0.15,
        },
      });
      m.addLayer({
        id: 'state-trust-outline',
        type: 'line',
        source: 'state-trust-lands',
        paint: {
          'line-color': '#16a34a',
          'line-width': 1.5,
          'line-dasharray': [4, 2],
          'line-opacity': 0.6,
        },
      });

      // Protected areas fill (military, tribal, monuments, wilderness)
      m.addLayer({
        id: 'protected-areas-fill',
        type: 'fill',
        source: 'protected-areas',
        paint: {
          'fill-color': [
            'match', ['get', 'designation_type'],
            'MIL', '#dc2626',
            'TRIBL', '#ea580c',
            'NM', '#16a34a',
            'NP', '#16a34a',
            'WILD', '#15803d',
            'NWR', '#15803d',
            'WSA', '#15803d',
            '#6b7280',
          ],
          'fill-opacity': 0.2,
        },
      });
      m.addLayer({
        id: 'protected-areas-border',
        type: 'line',
        source: 'protected-areas',
        paint: {
          'line-color': [
            'match', ['get', 'designation_type'],
            'MIL', '#dc2626',
            'TRIBL', '#ea580c',
            'NM', '#16a34a',
            'NP', '#16a34a',
            'WILD', '#15803d',
            'NWR', '#15803d',
            'WSA', '#15803d',
            '#6b7280',
          ],
          'line-width': 2,
          'line-opacity': 0.7,
        },
      });

      // Aquifer boundary fill (very transparent, rendered at bottom with federal lands)
      m.addLayer({
        id: 'aquifer-fill',
        type: 'fill',
        source: 'aquifer-boundaries',
        paint: {
          'fill-color': '#06b6d4',
          'fill-opacity': 0.1,
        },
      });
      m.addLayer({
        id: 'aquifer-outline',
        type: 'line',
        source: 'aquifer-boundaries',
        paint: {
          'line-color': '#0891b2',
          'line-width': 2,
          'line-dasharray': [4, 2],
          'line-opacity': 0.6,
        },
      });
      m.addLayer({
        id: 'aquifer-labels',
        type: 'symbol',
        source: 'aquifer-boundaries',
        minzoom: 7,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 11,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'center',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#0e7490',
          'text-halo-color': haloColor,
          'text-halo-width': 1.5,
        },
      });

      // Brackish aquifer extent (TWDB BRACS) — teal fill + dashed outline
      m.addLayer({
        id: 'brackish-fill',
        type: 'fill',
        source: 'brackish-zones',
        paint: {
          'fill-color': '#14b8a6',
          'fill-opacity': 0.15,
        },
      });
      m.addLayer({
        id: 'brackish-outline',
        type: 'line',
        source: 'brackish-zones',
        paint: {
          'line-color': '#0f766e',
          'line-width': 1.5,
          'line-dasharray': [2, 2],
          'line-opacity': 0.7,
        },
      });
      m.addLayer({
        id: 'brackish-labels',
        type: 'symbol',
        source: 'brackish-zones',
        minzoom: 8,
        layout: {
          'text-field': ['concat', ['get', 'aquifer_name'], ' (brackish)'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'center',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#134e4a',
          'text-halo-color': haloColor,
          'text-halo-width': 1.5,
        },
      });

      // Water service areas fill
      m.addLayer({
        id: 'water-service-areas',
        type: 'fill',
        source: 'water-infra',
        filter: ['==', ['get', 'type'], 'water_service_area'],
        paint: {
          'fill-color': '#0ea5e9',
          'fill-opacity': 0.12,
        },
      });
      m.addLayer({
        id: 'water-service-areas-outline',
        type: 'line',
        source: 'water-infra',
        filter: ['==', ['get', 'type'], 'water_service_area'],
        paint: {
          'line-color': '#0284c7',
          'line-width': 1.5,
          'line-opacity': 0.5,
        },
      });

      // Fiber coverage hexagons — enterprise vs residential
      m.addLayer({
        id: 'fiber-hexagons',
        type: 'fill',
        source: 'fiber-coverage',
        paint: {
          'fill-color': ['case',
            ['==', ['get', 'has_enterprise'], true], '#0e7490',
            '#0891b2',
          ],
          'fill-opacity': ['case',
            ['==', ['get', 'has_enterprise'], true], 0.35,
            0.2,
          ],
        },
      });
      m.addLayer({
        id: 'fiber-hexagons-outline',
        type: 'line',
        source: 'fiber-coverage',
        paint: {
          'line-color': ['case',
            ['==', ['get', 'is_isolated'], true], '#6A7E8E',
            '#0e7490',
          ],
          'line-width': 1,
          'line-opacity': 0.6,
        },
      });

      // Carrier backbone fiber routes (FiberLight amber, Zayo cyan, Segra emerald)
      m.addLayer({
        id: 'carrier-routes-line',
        type: 'line',
        source: 'carrier-routes',
        paint: {
          'line-color': [
            'match',
            ['get', 'carrier'],
            'Zayo', '#0891b2',
            'FiberLight', '#f59e0b',
            'Segra', '#10b981',
            ...NTIA_CARRIER_NAMES.flatMap(n => [n, NTIA_CARRIER_COLOR]),
            NTIA_CARRIER_COLOR,
          ],
          'line-width': 3,
          'line-opacity': 0.8,
        },
      });

      // IXP & Colo facility points
      m.addLayer({
        id: 'ixp-facilities-circle',
        type: 'circle',
        source: 'ixp-facilities',
        paint: {
          'circle-radius': ['case',
            ['==', ['get', 'type'], 'ixp'], 8,
            5,
          ],
          'circle-color': ['case',
            ['==', ['get', 'type'], 'ixp'], '#10b981',
            '#6ee7b7',
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#065f46',
          'circle-opacity': 0.9,
        },
      });
      m.addLayer({
        id: 'ixp-facilities-label',
        type: 'symbol',
        source: 'ixp-facilities',
        filter: ['==', ['get', 'type'], 'ixp'],
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 11,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
        },
        paint: {
          'text-color': '#065f46',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1,
        },
      });

      // TWDB existing 1% annual-chance hazard. FEMA layers follow so regulatory
      // flood zones remain visually distinct and render above the screening model.
      m.addLayer({
        id: 'twdb-flood-hazards-fill',
        type: 'fill',
        source: 'twdb-flood-hazards',
        paint: {
          'fill-color': '#2563eb',
          'fill-opacity': 0.22,
        },
      });
      m.addLayer({
        id: 'twdb-flood-hazards-outline',
        type: 'line',
        source: 'twdb-flood-hazards',
        paint: {
          'line-color': '#2563eb',
          'line-width': 1,
          'line-opacity': 0.75,
        },
      });

      // FEMA flood zone fill — risk zones only (minimal/X zones filtered server-side)
      m.addLayer({
        id: 'flood-zones-fill',
        type: 'fill',
        source: 'flood-zones',
        paint: {
          'fill-color': [
            'match', ['get', 'flood_type'],
            '100yr', '#dc2626',
            '500yr', '#f97316',
            'undetermined', '#f97316',
            '#dc2626',
          ],
          'fill-opacity': 0.25,
        },
      });
      m.addLayer({
        id: 'flood-zones-outline',
        type: 'line',
        source: 'flood-zones',
        paint: {
          'line-color': [
            'match', ['get', 'flood_type'],
            '100yr', '#dc2626',
            '500yr', '#f97316',
            'undetermined', '#f97316',
            '#dc2626',
          ],
          'line-width': 1,
          'line-opacity': 0.6,
        },
      });

      for (const layer of pendingFloodLayers('pending-flood-zones', 'pending-flood-zones-fill', 'pending-flood-zones-outline')) m.addLayer(layer);

      // Highway lines
      m.addLayer({
        id: 'highways-line',
        type: 'line',
        source: 'highways',
        paint: {
          'line-color': [
            'match', ['get', 'route_type'],
            'Interstate', '#6366f1',
            '#818cf8',
          ],
          'line-width': [
            'match', ['get', 'route_type'],
            'Interstate', 3,
            2,
          ],
          'line-opacity': 0.7,
        },
      });
      m.addLayer({
        id: 'highways-labels',
        type: 'symbol',
        source: 'highways',
        minzoom: 8,
        layout: {
          'symbol-placement': 'line',
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#4338ca',
          'text-halo-color': haloColor,
          'text-halo-width': 1.5,
        },
      });

      // Railroad lines — dashed, dark brown/gray
      m.addLayer({
        id: 'railroads-line',
        type: 'line',
        source: 'railroads',
        paint: {
          'line-color': '#78716c',
          'line-width': 1.5,
          'line-opacity': 0.7,
          'line-dasharray': [4, 3],
        },
      });
      m.addLayer({
        id: 'railroads-labels',
        type: 'symbol',
        source: 'railroads',
        minzoom: 10,
        layout: {
          'symbol-placement': 'line',
          'text-field': ['get', 'owner'],
          'text-size': 9,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#57534e',
          'text-halo-color': haloColor,
          'text-halo-width': 1.5,
        },
      });

      // Water wells (circles)
      m.addLayer({
        id: 'water-wells',
        type: 'circle',
        source: 'water-infra',
        filter: ['==', ['get', 'type'], 'well'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 3, 12, 6],
          'circle-color': '#06b6d4',
          'circle-stroke-color': '#0e7490',
          'circle-stroke-width': 1,
          'circle-opacity': 0.8,
        },
      });

      // Airport exclusion zone fills (100-mile radius)
      m.addLayer({
        id: 'airport-zones-fill',
        type: 'fill',
        source: 'airport-zones',
        paint: {
          'fill-color': '#ef4444',
          'fill-opacity': 0.06,
        },
      });

      m.addLayer({
        id: 'airport-zones-border',
        type: 'line',
        source: 'airport-zones',
        paint: {
          'line-color': '#ef4444',
          'line-width': 1.5,
          'line-dasharray': [6, 4],
          'line-opacity': 0.35,
        },
      });

      // Airport point markers
      m.addLayer({
        id: 'airport-points',
        type: 'circle',
        source: 'airports',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 3, 8, 6, 12, 9],
          'circle-color': '#ef4444',
          'circle-stroke-color': '#7f1d1d',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.85,
        },
      });

      // Airport labels
      m.addLayer({
        id: 'airport-labels',
        type: 'symbol',
        source: 'airports',
        minzoom: 7,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#991b1b',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // Pipeline layers (gathering first/below, then transmission on top)
      PIPELINE_LAYERS.forEach(pl => {
        const initVis = 'visible';
        // Glow layer (behind the main line)
        m.addLayer({
          id: pl.layerId + '-glow',
          type: 'line',
          source: pl.sourceId,
          minzoom: pl.minzoom,
          layout: { visibility: initVis },
          paint: {
            'line-color': '#C4705A',
            'line-width': pl.group === 'desert_sw' ? 14 : 10,
            'line-opacity': pl.group === 'desert_sw' ? 0.25 : 0.12,
            'line-blur': 6,
          },
        });
        m.addLayer({
          id: pl.layerId,
          type: 'line',
          source: pl.sourceId,
          minzoom: pl.minzoom,
          layout: { visibility: initVis },
          paint: {
            'line-color': pl.color,
            'line-width': pl.width,
            'line-opacity': 0.8,
          },
        });
      });

      // Pipelines with fiber in ROW (KMI / Williams / Transwestern) — dashed purple.
      // Added AFTER PIPELINE_LAYERS so the dashes render on top of the regular pipeline
      // rendering rather than being hidden underneath the gas/liquids layer strokes.
      m.addLayer({
        id: 'fiber-row-pipelines-line',
        type: 'line',
        source: 'fiber-row-pipelines',
        paint: {
          'line-color': '#7c3aed',
          'line-width': 2.5,
          'line-opacity': 0.85,
          'line-dasharray': [2, 2],
        },
      });

      // Power infrastructure — transmission lines
      m.addLayer({
        id: 'power-lines',
        type: 'line',
        source: 'power-infra',
        filter: ['==', ['get', 'type'], 'transmission_line'],
        paint: {
          'line-color': '#f59e0b',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [6, 3],
        },
      });

      // Power infrastructure — substations (circles)
      m.addLayer({
        id: 'power-substations',
        type: 'circle',
        source: 'power-infra',
        filter: ['==', ['get', 'type'], 'substation'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 4, 12, 8],
          'circle-color': '#f59e0b',
          'circle-stroke-color': '#92400e',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.85,
        },
      });

      // Power infrastructure — power plants (larger circles)
      m.addLayer({
        id: 'power-plants',
        type: 'circle',
        source: 'power-infra',
        filter: ['==', ['get', 'type'], 'power_plant'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 5, 12, 10],
          'circle-color': '#dc2626',
          'circle-stroke-color': '#7f1d1d',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.85,
        },
      });

      // Power infrastructure labels
      m.addLayer({
        id: 'power-labels',
        type: 'symbol',
        source: 'power-infra',
        filter: ['in', ['get', 'type'], ['literal', ['substation', 'power_plant']]],
        minzoom: 9,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#92400e',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // OSM switchyards — circle (size scales with voltage_kv; hifld_backed=false gets emerald ring as our moat)
      m.addLayer({
        id: 'osm-switchyards-circle',
        type: 'circle',
        source: 'osm-switchyards',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            6, ['case', ['>=', ['coalesce', ['get', 'voltage_kv'], 0], 345], 5, 3],
            12, ['case', ['>=', ['coalesce', ['get', 'voltage_kv'], 0], 345], 10, 7],
          ],
          'circle-color': [
            'case',
            ['get', 'hifld_backed'], '#f59e0b',   // match power-infra amber when HIFLD also has it
            '#10b981',                             // emerald for OSM-only (our moat)
          ],
          'circle-stroke-color': [
            'case',
            ['get', 'hifld_backed'], '#92400e',
            '#047857',
          ],
          'circle-stroke-width': [
            'case',
            ['get', 'hifld_backed'], 1.5,
            2.5,
          ],
          'circle-opacity': 0.9,
        },
      });

      m.addLayer({
        id: 'osm-switchyards-labels',
        type: 'symbol',
        source: 'osm-switchyards',
        minzoom: 8,
        layout: {
          'text-field': [
            'format',
            ['coalesce', ['get', 'name'], '(unnamed switchyard)'], {},
            '\n', {},
            ['case', ['==', ['get', 'voltage_kv'], null], '', ['concat', ['to-string', ['get', 'voltage_kv']], ' kV']],
            { 'font-scale': 0.85 },
          ],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.9],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': ['case', ['get', 'hifld_backed'], '#92400e', '#047857'],
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // ERCOT queue congestion county choropleth
      m.addLayer({
        id: 'queue-heatmap-fill',
        type: 'fill',
        source: 'queue-heatmap',
        paint: {
          'fill-color': [
            'interpolate', ['linear'], ['get', 'pending_mw'],
            0, '#16a34a',
            1000, '#d97706',
            3000, '#ef4444',
            6000, '#dc2626',
          ],
          'fill-opacity': [
            'interpolate', ['linear'], ['get', 'pending_mw'],
            0, 0.08,
            1000, 0.15,
            3000, 0.25,
            6000, 0.35,
          ],
        },
      });
      m.addLayer({
        id: 'queue-heatmap-outline',
        type: 'line',
        source: 'queue-heatmap',
        paint: {
          'line-color': [
            'interpolate', ['linear'], ['get', 'pending_mw'],
            0, '#16a34a',
            1000, '#d97706',
            3000, '#ef4444',
            6000, '#dc2626',
          ],
          'line-width': 1,
          'line-opacity': 0.5,
        },
      });

      // Transmission congestion county choropleth (shadow prices)
      m.addLayer({
        id: 'congestion-heatmap-fill',
        type: 'fill',
        source: 'congestion-heatmap',
        paint: {
          'fill-color': [
            'match', ['get', 'severity'],
            'severe', '#dc2626',
            'high', '#f97316',
            'moderate', '#eab308',
            'low', '#22c55e',
            '#94a3b8',
          ],
          'fill-opacity': [
            'match', ['get', 'severity'],
            'severe', 0.35,
            'high', 0.25,
            'moderate', 0.15,
            'low', 0.08,
            0.05,
          ],
        },
      });
      m.addLayer({
        id: 'congestion-heatmap-outline',
        type: 'line',
        source: 'congestion-heatmap',
        paint: {
          'line-color': [
            'match', ['get', 'severity'],
            'severe', '#dc2626',
            'high', '#f97316',
            'moderate', '#eab308',
            'low', '#22c55e',
            '#94a3b8',
          ],
          'line-width': 1,
          'line-opacity': 0.6,
        },
      });

      // Grid capacity — transmission lines classified by 75 MW likelihood
      m.addLayer({
        id: 'grid-capacity-lines',
        type: 'line',
        source: 'grid-capacity',
        filter: ['all', ['==', ['get', 'type'], 'transmission_line'], ['has', 'capacity_tier']],
        paint: {
          'line-color': [
            'match', ['get', 'capacity_tier'],
            'high', '#16a34a',
            'likely', '#0891b2',
            'moderate', '#d97706',
            '#d97706',
          ],
          'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 12, 3.5],
          'line-opacity': 0.7,
        },
      });

      // Grid capacity — substations classified by 75 MW likelihood
      m.addLayer({
        id: 'grid-capacity-circles',
        type: 'circle',
        source: 'grid-capacity',
        filter: ['has', 'capacity_tier'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 5, 12, 11],
          'circle-color': [
            'match', ['get', 'capacity_tier'],
            'high', '#16a34a',
            'likely', '#0891b2',
            'moderate', '#d97706',
            'low', '#6A7E8E',
            '#6A7E8E',
          ],
          'circle-stroke-color': [
            'match', ['get', 'capacity_tier'],
            'high', '#15803d',
            'likely', '#0e7490',
            'moderate', '#b45309',
            'low', '#4B5563',
            '#4B5563',
          ],
          'circle-stroke-width': 2,
          'circle-opacity': 0.9,
        },
      });
      m.addLayer({
        id: 'grid-capacity-labels',
        type: 'symbol',
        source: 'grid-capacity',
        filter: ['has', 'capacity_tier'],
        minzoom: 9,
        layout: {
          'text-field': ['concat', ['get', 'voltage_kv'], ' kV'],
          'text-size': 10,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-anchor': 'top',
          'text-offset': [0, 1.0],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#E4E6E8',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // Reorder: move congestion heatmaps below interactive point/line layers
      // so substations, fiber, grid-capacity, and data centers remain clickable.
      const belowLayer = 'fiber-hexagons';
      ['queue-heatmap-fill', 'queue-heatmap-outline', 'congestion-heatmap-fill', 'congestion-heatmap-outline']
        .forEach(id => {
          if (m.getLayer(id) && m.getLayer(belowLayer)) m.moveLayer(id, belowLayer);
        });

      // Parcel fill (below infrastructure — insert before highways)
      m.addLayer({
        id: 'parcels-fill',
        type: 'fill',
        source: 'parcels',
        'source-layer': 'parcels',
        minzoom: 6,
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'is_government_owner'], true], designTokens.colors.government,
            ['all',
              ['>=', ['get', 'acquisition_score'], 76],
              ['>=', ['to-number', ['coalesce', ['get', 'pipeline_available_mw'], 0]], 10],
            ], designTokens.colors.urgent,
            ['>=', ['get', 'acquisition_score'], 51], designTokens.colors.hot,
            ['>=', ['get', 'acquisition_score'], 26], designTokens.colors.warm,
            'transparent',
          ],
          'fill-opacity': [
            'case',
            ['==', ['get', 'is_government_owner'], true], 0.12,
            ['all',
              ['>=', ['get', 'acquisition_score'], 76],
              ['>=', ['to-number', ['coalesce', ['get', 'pipeline_available_mw'], 0]], 10],
            ], 0.40,  // URGENT
            ['>=', ['get', 'acquisition_score'], 51], 0.25, // HOT
            ['>=', ['get', 'acquisition_score'], 26], 0.15, // WARM
            0,  // COLD: outline only
          ],
        },
      }, 'highways-line');

      // Parcel outline
      m.addLayer({
        id: 'parcels-outline',
        type: 'line',
        source: 'parcels',
        'source-layer': 'parcels',
        minzoom: 6,
        paint: {
          'line-color': [
            'case',
            ['==', ['get', 'is_government_owner'], true], designTokens.colors.government,
            ['all',
              ['>=', ['get', 'acquisition_score'], 76],
              ['>=', ['to-number', ['coalesce', ['get', 'pipeline_available_mw'], 0]], 10],
            ], designTokens.colors.urgent,
            ['>=', ['get', 'acquisition_score'], 51], designTokens.colors.hot,
            ['>=', ['get', 'acquisition_score'], 26], designTokens.colors.warm,
            designTokens.colors.cold,
          ],
          'line-width': 1.5,
          'line-opacity': [
            'case',
            ['==', ['get', 'is_government_owner'], true], 0.12,
            ['all',
              ['>=', ['get', 'acquisition_score'], 76],
              ['>=', ['to-number', ['coalesce', ['get', 'pipeline_available_mw'], 0]], 10],
            ], 0.60,  // URGENT
            ['>=', ['get', 'acquisition_score'], 51], 0.25, // HOT
            ['>=', ['get', 'acquisition_score'], 26], 0.15, // WARM
            0.08, // COLD
          ],
        },
      }, 'highways-line');

      // Parcel owner name labels
      m.addLayer({
        id: 'parcels-owner-labels',
        type: 'symbol',
        source: 'parcels',
        'source-layer': 'parcels',
        minzoom: 11,
        layout: {
          'text-field': ['coalesce', ['get', 'owner_name'], ''],
          'text-size': ['interpolate', ['linear'], ['zoom'], 11, 9, 14, 12],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-allow-overlap': false,
          'text-padding': 4,
        },
        paint: {
          'text-color': '#1a1a1a',
          'text-halo-color': haloColor,
          'text-halo-width': 1.5,
        },
      }, 'highways-line');

      // Parcel centroid dots (z6-8 where tiles contain points, not polygons)
      m.addLayer({
        id: 'parcel-centroid-dots',
        type: 'circle',
        source: 'parcels',
        'source-layer': 'parcels',
        minzoom: 6,
        maxzoom: 9,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2, 7, 3, 8, 4],
          'circle-color': [
            'case',
            ['==', ['get', 'is_government_owner'], true], designTokens.colors.government,
            ['all',
              ['>=', ['get', 'acquisition_score'], 76],
              ['>=', ['to-number', ['coalesce', ['get', 'pipeline_available_mw'], 0]], 10],
            ], designTokens.colors.urgent,
            ['>=', ['get', 'acquisition_score'], 51], designTokens.colors.hot,
            ['>=', ['get', 'acquisition_score'], 26], designTokens.colors.warm,
            designTokens.colors.cold,
          ],
          'circle-opacity': [
            'case',
            ['==', ['get', 'is_government_owner'], true], 0.15,
            ['all',
              ['>=', ['get', 'acquisition_score'], 76],
              ['>=', ['to-number', ['coalesce', ['get', 'pipeline_available_mw'], 0]], 10],
            ], 0.80,  // URGENT
            ['>=', ['get', 'acquisition_score'], 51], 0.35, // HOT
            ['>=', ['get', 'acquisition_score'], 26], 0.20, // WARM
            0.10, // COLD
          ],
        },
      }, 'highways-line');

      // Selected parcel highlight
      m.addLayer({
        id: 'parcels-selected',
        type: 'line',
        source: 'parcels',
        'source-layer': 'parcels',
        minzoom: 6,
        paint: { 'line-color': '#000000', 'line-width': 3 },
        filter: ['==', ['get', 'id'], ''],
      }, 'highways-line');

      // Georgia research site outline: a static county parcel polygon, drawn under the
      // candidate corridors and hidden until the sidebar's Georgia section turns it on.
      m.addLayer({
        id: 'georgia-sites-fill',
        type: 'fill',
        source: 'georgia-sites',
        layout: { visibility: 'none' },
        paint: { 'fill-color': '#1e2a3a', 'fill-opacity': 0.12 },
      }, 'highways-line');
      m.addLayer({
        id: 'georgia-sites-outline',
        type: 'line',
        source: 'georgia-sites',
        layout: { visibility: 'none', 'line-join': 'round' },
        paint: { 'line-color': '#1e2a3a', 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1.5, 14, 3] },
      }, 'highways-line');
      m.addLayer({
        id: 'georgia-sites-label',
        type: 'symbol',
        source: 'georgia-sites',
        minzoom: 10,
        layout: {
          visibility: 'none',
          'text-field': ['concat', ['get', 'name'], ' · ', ['get', 'parcel']],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': 12,
        },
        paint: { 'text-color': '#1e2a3a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      }, 'highways-line');

      // Georgia evidence pins: where a public document pins a diameter, meter, valve or
      // interconnect fact to one of the three lines. Colour follows the line; the short label
      // carries the fact (20″, 12″?, meter, MLV). Hidden until the Georgia section turns it on.
      m.addLayer({
        id: 'georgia-evidence-circle',
        type: 'circle',
        source: 'georgia-evidence',
        minzoom: 8,
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 4, 12, 7],
          'circle-color': ['match', ['get', 'line'], 'SNG', '#1f5fd1', 'ETNG', '#0b8f6f', 'Transco', '#d17a1f', '#1e2a3a'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });
      m.addLayer({
        id: 'georgia-evidence-label',
        type: 'symbol',
        source: 'georgia-evidence',
        minzoom: 9,
        layout: {
          visibility: 'none',
          'text-field': ['get', 'label'],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': 11,
          'text-offset': [0, 1.1],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#1e2a3a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });

      // OSM pipeline candidates — crowd-mapped corridors in the gas-transmission green but
      // dashed, so they never read as the scored EIA lines. Inserted before 'highways-line';
      // hidden until the toggle turns them on, and the sidebar shows the ODbL credit while
      // they are. Labels ride the line from zoom 10 so a screenshot names the operator.
      m.addLayer({
        id: 'osm-pipeline-candidates-line',
        type: 'line',
        source: 'osm-pipeline-candidates',
        minzoom: 7,
        layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': designTokens.colors.pipelineGasTransmission,
          'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1.5, 12, 3.5],
          'line-opacity': 0.9,
          'line-dasharray': [2, 1.5],
        },
      }, 'highways-line');
      m.addLayer({
        id: 'osm-pipeline-candidates-label',
        type: 'symbol',
        source: 'osm-pipeline-candidates',
        minzoom: 10,
        layout: {
          visibility: 'none',
          'symbol-placement': 'line',
          'symbol-spacing': 400,
          'text-field': ['coalesce', ['get', 'operator'], ['get', 'name'], ''],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': 11,
          'text-letter-spacing': 0.04,
        },
        paint: { 'text-color': '#14532d', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      }, 'highways-line');

      // Roads — FM/RM, state and county roads. Inserted before 'highways-line' so the
      // highway stays on top; hidden until the Roads toggle turns the layer on.
      const roadsPrimaryFilter = ['any',
        // State-maintained routes: interstates, US, state highways, toll, loops, spurs, business
        // routes and urban arterials (statewide the inventory carries all of them).
        ['all', ['==', ['get', 'kind'], 'inventory'], ['in', ['get', 'hsys'], ['literal', ['IH', 'US', 'SH', 'TL', 'SL', 'SS', 'BI', 'BU', 'BS', 'UA']]]],
        ['all', ['==', ['get', 'kind'], 'prisec'], ['in', ['get', 'mtfcc'], ['literal', ['S1100', 'S1200']]],
          ['!=', ['index-of', 'FM', ['coalesce', ['get', 'name'], '']], 0]],
      ] as FilterSpecification;
      const roadsFmFilter = ['any',
        ['all', ['==', ['get', 'kind'], 'inventory'], ['in', ['get', 'hsys'], ['literal', ['FM', 'RM', 'PR']]]],
        ['all', ['!=', ['get', 'kind'], 'inventory'], ['any',
          ['==', ['index-of', 'FM ', ['coalesce', ['get', 'name'], '']], 0],
          ['==', ['index-of', 'RM ', ['coalesce', ['get', 'name'], '']], 0],
          ['==', ['index-of', 'Farm', ['coalesce', ['get', 'name'], '']], 0],
        ]],
      ] as FilterSpecification;
      // Whatever the two above did not claim: inventory CR/LS, TIGER county rows, the rest.
      const roadsRest = ['all', ['!', roadsPrimaryFilter], ['!', roadsFmFilter]];
      const roadsUncertified = ['all', ['==', ['get', 'kind'], 'county'], ['!=', ['get', 'certified'], true]];

      m.addLayer({
        id: 'roads-county-uncertified',
        type: 'line',
        source: 'roads',
        // County roads and city streets only from zoom 11 — statewide they are 70% of the
        // segments and would swamp a zoom-9 view of a metro; FM/state stay at 9.
        minzoom: 11,
        layout: { visibility: 'none' },
        filter: ['all', roadsRest, roadsUncertified] as FilterSpecification,
        paint: { 'line-color': '#c7d2fe', 'line-width': 0.9, 'line-opacity': 0.55, 'line-dasharray': [3, 2] },
      }, 'highways-line');
      m.addLayer({
        id: 'roads-county',
        type: 'line',
        source: 'roads',
        minzoom: 11,
        layout: { visibility: 'none' },
        filter: ['all', roadsRest, ['!', roadsUncertified]] as FilterSpecification,
        paint: { 'line-color': '#c7d2fe', 'line-width': 0.9, 'line-opacity': 0.6 },
      }, 'highways-line');
      m.addLayer({
        id: 'roads-fm',
        type: 'line',
        source: 'roads',
        minzoom: 9,
        layout: { visibility: 'none' },
        filter: roadsFmFilter,
        paint: { 'line-color': '#a5b4fc', 'line-width': 1.2, 'line-opacity': 0.7 },
      }, 'highways-line');
      m.addLayer({
        id: 'roads-primary',
        type: 'line',
        source: 'roads',
        minzoom: 9,
        layout: { visibility: 'none' },
        filter: roadsPrimaryFilter,
        paint: { 'line-color': '#818cf8', 'line-width': 1.6, 'line-opacity': 0.75 },
      }, 'highways-line');

      // Well lateral lines (surface → bottomhole for horizontal wells)
      m.addLayer({
        id: 'well-laterals-line',
        type: 'line',
        source: 'well-laterals',
        minzoom: 8,
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#f59e0b',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 3],
          'line-opacity': 0.8,
        },
      });

      // Highlighted wells — outer glow ring behind the normal dot
      m.addLayer({
        id: 'wells-highlight-ring',
        type: 'circle',
        source: 'wells-data',
        minzoom: 8,
        filter: ['==', ['get', 'highlighted'], true],
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 8, 10, 12, 14, 16],
          'circle-color': '#f59e0b',
          'circle-opacity': 0.25,
          'circle-stroke-color': '#f59e0b',
          'circle-stroke-width': 2,
          'circle-stroke-opacity': 0.7,
        },
      });

      // Gas wells circles + labels
      m.addLayer({
        id: 'gas-wells-circles',
        type: 'circle',
        source: 'wells-data',
        minzoom: 8,
        filter: ['==', ['get', 'well_type'], 'Gas'],
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2, 10, 4, 14, 6],
          'circle-color': '#16a34a',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 0.5,
          'circle-opacity': 0.85,
        },
      });
      m.addLayer({
        id: 'gas-wells-labels',
        type: 'symbol',
        source: 'wells-data',
        minzoom: 13,
        filter: ['==', ['get', 'well_type'], 'Gas'],
        layout: {
          'text-field': ['get', 'well_name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-allow-overlap': false,
          'visibility': 'none',
        },
        paint: {
          'text-color': '#374151',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });
      // Oil wells circles + labels
      m.addLayer({
        id: 'oil-wells-circles',
        type: 'circle',
        source: 'wells-data',
        minzoom: 8,
        filter: ['==', ['get', 'well_type'], 'Oil'],
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2, 10, 4, 14, 6],
          'circle-color': '#d97706',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 0.5,
          'circle-opacity': 0.85,
        },
      });
      m.addLayer({
        id: 'oil-wells-labels',
        type: 'symbol',
        source: 'wells-data',
        minzoom: 13,
        filter: ['==', ['get', 'well_type'], 'Oil'],
        layout: {
          'text-field': ['get', 'well_name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-allow-overlap': false,
          'visibility': 'none',
        },
        paint: {
          'text-color': '#374151',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // EPA nonattainment zones (below parcels, data centers)
      // Red = active nonattainment, amber = maintenance (former nonattainment, on probation)
      m.addLayer({
        id: 'epa-zones-fill',
        type: 'fill',
        source: 'epa-zones',
        paint: {
          'fill-color': ['match', ['get', 'status'], 'Maintenance', '#d97706', '#ef4444'],
          'fill-opacity': ['match', ['get', 'status'], 'Maintenance', 0.08, 0.12],
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'epa-zones-border',
        type: 'line',
        source: 'epa-zones',
        paint: {
          'line-color': ['match', ['get', 'status'], 'Maintenance', '#d97706', '#ef4444'],
          'line-width': 1.5,
          'line-opacity': 0.4,
        },
      }, 'parcels-fill');

      // Wildfire hazard choropleth (0-5 USFS WHP). Green → yellow → red.
      m.addLayer({
        id: 'wildfire-fill',
        type: 'fill',
        source: 'wildfire',
        paint: {
          'fill-color': [
            'step', ['get', 'risk_mean'],
            '#16a34a',
            1.5, '#84cc16',
            2.5, '#eab308',
            3.5, '#f97316',
            4.5, '#dc2626',
          ],
          'fill-opacity': 0.35,
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'wildfire-border',
        type: 'line',
        source: 'wildfire',
        paint: {
          'line-color': '#78350f',
          'line-width': 0.5,
          'line-opacity': 0.4,
        },
      }, 'parcels-fill');

      // EJScreen gradient fill
      m.addLayer({
        id: 'ejscreen-fill',
        type: 'fill',
        source: 'ejscreen',
        paint: {
          'fill-color': [
            'interpolate', ['linear'], ['get', 'max_ej_pctile'],
            50, '#f59e0b',
            80, '#f97316',
            90, '#ef4444',
            100, '#dc2626',
          ],
          'fill-opacity': [
            'interpolate', ['linear'], ['get', 'max_ej_pctile'],
            50, 0.15,
            80, 0.3,
            90, 0.45,
            100, 0.55,
          ],
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'ejscreen-outline',
        type: 'line',
        source: 'ejscreen',
        paint: {
          'line-color': [
            'interpolate', ['linear'], ['get', 'max_ej_pctile'],
            50, '#f59e0b',
            80, '#f97316',
            90, '#ef4444',
            100, '#dc2626',
          ],
          'line-width': 1,
          'line-opacity': 0.5,
        },
      }, 'parcels-fill');

      // Critical Habitat (ESA-listed species, hatched pattern)
      m.addLayer({
        id: 'critical-habitat-fill',
        type: 'fill',
        source: 'critical-habitat',
        paint: {
          'fill-color': [
            'match', ['get', 'listing_status'],
            'Endangered', '#dc2626',
            'Threatened', '#f59e0b',
            '#8b5cf6',
          ],
          'fill-opacity': 0.2,
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'critical-habitat-border',
        type: 'line',
        source: 'critical-habitat',
        paint: {
          'line-color': [
            'match', ['get', 'listing_status'],
            'Endangered', '#dc2626',
            'Threatened', '#d97706',
            '#7c3aed',
          ],
          'line-width': 2,
          'line-dasharray': [3, 2],
          'line-opacity': 0.7,
        },
      }, 'parcels-fill');

      // Operator AOIs (partner-deck supply zones, e.g. Revenant East TX).
      // Rendered above parcels-fill so the AOI footprint reads cleanly even
      // when parcel acquisition-score tints are on.
      m.addLayer({
        id: 'operator-aois-buffer-halo',
        type: 'line',
        source: 'operator-aois',
        filter: ['==', ['get', 'polygon_kind'], 'buffer_halo'],
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#475569',
          'line-width': 1.5,
          'line-dasharray': [4, 3],
          'line-opacity': 0.55,
        },
      });
      m.addLayer({
        id: 'operator-aois-phase-fill',
        type: 'fill',
        source: 'operator-aois',
        filter: ['in', ['get', 'polygon_kind'], ['literal', ['phase_2', 'phase_3']]],
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'polygon_kind'],
            'phase_2', '#b45309',
            'phase_3', '#1d4ed8',
            '#64748b',
          ],
          'fill-opacity': 0.35,
        },
      });
      m.addLayer({
        id: 'operator-aois-phase-line',
        type: 'line',
        source: 'operator-aois',
        filter: ['in', ['get', 'polygon_kind'], ['literal', ['phase_2', 'phase_3']]],
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'polygon_kind'],
            'phase_2', '#b45309',
            'phase_3', '#1d4ed8',
            '#64748b',
          ],
          'line-width': 2.5,
          'line-opacity': 0.9,
        },
      });
      m.addLayer({
        id: 'operator-aois-outer-line',
        type: 'line',
        source: 'operator-aois',
        filter: ['==', ['get', 'polygon_kind'], 'aoi_outer'],
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#0f766e',
          'line-width': 2.5,
          'line-opacity': 0.95,
        },
      });

      // Mine Subsidence Zones (WV — historic underground mine footprints).
      // No beforeId — must render above parcels-fill so overlap inside acquisition-
      // score-tinted parcels stays visible.
      m.addLayer({
        id: 'mine-subsidence-fill',
        type: 'fill',
        source: 'mine-subsidence',
        paint: {
          'fill-color': '#7c2d12',
          'fill-opacity': 0.45,
        },
      });
      m.addLayer({
        id: 'mine-subsidence-border',
        type: 'line',
        source: 'mine-subsidence',
        paint: {
          'line-color': '#7c2d12',
          'line-width': 1.5,
          'line-dasharray': [2, 2],
          'line-opacity': 0.9,
        },
      });

      // AML Sites (WV — Abandoned Mine Land problem features, severity colored).
      // No beforeId for the same reason as mine-subsidence above.
      m.addLayer({
        id: 'aml-sites-fill',
        type: 'fill',
        source: 'aml-sites',
        paint: {
          'fill-color': [
            'match', ['get', 'severity'],
            'emergency', '#dc2626',
            'active', '#f97316',
            'unfunded', '#eab308',
            'completed', '#65a30d',
            '#a16207',
          ],
          'fill-opacity': 0.55,
        },
      });
      m.addLayer({
        id: 'aml-sites-border',
        type: 'line',
        source: 'aml-sites',
        paint: {
          'line-color': [
            'match', ['get', 'severity'],
            'emergency', '#991b1b',
            'active', '#c2410c',
            'unfunded', '#a16207',
            'completed', '#4d7c0f',
            '#78350f',
          ],
          'line-width': 1.5,
          'line-opacity': 1,
        },
      });

      // Water Regulatory Zones (GCDs, AMAs, NM basins) — GeoJSON polygons
      m.addLayer({
        id: 'water-reg-zones-fill',
        type: 'fill',
        source: 'water-regulatory-zones',
        paint: {
          'fill-color': [
            'match', ['get', 'zone_type'],
            'gcd', '#0284c7',            // TX Groundwater Conservation District — blue
            'ama', '#d97706',            // AZ Active Management Area — amber
            'ina', '#c48a5c',            // AZ Irrigation Non-Expansion Area — clay
            'declared_basin', '#b85c5c', // NM declared basin — brick
            'regulation', '#16a34a',     // NM regulation area — green
            'closure', '#dc2626',        // NM closure area — red
            '#6A7E8E',                   // fallback
          ],
          'fill-opacity': [
            'match', ['get', 'zone_type'],
            'closure', 0.35,
            0.15,
          ],
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'water-reg-zones-border',
        type: 'line',
        source: 'water-regulatory-zones',
        paint: {
          'line-color': [
            'match', ['get', 'zone_type'],
            'gcd', '#0284c7',
            'ama', '#d97706',
            'ina', '#c48a5c',
            'declared_basin', '#b85c5c',
            'regulation', '#16a34a',
            'closure', '#dc2626',
            '#6A7E8E',
          ],
          'line-width': 1.5,
          'line-dasharray': [4, 2],
          'line-opacity': 0.6,
        },
      }, 'parcels-fill');

      // TWDB Water Wells — point circles
      m.addLayer({
        id: 'twdb-wells-circle',
        type: 'circle',
        source: 'twdb-wells',
        minzoom: 9,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 3, 12, 5, 14, 8],
          'circle-color': '#0ea5e9',
          'circle-stroke-color': '#0c4a6e',
          'circle-stroke-width': 1,
          'circle-opacity': 0.8,
        },
      });

      // TWDB GWDB TDS samples — colored by TDS value (fresh → brackish → brine)
      m.addLayer({
        id: 'tds-samples-circle',
        type: 'circle',
        source: 'tds-samples',
        minzoom: 7,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 2.5, 10, 4, 13, 7],
          'circle-color': [
            'step', ['coalesce', ['get', 'tds_mgl'], 0],
            '#3b82f6',    // fresh <1k
            1000, '#06b6d4',   // slightly brackish 1-3k
            3000, '#14b8a6',   // moderately brackish 3-10k (BRACS sweet spot)
            10000, '#f59e0b',  // highly brackish 10-35k
            35000, '#ef4444',  // brine >35k
          ],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 0.5,
          'circle-opacity': 0.85,
        },
      });

      // Wetlands (USFWS NWI) — vector tiles, starts hidden
      m.addLayer({
        id: 'wetlands-fill',
        type: 'fill',
        source: 'wetlands',
        'source-layer': 'wetlands',
        minzoom: 8,
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'wetland_type'],
            'Freshwater Pond', '#0ea5e9',
            'Lake', '#0284c7',
            'Riverine', '#6366f1',
            'Freshwater Emergent Wetland', '#22c55e',
            'Freshwater Forested/Shrub Wetland', '#15803d',
            'Estuarine and Marine Wetland', '#14b8a6',
            '#94a3b8',
          ],
          'fill-opacity': 0.25,
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'wetlands-border',
        type: 'line',
        source: 'wetlands',
        'source-layer': 'wetlands',
        minzoom: 8,
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'wetland_type'],
            'Freshwater Pond', '#0ea5e9',
            'Lake', '#0284c7',
            'Riverine', '#6366f1',
            'Freshwater Emergent Wetland', '#22c55e',
            'Freshwater Forested/Shrub Wetland', '#15803d',
            'Estuarine and Marine Wetland', '#14b8a6',
            '#94a3b8',
          ],
          'line-width': 1,
          'line-opacity': 0.5,
        },
      }, 'parcels-fill');

      // Seismic Risk grid (USGS NSHM 2023 — probability of damaging shaking)
      m.addLayer({
        id: 'seismic-risk-circles',
        type: 'circle',
        source: 'seismic-risk',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3, 8, 6, 10, 10],
          'circle-color': [
            'interpolate', ['linear'], ['get', 'pga'],
            0, '#22c55e',    // green — negligible
            5, '#84cc16',    // lime
            10, '#eab308',   // yellow
            25, '#f97316',   // orange
            50, '#ef4444',   // red
            100, '#991b1b',  // dark red — near certain
          ],
          'circle-opacity': [
            'interpolate', ['linear'], ['get', 'pga'],
            0, 0.15,
            5, 0.35,
            15, 0.55,
            50, 0.7,
          ],
          'circle-stroke-width': 0,
        },
      }, 'parcels-fill');

      // Urban Areas (Census 2020 — community opposition proxy)
      m.addLayer({
        id: 'urban-areas-fill',
        type: 'fill',
        source: 'urban-areas',
        paint: {
          'fill-color': '#6366f1',
          'fill-opacity': 0.1,
        },
      }, 'parcels-fill');
      m.addLayer({
        id: 'urban-areas-border',
        type: 'line',
        source: 'urban-areas',
        paint: {
          'line-color': '#6366f1',
          'line-width': 1.5,
          'line-opacity': 0.5,
        },
      }, 'parcels-fill');

      // Gas processing plants (orange circles)
      m.addLayer({
        id: 'gas-plants-circle',
        type: 'circle',
        source: 'gas-plants',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            6, 4, 12, 10,
          ],
          'circle-color': '#f97316',
          'circle-stroke-color': '#9a3412',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9,
        },
      });
      m.addLayer({
        id: 'gas-plants-labels',
        type: 'symbol',
        source: 'gas-plants',
        minzoom: 10,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 1.0],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#9a3412',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // Gas infrastructure layers (compressors, receipt/delivery, interconnects, storage)
      const gasInfraConfigs = [
        { sourceId: 'gas-infra-compressors', layerId: 'gas-infra-compressors', color: designTokens.colors.gasInfraCompressor, strokeColor: '#8b3a20', label: 'Compressor' },
        { sourceId: 'gas-infra-receipt-delivery', layerId: 'gas-infra-receipt-delivery', color: designTokens.colors.gasInfraReceiptDelivery, strokeColor: '#2d5e50', label: 'Receipt/Delivery' },
        { sourceId: 'gas-infra-interconnects', layerId: 'gas-infra-interconnects', color: designTokens.colors.gasInfraInterconnect, strokeColor: '#7a5a34', label: 'Interconnect' },
        { sourceId: 'gas-infra-storage', layerId: 'gas-infra-storage', color: designTokens.colors.gasInfraStorage, strokeColor: '#3d4f6e', label: 'Storage' },
      ];
      for (const gi of gasInfraConfigs) {
        m.addLayer({
          id: gi.layerId + '-circle',
          type: 'circle',
          source: gi.sourceId,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 3, 12, 8],
            'circle-color': gi.color,
            'circle-stroke-color': gi.strokeColor,
            'circle-stroke-width': 1.5,
            'circle-opacity': 0.9,
          },
        });
        m.addLayer({
          id: gi.layerId + '-labels',
          type: 'symbol',
          source: gi.sourceId,
          minzoom: 10,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 10,
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-anchor': 'top',
            'text-offset': [0, 1.0],
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': gi.strokeColor,
            'text-halo-color': haloColor,
            'text-halo-width': 1,
          },
        });
      }

      // Data center layers (above corridor mask, below measurement)
      const dcRadius: mapboxgl.Expression = [
        'interpolate', ['linear'], ['zoom'],
        6, ['case', ['==', ['get', 'source_tag'], 'curated'], 6, 3],
        12, ['case', ['==', ['get', 'source_tag'], 'curated'], 12, 7],
      ];
      m.addLayer({
        id: 'dc-operational',
        type: 'circle',
        source: 'data-centers',
        filter: ['==', ['get', 'status'], 'operational'],
        paint: {
          'circle-radius': dcRadius,
          'circle-color': [
            'match', ['get', 'source_tag'],
            'segra', '#10b981',
            '#0d9488',
          ],
          'circle-stroke-color': [
            'match', ['get', 'source_tag'],
            'segra', '#065f46',
            '#134e4a',
          ],
          'circle-stroke-width': 2,
          'circle-opacity': 0.9,
        },
      });
      m.addLayer({
        id: 'dc-planned',
        type: 'circle',
        source: 'data-centers',
        filter: ['in', ['get', 'status'], ['literal', ['announced', 'under_construction']]],
        paint: {
          'circle-radius': dcRadius,
          'circle-color': 'transparent',
          'circle-stroke-color': '#0d9488',
          'circle-stroke-width': 2.5,
          'circle-opacity': 0.9,
        },
      });
      // "S" badge on Segra-sourced DCs (visible before name labels)
      m.addLayer({
        id: 'dc-segra-badge',
        type: 'symbol',
        source: 'data-centers',
        minzoom: 8,
        filter: ['==', ['get', 'source_tag'], 'segra'],
        layout: {
          'text-field': 'S',
          'text-size': 8,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
      m.addLayer({
        id: 'dc-labels',
        type: 'symbol',
        source: 'data-centers',
        minzoom: 10,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 1.0],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#134e4a',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // Land listings circles (BTM corridor — 200+ acres, pink)
      m.addLayer({
        id: 'land-listings-circles',
        type: 'circle',
        source: 'land-listings',
        minzoom: 6,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 5, 14, 9],
          'circle-color': '#ec4899',
          'circle-stroke-color': '#9d174d',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.85,
        },
      });

      // Grid listings circles (grid+fiber — 10-30 acres, sky blue)
      m.addLayer({
        id: 'grid-listings-circles',
        type: 'circle',
        source: 'grid-listings',
        minzoom: 6,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 5, 14, 9],
          'circle-color': '#0ea5e9',
          'circle-stroke-color': '#0369a1',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.85,
        },
      });

      // Saved locations — always-on data source; layer visibility controlled by props
      m.addSource('saved-locations', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      m.addLayer({
        id: 'saved-locations-circles',
        type: 'circle',
        source: 'saved-locations',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 4, 12, 8, 16, 11],
          'circle-color': '#eab308',
          'circle-stroke-color': '#713f12',
          'circle-stroke-width': 2,
          'circle-opacity': 0.95,
        },
      });

      m.addLayer({
        id: 'saved-locations-labels',
        type: 'symbol',
        source: 'saved-locations',
        minzoom: 10,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 1.0],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#713f12',
          'text-halo-color': haloColor,
          'text-halo-width': 1,
        },
      });

      // Measurement tool layers (always on top of everything)
      m.addSource('measurement-lines', { type: 'geojson', data: EMPTY_FC });
      m.addSource('measurement-points', { type: 'geojson', data: EMPTY_FC });
      m.addSource('measurement-preview', { type: 'geojson', data: EMPTY_FC });

      m.addLayer({
        id: 'measurement-lines',
        type: 'line',
        source: 'measurement-lines',
        paint: {
          'line-color': designTokens.colors.measurement,
          'line-width': 2,
        },
      });

      m.addLayer({
        id: 'measurement-preview',
        type: 'line',
        source: 'measurement-preview',
        paint: {
          'line-color': designTokens.colors.measurement,
          'line-width': 1.5,
          'line-dasharray': [2, 4],
        },
      });

      m.addLayer({
        id: 'measurement-points',
        type: 'circle',
        source: 'measurement-points',
        paint: {
          'circle-radius': 4,
          'circle-color': isDark ? '#E4E6E8' : '#2E3840',
          'circle-stroke-color': designTokens.colors.measurement,
          'circle-stroke-width': 2,
        },
      });
    };
    addSourcesAndLayersRef.current = addSourcesAndLayers;

    m.on('load', () => {
      addSourcesAndLayers();

      // Click: wells circles → open well detail panel
      for (const wellLayer of ['gas-wells-circles', 'oil-wells-circles'] as const) {
        m.on('click', wellLayer, (e) => {
          if (measurementActiveRef.current) return;
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            if (p.id) {
              onSelectWell?.(Number(p.id));
              e.originalEvent.stopPropagation();
            }
          }
        });
      }

      // Click: data centers → open data center detail panel
      for (const dcLayer of ['dc-operational', 'dc-planned']) {
        m.on('click', dcLayer, (e) => {
          if (measurementActiveRef.current) return;
          if (e.features?.[0]?.properties) {
            onSelectDataCenter?.(e.features[0].properties as unknown as DataCenterProperties);
            e.originalEvent.stopPropagation();
          }
        });
      }

      // Click: land listings → open listing detail panel
      m.on('click', 'land-listings-circles', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          onSelectListing?.(e.features[0].properties as unknown as LandListingProperties);
          e.originalEvent.stopPropagation();
        }
      });

      // Click: grid listings → same detail panel
      m.on('click', 'grid-listings-circles', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          onSelectListing?.(e.features[0].properties as unknown as LandListingProperties);
          e.originalEvent.stopPropagation();
        }
      });

      // Click: parcels (yield to pipeline/dc/listings clicks and measurement mode)
      m.on('click', 'parcels-fill', (e) => {
        if (measurementActiveRef.current || hasMemphisResearchHit(m, e.point) || hasEtResearchHit(m, e.point)) return;
        const pipelineHit = PIPELINE_LAYERS.some(pl =>
          m.queryRenderedFeatures(e.point, { layers: [pl.layerId] }).length > 0
        );
        if (pipelineHit) return;
        const dcHit = ['dc-operational', 'dc-planned'].some(l =>
          m.queryRenderedFeatures(e.point, { layers: [l] }).length > 0
        );
        if (dcHit) return;
        const wellsHit = m.queryRenderedFeatures(e.point, { layers: ['gas-wells-circles', 'oil-wells-circles'] }).length > 0;
        if (wellsHit) return;
        const listingsHit = m.queryRenderedFeatures(e.point, { layers: ['land-listings-circles', 'grid-listings-circles'] }).length > 0;
        if (listingsHit) return;
        const features = e.features?.filter(f => f.properties?.id) || [];
        if (features.length === 0) return;
        if (features.length === 1) { onSelectParcel(features[0].properties!.id); return; }
        // Multiple overlapping parcels: pick the one whose centroid is closest to click
        const { lng, lat } = e.lngLat;
        let best = features[0];
        let bestDist = Infinity;
        for (const f of features) {
          const cx = f.properties?.centroid_lng ?? lng;
          const cy = f.properties?.centroid_lat ?? lat;
          {
            const d = (cx - lng) ** 2 + (cy - lat) ** 2;
            if (d < bestDist) { bestDist = d; best = f; }
          }
        }
        onSelectParcel(best.properties!.id);
      });

      // Hover cursors
      m.on('mouseenter', 'parcels-fill', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'parcels-fill', () => { m.getCanvas().style.cursor = ''; });

      // Escape HTML to prevent XSS from DB-sourced strings in popups
      const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      // Pipeline popup (shared across all 3 pipeline layers)
      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
      PIPELINE_LAYERS.forEach(pl => {
        m.on('mouseenter', pl.layerId, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            const capacity = pipelineCapacityDisplay(p);
            const cap = capacity.capacityMmcf != null
              ? `<br/>${esc(capacity.label)}: ${Math.round(capacity.capacityMmcf).toLocaleString()} MMcf/d`
              : '<br/>Segment capacity unavailable';
            const mw = capacity.powerMw != null
              ? `<br/><strong style="color:#7c3aed">${capacity.powerMw >= 1000 ? (capacity.powerMw / 1000).toFixed(1) + ' GW' : Math.round(capacity.powerMw) + ' MW'}</strong> power screening equivalent`
              : '';
            const src = capacity.sourceCapacityMmcf != null
              ? `<br/>${esc(capacity.sourceLabel)}: ${capacity.sourceCapacityMmcf.toLocaleString()} MMcf/d`
              : '';
            const company = companyLabelForRef.current?.(String(p.operator || '')) ?? null;
            popup.setLngLat(e.lngLat)
              .setHTML(`<strong>${esc(p.display_name || p.name || 'Pipeline')}</strong><br/>${esc(p.operator)}${company ? ' · ' + esc(company) : ''}<br/><span style="color:${pl.color};font-weight:600">${pl.label}</span>${cap}${src}${mw}`)
              .addTo(m);
          }
        });
        m.on('mouseleave', pl.layerId, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
        // Pipeline click → open detail panel (suppressed during measurement)
        m.on('click', pl.layerId, (e) => {
          if (measurementActiveRef.current || hasMemphisResearchHit(m, e.point) || hasEtResearchHit(m, e.point)) return;
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            onSelectPipeline({
              id: p.id,
              name: p.name || 'Unknown',
              operator: p.operator || 'Unknown',
              type: p.type || '',
              source: p.source || '',
              diameter_inches: p.diameter_inches ? Number(p.diameter_inches) : null,
              capacity_mmcf_day: p.capacity_mmcf_day != null ? Number(p.capacity_mmcf_day) : null,
              mw_potential: p.mw_potential != null ? Number(p.mw_potential) : null,
              capacity_source: p.capacity_source || null,
              capacity_basis: p.capacity_basis,
              capacity_scope: p.capacity_scope,
              source_capacity_mmcf_day: p.source_capacity_mmcf_day != null ? Number(p.source_capacity_mmcf_day) : null,
              source_capacity_scope: p.source_capacity_scope,
              utilization_pct: p.utilization_pct != null ? Number(p.utilization_pct) : null,
              utilization_source: p.utilization_source || null,
              flow_volume_mmcf_day: p.flow_volume_mmcf_day ? Number(p.flow_volume_mmcf_day) : null,
            });
            e.originalEvent.stopPropagation();
          }
        });
      });

      // Carrier fiber route (Zayo / FiberLight) hover popup + click
      m.on('mouseenter', 'carrier-routes-line', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const carrier = p.carrier || 'Carrier';
          const carrierColorMap: Record<string, string> = { 'Zayo': '#0891b2', 'Segra': '#10b981', 'FiberLight': '#f59e0b' };
          const carrierColor = carrierColorMap[carrier] || NTIA_CARRIER_COLOR;
          const len = p.length_miles ? `${Number(p.length_miles).toFixed(2)} mi segment` : '';
          const tierMap: Record<string, string> = { 'Zayo': 'Tier-1 long-haul backbone', 'FiberLight': 'Regional backbone (TX / Permian focus)', 'Segra': 'Regional backbone (Eastern US)' };
          const tier = tierMap[carrier] || 'NTIA Middle Mile funded route';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:${carrierColor}">${esc(carrier)}</strong>${len ? `<br/>${esc(len)}` : ''}${tier ? `<br/><span style="font-size:9px;color:#999">${esc(tier)}</span>` : ''}<br/><span style="font-size:9px;color:#777">Click for carrier details</span>`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'carrier-routes-line', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'carrier-routes-line', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const carrier = e.features[0].properties.carrier;
          if (carrier) {
            onSelectCarrierFiber(String(carrier));
            e.originalEvent.stopPropagation();
          }
        }
      });

      // Railroad hover + click — FRA National Rail Network segments
      m.on('mouseenter', 'railroads-line', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const owner = p.owner || p.name || 'Railroad';
          const nt = p.network_type ? `<br/><span style="font-size:9px;color:#999">${esc(String(p.network_type))}</span>` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:#78716c">${esc(String(owner))}</strong>${nt}<br/><span style="font-size:9px;color:#777">Click for carrier details</span>`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'railroads-line', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'railroads-line', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const props = e.features[0].properties;
          onSelectRailroad?.({
            id: props.id ? String(props.id) : undefined,
            name: props.name || null,
            owner: props.owner || null,
            network_type: props.network_type || null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Georgia evidence pin popup — the fact, where it was fixed, its source and its distance.
      const georgiaEvidencePopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '320px' });
      m.on('mouseenter', 'georgia-evidence-circle', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'georgia-evidence-circle', () => { m.getCanvas().style.cursor = ''; });
      m.on('click', 'georgia-evidence-circle', (e) => {
        if (measurementActiveRef.current) return;
        const p = e.features?.[0]?.properties;
        if (!p) return;
        const coords = (e.features?.[0]?.geometry as GeoJSON.Point | undefined)?.coordinates;
        const where = coords ? `${Number(coords[1]).toFixed(5)}, ${Number(coords[0]).toFixed(5)}` : '';
        georgiaEvidencePopup
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="font-size:12px;line-height:1.4">` +
            `<strong>${esc(p.lineName)}</strong><br/>` +
            `${esc(p.value)}<br/>` +
            (p.changeNote ? `<span style="color:#444">${esc(p.changeNote)}</span><br/>` : '') +
            `<span style="color:#666">${esc(Number(p.distanceMi).toFixed(2))} mi from the parcel edge · ${esc(where)}</span><br/>` +
            `<span style="font-size:10px;color:#888">Position: ${esc(p.located)}<br/>Source: ${esc(p.source)}</span>` +
            `</div>`
          )
          .addTo(m);
        e.originalEvent.stopPropagation();
      });

      // Georgia site outline click popup — the county record behind the polygon.
      const georgiaSitePopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '280px' });
      m.on('click', 'georgia-sites-fill', (e) => {
        if (measurementActiveRef.current) return;
        const p = e.features?.[0]?.properties;
        if (!p) return;
        georgiaSitePopup
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="font-size:12px;line-height:1.4"><strong>${esc(p.name)}</strong><br/>` +
            `Parcel ${esc(p.parcel)} · ${esc(p.county)} County, ${esc(p.state)}<br/>` +
            `${esc(p.address)}<br/>${esc(p.recorded_acres)} ac recorded · ${esc(p.gis_acres)} ac county GIS<br/>` +
            `<span style="font-size:10px;color:#888">${esc(p.note)}</span></div>`
          )
          .addTo(m);
        e.originalEvent.stopPropagation();
      });

      // OSM pipeline candidates click popup — the raw OSM tags, the ODbL credit, and a link
      // back to the way so the mapper's source can be checked. Nothing here is verified.
      const osmCandidatePopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '280px' });
      m.on('mouseenter', 'osm-pipeline-candidates-line', () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', 'osm-pipeline-candidates-line', () => { m.getCanvas().style.cursor = ''; });
      m.on('click', 'osm-pipeline-candidates-line', (e) => {
        if (measurementActiveRef.current) return;
        // An evidence pin sits on the line; let the pin's popup win when it was the target.
        if (m.getLayer('georgia-evidence-circle') && m.queryRenderedFeatures(e.point, { layers: ['georgia-evidence-circle'] }).length) return;
        const p = e.features?.[0]?.properties;
        if (!p) return;
        const title = p.name ? String(p.name) : p.operator ? String(p.operator) : 'OSM pipeline way';
        const lines: string[] = [];
        if (p.operator) lines.push(`Operator: ${esc(p.operator)}`);
        if (p.ref) lines.push(`Ref: ${esc(p.ref)}`);
        if (p.diameter) lines.push(`Diameter tag: ${esc(p.diameter)}`);
        lines.push(`Usage: ${esc(p.usage || 'untagged')} · substance: ${esc(p.substance || 'untagged')}`);
        if (p.osm_timestamp) lines.push(`Last OSM edit: ${esc(String(p.osm_timestamp).slice(0, 10))}`);
        const osmUrl = `https://www.openstreetmap.org/${esc(p.osm_type || 'way')}/${esc(p.osm_id)}`;
        osmCandidatePopup
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="font-size:12px;line-height:1.4">` +
            `<strong>${esc(title)}</strong><br/>` +
            `<span style="color:#666">OSM candidate · not verified, not scored</span><br/>` +
            lines.join('<br/>') +
            `<br/><a href="${osmUrl}" target="_blank" rel="noopener noreferrer">View on OpenStreetMap ↗</a>` +
            `<br/><span style="font-size:10px;color:#888">© OpenStreetMap contributors (ODbL)</span>` +
            `</div>`
          )
          .addTo(m);
        e.originalEvent.stopPropagation();
      });

      // Roads click popup — TxDOT Roadway Inventory / TIGER primary-secondary / TIGER county
      const roadsPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, maxWidth: '260px' });
      const roadKindLabels: Record<string, string> = {
        inventory: 'TxDOT inventory',
        prisec: 'Primary or secondary road',
        county: 'County road',
      };
      // TxDOT pavement-type code → the label roadSurfaceShort() knows how to shorten.
      const roadSurfaceLabel = (code: number | null): string | null => {
        if (code == null || !Number.isFinite(code)) return null;
        if (code >= 1 && code <= 3) return 'concrete';
        if (code === 4) return 'Asphaltic concrete, thick (> 5.5 in)';
        if (code === 5) return 'Asphaltic concrete, intermediate (2.5–5.5 in)';
        if (code === 6) return 'Asphaltic concrete, thin (< 2.5 in)';
        if (code === 7) return 'Composite (asphalt over concrete)';
        if (code === 10) return 'Surface-treated flexible base (chip seal)';
        return null;
      };
      // 'FM0469' → 'FM 469'
      const roadRouteLabel = (hwy: string): string => {
        const parts = hwy.trim().match(/^([A-Za-z]{2})[\s-]*0*(\d.*)$/);
        return parts ? `${parts[1].toUpperCase()} ${parts[2]}` : hwy.trim();
      };
      const roadNum = (v: unknown): number | null => {
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      for (const roadLayerId of ROADS_LAYERS) {
        m.on('mouseenter', roadLayerId, () => { m.getCanvas().style.cursor = 'pointer'; });
        m.on('mouseleave', roadLayerId, () => { m.getCanvas().style.cursor = ''; });
        m.on('click', roadLayerId, (e) => {
          if (measurementActiveRef.current) return;
          const p = e.features?.[0]?.properties;
          if (!p) return;
          const kind = String(p.kind || '');
          // Inventory rows carry the raw route code as their name ('FM0469'), so the
          // formatter runs on either field; it leaves real street names alone.
          const rawTitle = p.name ? String(p.name) : p.hwy ? String(p.hwy) : '';
          const title = rawTitle ? roadRouteLabel(rawTitle) : 'Road';
          const lines: string[] = [];
          lines.push([
            roadKindLabels[kind] || 'Road',
            p.maintainer ? `${esc(String(p.maintainer))} maintained` : '',
            p.certified === true ? 'TxDOT-certified' : '',
          ].filter(Boolean).join(' · '));
          if (kind === 'inventory') {
            const lanes = roadNum(p.lanes);
            const width = roadNum(p.surface_width_ft);
            const spec = [
              lanes ? `${lanes} lanes` : '',
              width ? `${width} ft wide` : '',
              roadSurfaceShort(roadSurfaceLabel(roadNum(p.surface_code))) || '',
            ].filter(Boolean).join(' · ');
            if (spec) lines.push(spec);
            const adt = roadNum(p.aadt);
            const year = roadNum(p.aadt_year);
            if (adt != null) {
              lines.push([
                `Average daily traffic ${adt.toLocaleString()}${year ? ` (${year})` : ''}`,
                truckShareWords(roadNum(p.truck_pct)) || '',
                trafficWord(adt) || '',
              ].filter(Boolean).join(' · '));
            }
          }
          const html = `<strong style="color:${designTokens.colors.highway}">${esc(title)}</strong>`
            + lines.filter(Boolean).map(l => `<br/><span style="font-size:10px;color:#999">${l}</span>`).join('');
          roadsPopup.setLngLat(e.lngLat).setHTML(html).addTo(m);
          e.originalEvent.stopPropagation();
        });
      }

      // IXP & Colo facility hover popup
      m.on('mouseenter', 'ixp-facilities-circle', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const isIxp = p.type === 'ixp';
          const color = isIxp ? '#10b981' : '#6ee7b7';
          const label = isIxp ? 'Internet Exchange' : 'Colo Facility';
          const nets = p.networks_count ? `${p.networks_count} networks` : '';
          const loc = [p.city, p.state].filter(Boolean).join(', ');
          const site = p.website ? `<br/><span style="font-size:9px;color:#6ee7b7">${esc(String(p.website).replace(/^https?:\/\//, ''))}</span>` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:${color}">${esc(p.name)}</strong><br/><span style="font-size:10px;color:#999">${esc(label)}</span>${loc ? `<br/><span style="font-size:10px;color:#ccc">${esc(loc)}</span>` : ''}${nets ? `<br/>${esc(nets)}` : ''}${site}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'ixp-facilities-circle', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'ixp-facilities-circle', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectIxpFacility?.({
            id: Number(p.id),
            peeringdb_id: Number(p.peeringdb_id),
            type: p.type as 'ixp' | 'facility',
            name: String(p.name || ''),
            city: p.city ? String(p.city) : null,
            state: p.state ? String(p.state) : null,
            networks_count: Number(p.networks_count) || 0,
            website: p.website ? String(p.website) : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Fiber-ROW pipelines hover popup + click (separate from the normal pipeline layers
      // because this layer shows a subset with dashed styling and its own attribution)
      m.on('mouseenter', 'fiber-row-pipelines-line', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const dia = p.diameter_inches ? `${Number(p.diameter_inches)}\u2033 · ` : '';
          const capacity = pipelineCapacityDisplay(p);
          const cap = capacity.capacityMmcf != null ? `${esc(capacity.label)}: ${Math.round(capacity.capacityMmcf).toLocaleString()} MMcf/d` : 'Segment capacity unavailable';
          const mw = capacity.powerMw != null ? `<br/>${capacity.powerMw >= 1000 ? (capacity.powerMw / 1000).toFixed(1) + ' GW' : Math.round(capacity.powerMw) + ' MW'} power screening equivalent` : '';
          const fiber = p.fiber_company ? `<br/><strong style="color:#7c3aed">Dark fiber: ${esc(p.fiber_company)}</strong>` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Pipeline')}</strong><br/>${esc(p.operator)}<br/>${dia}${cap}${mw}${fiber}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'fiber-row-pipelines-line', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'fiber-row-pipelines-line', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectPipeline({
            id: p.id,
            name: p.name || 'Unknown',
            operator: p.operator || 'Unknown',
            type: p.type || '',
            source: p.source || '',
            diameter_inches: p.diameter_inches ? Number(p.diameter_inches) : null,
            capacity_mmcf_day: p.capacity_mmcf_day != null ? Number(p.capacity_mmcf_day) : null,
            mw_potential: p.mw_potential != null ? Number(p.mw_potential) : null,
            capacity_source: p.capacity_source || null,
            capacity_basis: p.capacity_basis,
            capacity_scope: p.capacity_scope,
            source_capacity_mmcf_day: p.source_capacity_mmcf_day != null ? Number(p.source_capacity_mmcf_day) : null,
            source_capacity_scope: p.source_capacity_scope,
            utilization_pct: p.utilization_pct != null ? Number(p.utilization_pct) : null,
            utilization_source: p.utilization_source || null,
            flow_volume_mmcf_day: p.flow_volume_mmcf_day ? Number(p.flow_volume_mmcf_day) : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // ERCOT queue heatmap popup (mousemove for adjacent polygons)
      m.on('mousemove', 'queue-heatmap-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const mw = Number(p.pending_mw);
          const severity = mw > 3000 ? '#dc2626' : mw > 1000 ? '#d97706' : '#16a34a';
          const label = mw > 3000 ? 'High Congestion' : mw > 1000 ? 'Moderate' : 'Low';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.county)} Co., ${esc(p.state)}</strong><br/><span style="color:${severity};font-weight:600">${esc(label)}</span><br/>${mw.toLocaleString()} MW pending (${p.active_projects} projects)<br/>${Number(p.completed_mw).toLocaleString()} MW completed`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'queue-heatmap-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'queue-heatmap-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectQueueCounty?.({
            county: String(p.county),
            state: String(p.state),
            active_projects: Number(p.active_projects),
            pending_mw: Number(p.pending_mw),
            completed_projects: Number(p.completed_projects),
            completed_mw: Number(p.completed_mw),
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Click: congestion heatmap → open detail panel
      m.on('click', 'congestion-heatmap-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectCongestionCounty?.({
            county: String(p.county),
            constraint_count: Number(p.constraint_count),
            max_hours_binding: Number(p.max_hours_binding),
            avg_hours_binding: Number(p.avg_hours_binding),
            max_avg_shadow_price: Number(p.max_avg_shadow_price),
            total_binding_hours: Number(p.total_binding_hours),
            severity: String(p.severity),
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Grid capacity transmission lines popup
      m.on('mouseenter', 'grid-capacity-lines', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const tierLabels: Record<string, string> = { high: 'High Capacity', likely: 'Likely', moderate: 'Moderate' };
          const tierColors: Record<string, string> = { high: '#16a34a', likely: '#0891b2', moderate: '#d97706' };
          const tier = p.capacity_tier || 'moderate';
          const tierLabel = `<span style="color:${tierColors[tier]};font-weight:600">${tierLabels[tier] || tier}</span>`;
          const volts = p.voltage_kv ? `${esc(p.voltage_kv)} kV` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Transmission Line')}</strong><br/>75 MW: ${tierLabel}${volts ? '<br/>' + volts : ''}${p.operator ? '<br/>' + esc(p.operator) : ''}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'grid-capacity-lines', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Grid capacity substations popup
      m.on('mouseenter', 'grid-capacity-circles', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const tierLabels: Record<string, string> = { high: 'High Confidence', likely: 'Likely', moderate: 'Moderate', low: 'Low Confidence' };
          const tierColors: Record<string, string> = { high: '#16a34a', likely: '#0891b2', moderate: '#d97706', low: '#6A7E8E' };
          const tier = p.capacity_tier || 'low';
          const tierLabel = `<span style="color:${tierColors[tier]};font-weight:600">${tierLabels[tier] || tier}</span>`;
          const lines = p.lines ? `${p.lines} lines` : '';
          const volts = p.voltage_kv ? `${esc(p.voltage_kv)} kV` : '';
          const detail = [volts, lines].filter(Boolean).join(' · ');
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Substation')}</strong><br/>75 MW Capacity: ${tierLabel}${detail ? '<br/>' + detail : ''}${p.operator ? '<br/>' + esc(p.operator) : ''}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'grid-capacity-circles', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      for (const layerId of ['grid-capacity-circles', 'power-substations']) {
        m.on('click', layerId, (e) => {
          if (measurementActiveRef.current) return;
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            onSelectSubstation?.({
              id: p.id,
              name: p.name || null,
              operator: p.operator || null,
              voltage_kv: p.voltage_kv ? Number(p.voltage_kv) : null,
              lines: p.lines ? Number(p.lines) : null,
              capacity_tier: p.capacity_tier || null,
              county: p.county || null,
              state: p.state || null,
              status: p.status || null,
              metadata: typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata,
            });
            e.originalEvent.stopPropagation();
          }
        });
      }

      // Wells hover popup + cursor
      for (const wellLayer of ['gas-wells-circles', 'oil-wells-circles'] as const) {
        m.on('mouseenter', wellLayer, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            const name = p.well_name ? `<strong>${esc(p.well_name)}</strong>` : '<strong>Well</strong>';
            const op = p.operator ? `<br/>${esc(p.operator)}` : '';
            const typeColor = p.well_type === 'Gas' ? '#16a34a' : p.well_type === 'Oil' ? '#d97706' : '#6b7280';
            const typeLabel = `<br/><span style="color:${typeColor};font-weight:600">${esc(p.well_type || 'Unknown')} Well</span>`;
            const gas = p.gas_prod_mcf_12mo > 0 ? `<br/>Gas: ${Number(p.gas_prod_mcf_12mo).toLocaleString()} Mcf/12mo` : '';
            const oil = p.oil_prod_bbl_12mo > 0 ? `<br/>Oil: ${Number(p.oil_prod_bbl_12mo).toLocaleString()} bbl/12mo` : '';
            popup.setLngLat(e.lngLat)
              .setHTML(`${name}${op}${typeLabel}${gas}${oil}`)
              .addTo(m);
          }
        });
        m.on('mouseleave', wellLayer, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
      }

      // Power infrastructure popups (substations + power plants)
      for (const layerId of ['power-substations', 'power-plants']) {
        m.on('mouseenter', layerId, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            const volts = p.voltage_kv ? `<br/>${esc(p.voltage_kv)} kV` : '';
            const mw = p.capacity_mw ? `<br/>${esc(p.capacity_mw)} MW` : '';
            const fuel = p.fuel_source ? `<br/>Fuel: ${esc(p.fuel_source)}` : '';
            const typeLabel = p.type === 'substation' ? 'Substation' : 'Power Plant';
            popup.setLngLat(e.lngLat)
              .setHTML(`<strong>${esc(p.name || typeLabel)}</strong><br/><span style="color:#f59e0b;font-weight:600">${typeLabel}</span>${volts}${mw}${fuel}${p.operator ? '<br/>' + esc(p.operator) : ''}`)
              .addTo(m);
          }
        });
        m.on('mouseleave', layerId, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
      }

      // OSM switchyards hover + click
      m.on('mouseenter', 'osm-switchyards-circle', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const hifldMissing = p.hifld_backed === false || p.hifld_backed === 'false';
          const accent = hifldMissing ? '#10b981' : '#f59e0b';
          const label = hifldMissing ? 'HIFLD-MISSING SWITCHYARD' : 'HIFLD-CONFIRMED SWITCHYARD';
          const volts = p.voltage_kv ? `<br/>${esc(p.voltage_kv)} kV` : '';
          const op = p.operator ? `<br/>${esc(p.operator)}` : '';
          const cls = p.classifier ? `<br/><span style="color:#9ca3af;font-size:11px">via ${esc(p.classifier)}</span>` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || '(unnamed switchyard)')}</strong><br/><span style="color:${accent};font-weight:600">${esc(label)}</span>${volts}${op}${cls}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'osm-switchyards-circle', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'osm-switchyards-circle', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectOsmSwitchyard?.({
            id: Number(p.id),
            osm_type: String(p.osm_type),
            osm_id: p.osm_id,
            name: p.name || null,
            operator: p.operator || null,
            voltage_kv: p.voltage_kv != null && p.voltage_kv !== '' ? Number(p.voltage_kv) : null,
            classifier: (p.classifier || null) as OsmSwitchyardProperties['classifier'],
            state: p.state || null,
            hifld_backed: p.hifld_backed === true || p.hifld_backed === 'true'
              ? true
              : p.hifld_backed === false || p.hifld_backed === 'false'
              ? false
              : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Transmission lines hover
      m.on('mouseenter', 'power-lines', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const volts = p.voltage_kv ? `<br/>${esc(p.voltage_kv)} kV` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Transmission Line')}</strong><br/><span style="color:#f59e0b;font-weight:600">Transmission Line</span>${volts}${p.operator ? '<br/>' + esc(p.operator) : ''}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'power-lines', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Data center popups
      for (const layerId of ['dc-operational', 'dc-planned']) {
        m.on('mouseenter', layerId, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            const statusColors: Record<string, string> = { operational: '#0d9488', under_construction: '#d97706', announced: '#6366f1' };
            const statusLabels: Record<string, string> = { operational: 'Operational', under_construction: 'Under Construction', announced: 'Announced' };
            const color = statusColors[p.status] || '#0d9488';
            const review = parseReview(p.review);
            const label = review.operating_status?.replace(/_/g, ' ') || statusLabels[p.status] || p.status;
            let html = `<strong>${esc(p.name)}</strong><br/><span style="color:${color};font-weight:600">${esc(label)}</span>`;
            html += `<br/>${esc(p.operator)}`;
            if (p.capacity_mw != null) html += `<br/>${esc(capacityLabel(review))}: <strong>${Number(p.capacity_mw).toLocaleString()} MW</strong>`;
            if (!preciseLocation(review)) html += '<br/>Approximate / unverified location';
            if (p.num_buildings) html += `<br/>${p.num_buildings} building${Number(p.num_buildings) > 1 ? 's' : ''}`;
            if (p.acreage) html += `<br/>${Number(p.acreage).toLocaleString()} acres`;
            if (p.investment_usd) { const inv = Number(p.investment_usd); html += inv >= 1e9 ? `<br/>$${(inv / 1e9).toFixed(1)}B investment` : `<br/>$${Math.round(inv / 1e6)}M investment`; }
            if (p.utility_provider) html += `<br/>Utility: ${esc(p.utility_provider)}`;
            if (review.target_delivery) html += `<br/>Target delivery: ${esc(review.target_delivery)}`;
            else if (p.year_opened) html += `<br/>Reported opening year: ${esc(p.year_opened)}`;
            else if (p.year_announced) html += `<br/>Announced: ${p.year_announced}`;
            if (p.nearest_pipeline_distance_mi != null) {
              const d = Number(p.nearest_pipeline_distance_mi);
              const dist = d < 1 ? '< 1 mi' : `${d.toFixed(1)} mi`;
              html += `<br/>Nearest pipeline: <strong>${dist}</strong>`;
              if (p.nearest_pipeline_name) html += ` (${esc(p.nearest_pipeline_name)})`;
            }
            if (p.notes) html += `<br/><em style="color:#6b7280;font-size:11px">${esc(p.notes)}</em>`;
            popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
          }
        });
        m.on('mouseleave', layerId, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
      }

      // Gas plant popups
      m.on('mouseenter', 'gas-plants-circle', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          let html = `<strong>${esc(p.name || 'Gas Plant')}</strong><br/><span style="color:#f97316;font-weight:600">Gas Processing Plant</span>`;
          if (p.operator) html += `<br/>${esc(p.operator)}`;
          if (p.capacity_mmcf_d) html += `<br/><strong>${Number(p.capacity_mmcf_d).toLocaleString()} MMcf/d</strong>`;
          if (p.county && p.state) html += `<br/>${esc(p.county)} Co., ${esc(p.state)}`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'gas-plants-circle', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Gas infrastructure popups
      const gasInfraPopupConfigs: { layerId: string; color: string; typeLabel: string }[] = [
        { layerId: 'gas-infra-compressors-circle', color: designTokens.colors.gasInfraCompressor, typeLabel: 'Compressor Station' },
        { layerId: 'gas-infra-receipt-delivery-circle', color: designTokens.colors.gasInfraReceiptDelivery, typeLabel: 'Receipt/Delivery Point' },
        { layerId: 'gas-infra-interconnects-circle', color: designTokens.colors.gasInfraInterconnect, typeLabel: 'Pipeline Interconnect' },
        { layerId: 'gas-infra-storage-circle', color: designTokens.colors.gasInfraStorage, typeLabel: 'Gas Storage Facility' },
      ];
      for (const gi of gasInfraPopupConfigs) {
        m.on('mouseenter', gi.layerId, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            let html = `<strong>${esc(p.name || 'Unknown')}</strong><br/><span style="color:${gi.color};font-weight:600">${gi.typeLabel}</span>`;
            if (p.operator || p.pipeline_company) html += `<br/>${esc(p.operator || p.pipeline_company)}`;
            if (p.horsepower) html += `<br/><strong>${Number(p.horsepower).toLocaleString()} HP</strong>`;
            if (p.methane_mt) html += `<br/>CH₄: ${Number(p.methane_mt).toLocaleString()} MT/yr`;
            if (p.capacity_dth_day) html += `<br/><strong>${Number(p.capacity_dth_day).toLocaleString()} Dth/day</strong>`;
            if (p.county && p.state) html += `<br/>${esc(p.county)} Co., ${esc(p.state)}`;
            popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
          }
        });
        m.on('mouseleave', gi.layerId, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
        m.on('click', gi.layerId, (e) => {
          if (measurementActiveRef.current) return;
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            onSelectGasInfra?.({
              id: p.id,
              type: p.type,
              name: p.name || null,
              operator: p.operator || null,
              pipeline_company: p.pipeline_company || null,
              capacity_dth_day: p.capacity_dth_day ? Number(p.capacity_dth_day) : null,
              horsepower: p.horsepower ? Number(p.horsepower) : null,
              num_units: p.num_units ? Number(p.num_units) : null,
              connecting_companies: p.connecting_companies || null,
              facility_type: p.facility_type || null,
              status: p.status || null,
              state: p.state || null,
              county: p.county || null,
              methane_mt: p.methane_mt ? Number(p.methane_mt) : null,
              source: p.source || null,
            });
            e.originalEvent.stopPropagation();
          }
        });
      }

      // EPA zone popups
      m.on('mouseenter', 'epa-zones-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const isMaint = p.status === 'Maintenance';
          const statusColor = isMaint ? '#d97706' : '#ef4444';
          let html = `<strong>${esc(p.area_name || 'EPA Zone')}</strong>`;
          html += `<br/><span style="color:${statusColor};font-weight:600">${esc(p.pollutant || 'Unknown')} ${isMaint ? 'Maintenance' : 'Nonattainment'}</span>`;
          if (p.classification) html += `<br/>Classification: ${esc(p.classification)}`;
          if (p.standard) html += `<br/><span style="font-size:10px;color:#888">NAAQS standard: ${esc(p.standard)}</span>`;
          // Permitting impact note
          if (!isMaint) {
            const cls = p.classification || '';
            const timeline = /severe/i.test(cls) ? '18\u201336 mo, offsets 1.3:1' : /serious/i.test(cls) ? '18\u201336 mo, offsets 1.2:1' : '12\u201324 mo additional';
            html += `<br/><span style="font-size:10px;color:${statusColor}">Permitting impact: +${timeline}</span>`;
          } else {
            html += `<br/><span style="font-size:10px;color:${statusColor}">Former nonattainment \u2014 contingency measures apply</span>`;
          }
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'epa-zones-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Wildfire county choropleth hover popup
      m.on('mouseenter', 'wildfire-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const mean = typeof p.risk_mean === 'number' ? p.risk_mean : parseFloat(p.risk_mean);
          const max = typeof p.risk_max === 'number' ? p.risk_max : parseFloat(p.risk_max);
          const label = !isFinite(mean) ? 'Unknown' :
            mean < 1.5 ? 'Very Low' :
            mean < 2.5 ? 'Low' :
            mean < 3.5 ? 'Moderate' :
            mean < 4.5 ? 'High' : 'Very High';
          const color = !isFinite(mean) ? '#6b7280' :
            mean < 1.5 ? '#16a34a' :
            mean < 2.5 ? '#84cc16' :
            mean < 3.5 ? '#eab308' :
            mean < 4.5 ? '#f97316' : '#dc2626';
          let html = `<strong>${esc(p.county)}, ${esc(p.state)}</strong>`;
          html += `<br/><span style="color:${color};font-weight:600">Wildfire Risk: ${label}</span>`;
          if (isFinite(mean)) html += `<br/><span style="font-size:10px;color:#888">Mean WHP: ${mean.toFixed(2)}${isFinite(max) ? ` · Max: ${max.toFixed(2)}` : ''}</span>`;
          html += `<br/><span style="font-size:10px;color:#888">USFS Wildfire Hazard Potential (0\u20135)</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'wildfire-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'wildfire-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectWildfireCounty?.({
            state: p.state || null,
            county: p.county || null,
            risk_mean: p.risk_mean != null ? Number(p.risk_mean) : null,
            risk_max: p.risk_max != null ? Number(p.risk_max) : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Flood zone hover popup
      for (const floodLayer of ['flood-zones-fill', 'pending-flood-zones-fill']) {
      m.on('mouseenter', floodLayer, (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const is100yr = p.flood_type === '100yr';
          const pending = p.source === 'fema_pending';
          const color = pending ? PENDING_FEMA_COLOR : is100yr ? '#dc2626' : '#f97316';
          const label = pending ? esc(pendingFemaLabel(p.effective_date)) : is100yr ? '1% Annual-Chance Flood Zone' : p.flood_type === '500yr' ? '0.2% Annual-Chance Flood Zone' : 'Undetermined Risk';
          let meta = null;
          try { meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata; } catch { /* malformed FEMA record */ }
          let html = `<strong style="color:${color}">${label}</strong>`;
          html += `<br/>FEMA Zone: <strong>${esc(p.zone_code)}</strong>`;
          if (pending) html += `<br/>${is100yr ? '1%' : '0.2%'} annual-chance hazard · future mapping`;
          if (meta?.zone_subtype) html += `<br/>${esc(meta.zone_subtype)}`;
          if (meta?.static_bfe) html += `<br/>Base Flood Elev: ${esc(meta.static_bfe)} ft`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', floodLayer, () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      // Flood zone click → open detail panel
      m.on('click', floodLayer, (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          let meta = null;
          try { meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata; } catch { /* malformed FEMA record */ }
          onSelectFloodZone?.({
            id: p.id,
            zone_code: p.zone_code || '',
            flood_type: p.flood_type || '',
            metadata: meta || null,
            ...(p.source === 'fema_pending' ? { source: 'fema_pending' as const, product_id: p.product_id, effective_date: p.effective_date, source_url: p.source_url } : {}),
          });
          e.originalEvent.stopPropagation();
        }
      });
      }

      // EJ Screen click-to-inspect (hover is unreliable on dense block groups)
      m.on('click', 'ejscreen-fill', (e) => {
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const pctile = Number(p.max_ej_pctile) || 0;
          const color = pctile >= 90 ? '#dc2626' : pctile >= 80 ? '#f97316' : '#f59e0b';

          const pctBar = (label: string, val: number | null) => {
            if (val == null) return '';
            const barColor = val >= 90 ? '#dc2626' : val >= 80 ? '#f97316' : val >= 50 ? '#f59e0b' : '#94a3b8';
            const width = Math.max(val, 4);
            return `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
              <span style="font-size:10px;width:48px;color:#888">${esc(label)}</span>
              <div style="flex:1;height:6px;background:#1e293b;border-radius:3px;overflow:hidden">
                <div style="width:${width}%;height:100%;background:${barColor};border-radius:3px"></div>
              </div>
              <span style="font-size:10px;font-weight:600;color:${barColor};width:28px;text-align:right">${val}</span>
            </div>`;
          };

          let html = `<strong>${esc(p.county_name || 'Block Group')}</strong>`;
          html += `<br/><span style="color:${color};font-weight:600">${pctile}th percentile EJ burden</span>`;
          if (p.population) html += `<span style="font-size:10px;color:#888"> · Pop. ${Number(p.population).toLocaleString()}</span>`;

          html += `<div style="margin-top:6px;min-width:180px">`;
          html += pctBar('PM2.5', p.pctile_ej_pm25);
          html += pctBar('Ozone', p.pctile_ej_ozone);
          html += pctBar('NO\u2082', p.pctile_ej_no2);
          html += `</div>`;

          if (p.demog_idx != null) {
            const dIdx = Number(p.demog_idx);
            html += `<div style="font-size:10px;color:#888;margin-top:4px">Demographic index: ${dIdx.toFixed(0)}%</div>`;
          }
          html += `<div style="font-size:9px;color:#555;margin-top:2px">EPA EJScreen · national percentiles</div>`;

          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });

      // Critical Habitat popups
      m.on('mouseenter', 'critical-habitat-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const statusColor = p.listing_status === 'Endangered' ? '#dc2626' : '#d97706';
          let html = `<strong>${esc(p.common_name || 'Critical Habitat')}</strong>`;
          html += `<br/><em>${esc(p.scientific_name || '')}</em>`;
          html += `<br/><span style="color:${statusColor};font-weight:600">ESA ${esc(p.listing_status || 'Listed')}</span>`;
          html += `<br/><span style="font-size:10px;color:${statusColor}">Requires Section 7 consultation</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'critical-habitat-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Wetlands popups
      m.on('mouseenter', 'wetlands-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const typeColors: Record<string, string> = {
            'Freshwater Pond': '#0ea5e9', 'Lake': '#0284c7',
            'Riverine': '#6366f1', 'Freshwater Emergent Wetland': '#22c55e',
            'Freshwater Forested/Shrub Wetland': '#15803d',
            'Estuarine and Marine Wetland': '#14b8a6',
          };
          const color = typeColors[p.wetland_type] || '#94a3b8';
          let html = `<strong style="color:${color}">${esc(p.wetland_type || 'Wetland')}</strong>`;
          if (p.attribute_code) html += `<br/><span style="font-size:10px;color:#666">Code: ${esc(p.attribute_code)}</span>`;
          if (p.acres) html += `<br/>${Number(p.acres).toLocaleString(undefined, { maximumFractionDigits: 1 })} acres`;
          html += `<br/><span style="font-size:9px;color:#888">USFWS National Wetlands Inventory</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'wetlands-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Water Regulatory Zones hover (mousemove to handle adjacent polygons)
      let hoveredWaterRegZoneId: number | null = null;
      m.on('mousemove', 'water-reg-zones-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        const feat = e.features?.[0];
        if (!feat?.properties) return;
        const fid = feat.id as number;
        if (fid === hoveredWaterRegZoneId) return; // same zone, skip
        hoveredWaterRegZoneId = fid;
        const p = feat.properties;
        const typeColors: Record<string, string> = {
          'gcd': '#0284c7', 'ama': '#d97706', 'ina': '#c48a5c',
          'declared_basin': '#b85c5c', 'regulation': '#16a34a', 'closure': '#dc2626',
        };
        const color = typeColors[p.zone_type] || '#6A7E8E';
        const typeLabels: Record<string, string> = {
          'gcd': 'Groundwater Conservation District', 'ama': 'Active Management Area', 'ina': 'Irrigation Non-Expansion Area',
          'declared_basin': 'Declared Basin', 'regulation': 'Regulation Area', 'closure': 'Closure Area',
        };
        let html = `<strong style="color:${color}">${esc(p.name || typeLabels[p.zone_type] || 'Regulatory Zone')}</strong>`;
        html += `<br/><span style="font-size:10px;color:${color}">${esc(typeLabels[p.zone_type] || p.zone_type)}</span>`;
        if (p.state) html += ` <span style="font-size:10px;color:#888">(${esc(p.state)})</span>`;
        if (p.requirements) html += `<br/><span style="font-size:10px">${esc(p.requirements)}</span>`;
        html += `<br/><span style="font-size:9px;color:#888">Click for details</span>`;
        popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
      });
      m.on('mouseleave', 'water-reg-zones-fill', () => {
        m.getCanvas().style.cursor = '';
        hoveredWaterRegZoneId = null;
        popup.remove();
      });
      // Water Regulatory Zones click → detail panel
      m.on('click', 'water-reg-zones-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          let meta = null;
          try { meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata; } catch { /* */ }
          onSelectWaterRegZone?.({
            id: p.id,
            name: p.name || '',
            zone_type: p.zone_type || '',
            state: p.state || '',
            requirements: p.requirements || null,
            geologic_unit: p.geologic_unit || null,
            source: p.source || null,
            metadata: meta,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // TWDB Water Wells popups
      m.on('mouseenter', 'twdb-wells-circle', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          let html = `<strong style="color:#0891b2">Water Well</strong>`;
          if (p.proposed_use) html += `<br/>${esc(p.proposed_use)}`;
          if (p.total_depth_ft) html += `<br/>Depth: ${p.total_depth_ft} ft`;
          if (p.yield_gpm) html += `<br/>Yield: ${p.yield_gpm} GPM`;
          if (p.drill_date) html += `<br/>Drilled: ${String(p.drill_date).slice(0, 4)}`;
          if (p.county) html += `<br/>${esc(p.county)} County`;
          html += `<br/><span style="font-size:9px;color:#888">TWDB Submitted Drillers Reports</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'twdb-wells-circle', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'twdb-wells-circle', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectWaterWell?.({
            id: Number(p.id),
            tracking_number: p.tracking_number ?? null,
            county: p.county ?? null,
            proposed_use: p.proposed_use ?? null,
            total_depth_ft: p.total_depth_ft != null ? Number(p.total_depth_ft) : null,
            yield_gpm: p.yield_gpm != null ? Number(p.yield_gpm) : null,
            drawdown_ft: p.drawdown_ft != null ? Number(p.drawdown_ft) : null,
            drill_date: p.drill_date ?? null,
            driller_name: p.driller_name ?? null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Brackish aquifer extent popups
      m.on('mouseenter', 'brackish-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          let html = `<strong style="color:#0f766e">Brackish Aquifer Extent</strong>`;
          if (p.aquifer_name) html += `<br/>${esc(p.aquifer_name)}`;
          if (p.tds_min != null || p.tds_max != null) {
            html += `<br/>TDS range: ${p.tds_min ?? '?'}–${p.tds_max ?? '?'} mg/L`;
          } else {
            html += `<br/><span style="color:#888">TDS range: see nearby samples</span>`;
          }
          if (p.area_sq_miles) html += `<br/>${Math.round(p.area_sq_miles).toLocaleString()} sq mi`;
          html += `<br/><span style="font-size:9px;color:#888">TWDB BRACS (2024) · click for details</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'brackish-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'brackish-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectBrackishZone?.({
            id: p.id,
            name: p.name ?? null,
            zone_type: p.zone_type || 'brackish_extent',
            aquifer_name: p.aquifer_name ?? null,
            tds_min: p.tds_min != null ? Number(p.tds_min) : null,
            tds_max: p.tds_max != null ? Number(p.tds_max) : null,
            area_sq_miles: p.area_sq_miles != null ? Number(p.area_sq_miles) : null,
            source: p.source ?? null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // TDS sample popups
      m.on('mouseenter', 'tds-samples-circle', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const tds = p.tds_mgl != null ? Number(p.tds_mgl) : null;
          let band = 'Unknown';
          let color = '#64748b';
          if (tds != null) {
            if (tds < 1000)       { band = 'Fresh';              color = '#3b82f6'; }
            else if (tds < 3000)  { band = 'Slightly brackish';  color = '#06b6d4'; }
            else if (tds < 10000) { band = 'Moderately brackish';color = '#14b8a6'; }
            else if (tds < 35000) { band = 'Highly brackish';    color = '#f59e0b'; }
            else                   { band = 'Brine';              color = '#ef4444'; }
          }
          let html = `<strong style="color:${color}">Water Quality Sample</strong>`;
          if (tds != null) html += `<br/><span style="font-weight:600">TDS: ${tds.toLocaleString()} mg/L</span> <span style="color:${color}">(${band})</span>`;
          if (p.aquifer_name) html += `<br/>${esc(p.aquifer_name)}`;
          if (p.depth_ft) html += `<br/>Depth: ${p.depth_ft} ft`;
          if (p.sample_date) html += `<br/>Sampled: ${String(p.sample_date).slice(0, 10)}`;
          if (p.county) html += `<br/>${esc(p.county)}`;
          html += `<br/><span style="font-size:9px;color:#888">TWDB GWDB</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'tds-samples-circle', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'tds-samples-circle', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectTdsSample?.({
            id: Number(p.id),
            sample_id: p.sample_id ?? null,
            tds_mgl: p.tds_mgl != null ? Number(p.tds_mgl) : null,
            sample_date: p.sample_date ?? null,
            depth_ft: p.depth_ft != null ? Number(p.depth_ft) : null,
            aquifer_name: p.aquifer_name ?? null,
            county: p.county ?? null,
            source: p.source ?? null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Seismic Risk popups
      m.on('mouseenter', 'seismic-risk-circles', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const prob = Number(p.pga) || 0;
          const riskLabel = prob < 2 ? 'Very Low' : prob < 5 ? 'Low' : prob < 15 ? 'Moderate' : prob < 40 ? 'High' : 'Very High';
          const color = prob < 5 ? '#22c55e' : prob < 15 ? '#eab308' : prob < 40 ? '#f97316' : '#ef4444';
          let html = `<strong>Seismic Risk</strong>`;
          html += `<br/><span style="color:${color};font-weight:600">${riskLabel} (${prob.toFixed(1)}%)</span>`;
          html += `<br/><span style="font-size:10px;color:#888">Probability of damaging ground<br/>shaking (MMI VI+) in 100 years</span>`;
          html += `<br/><span style="font-size:10px;color:#888">USGS NSHM 2023</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'seismic-risk-circles', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'seismic-risk-circles', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectSeismicPoint?.({
            id: p.id,
            pga: p.pga != null ? Number(p.pga) : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Mine Subsidence popups
      m.on('mouseenter', 'mine-subsidence-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const seam = p.seam ? ` · ${esc(p.seam)} seam` : '';
          let html = `<strong>${esc(p.facility_name || 'Underground Mine')}</strong>`;
          if (p.operator) html += `<br/><span style="color:#94a3b8">${esc(p.operator)}</span>`;
          html += `<br/><span style="color:#a16207;font-weight:600">Historic mine footprint${seam}</span>`;
          html += `<br/><span style="font-size:10px;color:#888">Foundation risk — geotechnical verification required</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'mine-subsidence-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'mine-subsidence-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectMineSubsidence?.({
            id: p.id,
            facility_name: p.facility_name || null,
            operator: p.operator || null,
            permit_id: p.permit_id || null,
            seam: p.seam || null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // AML Sites popups
      m.on('mouseenter', 'aml-sites-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const sev = (p.severity || 'unknown') as string;
          const sevColor = sev === 'emergency' ? '#dc2626' : sev === 'active' ? '#f97316' : sev === 'unfunded' ? '#eab308' : sev === 'completed' ? '#65a30d' : '#a16207';
          const sevLabel = sev.charAt(0).toUpperCase() + sev.slice(1);
          const pcMap: Record<string, string> = { S: 'Subsidence', GO: 'Gob Pile', DS: 'Dangerous Slide', DPE: 'Dangerous Pile/Embankment', SB: 'Surface Burning', UMF: 'Underground Mine Fire', SA: 'Spoil Area', IRW: 'Industrial/Residential Waste' };
          const probLabel = p.problem_code ? (pcMap[p.problem_code] || p.problem_code) : 'Mine Problem Feature';
          let html = `<strong>${esc(probLabel)}</strong>`;
          if (p.pad_name) html += `<br/><span style="color:#94a3b8">${esc(p.pad_name)}</span>`;
          html += `<br/><span style="color:${sevColor};font-weight:600">${sevLabel} severity</span>`;
          if (p.area_acres) html += `<br/><span style="font-size:10px;color:#888">${Number(p.area_acres).toFixed(1)} ac · WVDEP AML</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'aml-sites-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'aml-sites-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectAmlSite?.({
            id: p.id,
            pad_name: p.pad_name || null,
            pad_number: p.pad_number || null,
            problem_code: p.problem_code || null,
            severity: p.severity || null,
            status: p.status || null,
            area_acres: p.area_acres != null ? Number(p.area_acres) : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Urban Areas popups
      m.on('mouseenter', 'urban-areas-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          let html = `<strong>${esc(p.name || 'Urban Area')}</strong>`;
          html += `<br/><span style="color:#6366f1;font-weight:600">${esc(p.urban_type || 'Urban')}</span>`;
          if (p.area_sq_miles) html += `<br/>${Number(p.area_sq_miles).toLocaleString()} sq mi`;
          html += `<br/><span style="font-size:10px;color:#6366f1">Community opposition risk zone</span>`;
          popup.setLngLat(e.lngLat).setHTML(html).addTo(m);
        }
      });
      m.on('mouseleave', 'urban-areas-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'urban-areas-fill', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectUrbanArea?.({
            id: p.id,
            name: p.name || null,
            urban_type: p.urban_type || null,
            area_sq_miles: p.area_sq_miles != null ? Number(p.area_sq_miles) : null,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Airport hover
      m.on('mouseenter', 'airport-points', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : (p.metadata || {});
          const faaId = meta.arpt_id ? ` (${esc(meta.arpt_id)})` : '';
          const classBadge = meta.airport_class === 'commercial' ? ' <span style="background:#ef444420;color:#ef4444;padding:1px 5px;border-radius:3px;font-size:9px;font-weight:600">COMMERCIAL</span>' : '';
          const city = meta.city ? `<br/>${esc(meta.city)}, ${esc(p.status === 'O' ? p.operator : '')}`.replace(/, $/, '') : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Airport')}${faaId}</strong><br/><span style="color:#ef4444;font-weight:600">Airport</span>${classBadge}${city}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'airport-points', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });
      m.on('click', 'airport-points', (e) => {
        if (measurementActiveRef.current) return;
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          onSelectAirport?.({
            id: p.id,
            name: p.name || null,
            operator: p.operator || null,
            county: p.county || null,
            state: p.state || null,
            status: p.status || null,
            metadata: typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata,
          });
          e.originalEvent.stopPropagation();
        }
      });

      // Fiber coverage popups
      m.on('mouseenter', 'fiber-hexagons', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const providers: Array<{ name: string; download: number; brc?: string }> =
            typeof p.providers === 'string' ? JSON.parse(p.providers) : (p.providers || []);
          const formatSpeed = (mbps: number) => mbps >= 1000 ? `${(mbps / 1000).toFixed(mbps % 1000 === 0 ? 0 : 1)} Gbps` : `${mbps} Mbps`;
          // Surface the strongest provider at the top: fastest brc='B' business-class wins;
          // fallback to fastest overall when no 'B' exists. Tighter than BDC's 'has_enterprise'
          // flag, which also counts brc='X' (both-types) as enterprise.
          const bySpeed = (a: typeof providers[0], b: typeof providers[0]) => Number(b.download || 0) - Number(a.download || 0);
          const enterprise = providers.filter(pr => pr.brc === 'B').sort(bySpeed);
          const nonEnterprise = providers.filter(pr => pr.brc !== 'B').sort(bySpeed);
          const ordered = [...enterprise, ...nonEnterprise];
          const hasBusinessClass = enterprise.length > 0;
          const best = ordered[0];
          const rest = ordered.slice(1);
          const brcFor = (brc?: string) => brc === 'B' ? ' (business)' : brc === 'X' ? ' (biz+res)' : brc === 'R' ? ' (residential)' : '';
          const bestLine = best
            ? `<strong style="color:#0e7490">${esc(best.name)}${brcFor(best.brc)}</strong> — <strong>${formatSpeed(best.download)}</strong>`
            : '';
          const restLines = rest.map(pr => `${esc(pr.name)}${brcFor(pr.brc)} — ${formatSpeed(pr.download)}`).join('<br/>');
          const typeLabel = hasBusinessClass
            ? '<span style="color:#0e7490;font-weight:600">Enterprise fiber</span>'
            : '<span style="color:#0891b2;font-weight:600">Residential fiber</span>';
          const isolatedNote = p.is_isolated ? '<br/><span style="color:#d97706;font-size:11px">Isolated coverage point</span>' : '';
          popup.setLngLat(e.lngLat)
            .setHTML(
              `<strong>Fiber Coverage</strong><br/>` +
              `${typeLabel} · ${esc(p.provider_count)} provider${p.provider_count !== 1 ? 's' : ''}<br/>` +
              `${bestLine}${restLines ? `<br/><span style="color:#6b7280;font-size:11px">${restLines}</span>` : ''}` +
              `${isolatedNote}`
            )
            .addTo(m);
        }
      });
      m.on('mouseleave', 'fiber-hexagons', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Water wells popups
      m.on('mouseenter', 'water-wells', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const depth = p.well_depth_ft ? `<br/>Depth: ${esc(p.well_depth_ft)} ft` : '';
          const aquifer = p.aquifer_name ? `<br/>Aquifer: ${esc(p.aquifer_name)}` : '';
          const use = p.water_use ? `<br/>Use: ${esc(p.water_use)}` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Water Well')}</strong><br/><span style="color:#06b6d4;font-weight:600">Groundwater Well</span>${depth}${aquifer}${use}${p.county ? '<br/>' + esc(p.county) + ' County' : ''}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'water-wells', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Water service area popups
      m.on('mouseenter', 'water-service-areas', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const pop = p.population_served ? `<br/>Pop. served: ${Number(p.population_served).toLocaleString()}` : '';
          const src = p.source_water_type ? `<br/>Source: ${esc(p.source_water_type)}` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name || 'Water Service Area')}</strong><br/><span style="color:#0ea5e9;font-weight:600">Water Service Area</span>${pop}${src}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'water-service-areas', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Aquifer boundary popups
      m.on('mouseenter', 'aquifer-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const typeLabel = p.aquifer_type === 'major' ? 'Major Aquifer' : 'Minor Aquifer';
          const area = p.area_sq_miles ? `<br/>~${Number(p.area_sq_miles).toLocaleString()} sq mi` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>${esc(p.name)}</strong><br/><span style="color:#0891b2;font-weight:600">${typeLabel}</span>${area}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'aquifer-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Electric service territory hover + click (all three fill layers)
      const etLayers = ['electric-territories-fill', 'electric-territories-coop-fill', 'electric-territories-muni-fill'];
      for (const layerId of etLayers) {
        m.on('mousemove', layerId, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            const color = ET_TYPE_COLORS[p.company_type] || '#6b7280';
            const iso = p.iso_rto ? ` · <span style="font-weight:600">${esc(p.iso_rto)}</span>` : '';
            popup.setLngLat(e.lngLat)
              .setHTML(`<strong>${esc(p.company_name)}</strong><br/><span style="color:${color};font-weight:600">${esc(p.company_type)}</span>${iso}`)
              .addTo(m);
          }
        });
        m.on('mouseleave', layerId, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
        m.on('click', layerId, (e) => {
          if (measurementActiveRef.current) return;
          if (e.features?.[0]?.properties) {
            const p = e.features[0].properties;
            onSelectElectricTerritory?.({
              id: p.id,
              company_name: p.company_name || '',
              company_abbreviation: p.company_abbreviation || null,
              company_type: p.company_type || '',
              iso_rto: p.iso_rto || null,
              main_phone: p.main_phone || null,
              outage_phone: p.outage_phone || null,
              outage_map_url: p.outage_map_url || null,
              website: p.website || null,
              gt_cooperative: p.gt_cooperative || null,
            });
            e.originalEvent.stopPropagation();
          }
        });
      }

      // Federal lands hover popup (mousemove so it tracks cursor across large polygons)
      m.on('mousemove', 'federal-lands-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const categoryColors: Record<string, string> = {
            'BLM': '#b45309', 'USFS': '#166534', 'NPS': '#92400e',
            'State': '#7c3aed', 'Private': '#059669', 'Other Federal': '#dc2626',
          };
          const cat = p.category || 'Other Federal';
          const color = categoryColors[cat] || '#6b7280';
          const acres = p.acreage ? `<br/>${Number(p.acreage).toLocaleString()} acres` : '';
          const st = p.state ? ` (${esc(p.state)})` : '';
          const heading = p.name ? esc(p.name) : esc(cat);
          const sub = p.name ? `<br/>${esc(cat)} · ${esc(p.managing_agency)}${st}` : `<br/>Agency: ${esc(p.managing_agency)}${st}`;
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:${color}">${heading}</strong>${sub}${acres}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'federal-lands-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Crown Pine LRM tract hover — tract name / legal acres / fund / group
      m.on('mousemove', 'crownpine-tracts-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        const p = e.features?.[0]?.properties;
        if (!p) return;
        const fund = String(p.fund || '').split('-')[0] || '—';
        const color = fund === 'CPT5' ? '#d97706' : '#9a3412';
        const acres = p.legal_acres != null && p.legal_acres !== '' ? `${Number(p.legal_acres).toLocaleString()} ac` : '— ac';
        const group = p.tract_group && p.tract_group !== 'Crown Pine' ? ` · ${esc(p.tract_group)}` : '';
        popup.setLngLat(e.lngLat)
          .setHTML(`<strong style="color:${color}">Crown Pine tract ${esc(p.tract_name)}</strong><br/>${acres} · ${esc(fund)}${group}`)
          .addTo(m);
      });
      m.on('mouseleave', 'crownpine-tracts-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Assembly groups hover popup
      m.on('mousemove', 'assembly-groups-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const acres = '~' + Number(p.total_acreage).toLocaleString();
          const tier = p.total_acreage >= 10000 ? '#1d4ed8' : p.total_acreage >= 2500 ? '#3b82f6' : '#60a5fa';
          const countiesStr = typeof p.counties === 'string' ? JSON.parse(p.counties).join(', ') : (Array.isArray(p.counties) ? p.counties.join(', ') : '');
          const counties = countiesStr ? `<br/>${countiesStr}` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:${tier}">${esc(p.owner_name)}</strong><br/>${acres} acres &middot; ${p.parcel_count} parcels${counties}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'assembly-groups-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // State trust lands hover popup
      m.on('mousemove', 'state-trust-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const acres = p.acreage ? `<br/>${Number(p.acreage).toLocaleString()} acres` : '';
          const lessee = p.lessee ? `<br/>Lessee: ${esc(p.lessee)}` : '';
          const county = p.county ? ` (${esc(p.county)})` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:#16a34a">${esc(p.managing_agency)}</strong> — ${esc(p.state)}${county}${acres}${lessee}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'state-trust-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Protected areas hover popup
      m.on('mousemove', 'protected-areas-fill', (e) => {
        m.getCanvas().style.cursor = 'pointer';
        if (e.features?.[0]?.properties) {
          const p = e.features[0].properties;
          const typeLabels: Record<string, string> = {
            'MIL': 'Military', 'TRIBL': 'Tribal', 'NM': 'Nat\'l Monument',
            'NP': 'Nat\'l Park', 'WILD': 'Wilderness', 'NWR': 'Wildlife Refuge', 'WSA': 'Wilderness Study',
          };
          const typeColors: Record<string, string> = {
            'MIL': '#dc2626', 'TRIBL': '#ea580c', 'NM': '#16a34a',
            'NP': '#16a34a', 'WILD': '#15803d', 'NWR': '#15803d', 'WSA': '#15803d',
          };
          const dt = p.designation_type || '';
          const label = typeLabels[dt] || dt;
          const color = typeColors[dt] || '#6b7280';
          const acres = p.gis_acres ? `<br/>${Number(p.gis_acres).toLocaleString()} acres` : '';
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong style="color:${color}">${esc(label)}</strong><br/>${esc(p.name)}${acres}`)
            .addTo(m);
        }
      });
      m.on('mouseleave', 'protected-areas-fill', () => {
        m.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Measurement: map-level click handler for placing waypoints
      m.on('click', (e) => {
        if (!distanceActiveRef.current) return;
        const pts = mCurrentPointsRef.current;
        const newPt: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        if (pts.length > 0) {
          measurementSegmentTotalRef.current += turfDistance(pts[pts.length - 1], newPt, { units: 'miles' });
        }
        mHandleClick(e.lngLat);
        updateMeasurementSources(m);
      });

      // Measurement: double-click to finish (zoom disabled in useEffect)
      m.on('dblclick', (e) => {
        if (!distanceActiveRef.current) return;
        e.preventDefault();
        mFinish();
        measurementSegmentTotalRef.current = 0;
        updateMeasurementSources(m);
        if (measurementCursorMarkerRef.current) {
          measurementCursorMarkerRef.current.remove();
          measurementCursorMarkerRef.current = null;
        }
      });

      // Measurement: rubber-band preview line + live distance label
      m.on('mousemove', (e) => {
        if (!distanceActiveRef.current || mCurrentPointsRef.current.length === 0) return;
        const pts = mCurrentPointsRef.current;
        const cursor: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const previewSrc = m.getSource('measurement-preview') as mapboxgl.GeoJSONSource;
        if (previewSrc) {
          previewSrc.setData(mGetPreview(cursor));
        }

        // Cached segment total + cursor-to-last-point distance
        const total = measurementSegmentTotalRef.current + turfDistance(pts[pts.length - 1], cursor, { units: 'miles' });

        // Update or create the cursor-following distance label
        if (!measurementCursorMarkerRef.current) {
          const el = document.createElement('div');
          el.style.cssText = `
            font-family: ${designTokens.typography.fontFamily.mono};
            font-size: ${designTokens.typography.fontSize.sm};
            font-weight: ${designTokens.typography.fontWeight.medium};
            color: ${designTokens.colors.measurementLabel};
            background: rgba(58, 70, 80, 0.92);
            padding: 2px 6px;
            border-radius: ${designTokens.borderRadius.sm};
            border: 1px solid ${designTokens.colors.measurement};
            pointer-events: none;
            white-space: nowrap;
          `;
          measurementCursorMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'left', offset: [12, 0] })
            .setLngLat(cursor)
            .addTo(m);
        }
        measurementCursorMarkerRef.current.setLngLat(cursor);
        measurementCursorMarkerRef.current.getElement().textContent = formatDistance(total);
      });

      // Track zoom level for UI hint
      m.on('zoom', () => setZoomLevel(m.getZoom()));

      // Right-click to save a pin at the cursor location
      m.on('contextmenu', (e) => {
        e.preventDefault();
        const { lng, lat } = e.lngLat;

        // Dismiss any previously open save popup before creating a new one.
        activeSavePopupCleanupRef.current?.();

        const container = document.createElement('div');
        let root: Root | null = null;

        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          maxWidth: '280px',
        })
          .setLngLat([lng, lat])
          .setDOMContent(container)
          .addTo(m);

        const cleanup = () => {
          if (activeSavePopupCleanupRef.current === cleanup) {
            activeSavePopupCleanupRef.current = null;
          }
          popup.remove();
          root?.unmount();
        };

        activeSavePopupCleanupRef.current = cleanup;

        root = createRoot(container);
        root.render(
          <SavePinPopup
            lat={lat}
            lng={lng}
            onCancel={cleanup}
            onSave={async (name, note) => {
              await onSaveLocation({ name, lat, lng, note });
              cleanup();
            }}
          />
        );
      });

      // Click on a saved-location pin: fly to it and open the linked detail panel if any
      m.on('click', 'saved-locations-circles', (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const props = feature.properties as { source_type: string | null; source_id: string | null };
        const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;

        m.flyTo({ center: [lng, lat], zoom: 14 });

        if (props.source_type === 'parcel' && props.source_id) {
          onSelectParcel(props.source_id);
        } else if (props.source_type === 'well' && props.source_id) {
          onSelectWell?.(Number(props.source_id));
        }
      });

      m.on('mouseenter', 'saved-locations-circles', () => {
        m.getCanvas().style.cursor = 'pointer';
      });
      m.on('mouseleave', 'saved-locations-circles', () => {
        m.getCanvas().style.cursor = '';
      });

      initialMapLoadCompleteRef.current = true;
      setMapLoaded(true);
    });

    map.current = m;
    return () => {
      activeSavePopupCleanupRef.current?.();
      m.remove();
      map.current = null;
      initialMapLoadCompleteRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch base style between dark/light/satellite
  // Cleanup removes stale once('style.load') handler on rapid style switching
  // (Mapbox GL JS does not clear once() handlers when setStyle() is called)
  useEffect(() => {
    if (!map.current || !initialMapLoadCompleteRef.current) return;
    const m = map.current;
    const style = isSatellite
      ? 'mapbox://styles/mapbox/satellite-streets-v12'
      : isDark
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11';

    const handler = () => {
      addSourcesAndLayersRef.current?.();
      // Our sources are back — tell the measurement hook to re-add its area
      // layers on top of them.
      setStyleEpoch(e => e + 1);
      setMapLoaded(true);
      // Toggle effects re-run when mapLoaded transitions true — they handle data reloads
    };
    setMapLoaded(false);
    m.once('style.load', handler);
    // Rebuild custom sources through the full style-load lifecycle.
    m.setStyle(style, { diff: false, localFontFamily: null, localIdeographFontFamily: 'sans-serif' });
    return () => { m.off('style.load', handler); };
  }, [isSatellite, isDark]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3D terrain: drape the basemap over the DEM already loaded for hillshade.
  // setStyle() clears terrain, so this re-applies when mapLoaded flips back true
  // after a basemap/theme switch re-adds the source.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapLoaded || !m.getSource('mapbox-dem')) return;
    m.setTerrain(show3DTerrain ? { source: 'mapbox-dem', exaggeration: terrainExaggeration } : null);
    // Reset pitch only when the mode changes; retain camera adjustments on style reload.
    if (appliedTerrainModeRef.current !== show3DTerrain) {
      if (show3DTerrain || appliedTerrainModeRef.current !== null) {
        m.easeTo(show3DTerrain ? { pitch: 60, duration: 600 } : { pitch: 0, bearing: 0, duration: 600 });
      }
      appliedTerrainModeRef.current = show3DTerrain;
    }
  }, [show3DTerrain, terrainExaggeration, mapLoaded]);

  // Slope drapes (10 m regional + 1 m per-site): toggle + shared opacity
  useEffect(() => {
    const m = map.current;
    if (!m || !mapLoaded || !m.getLayer('etx-slope')) return;
    m.setLayoutProperty('etx-slope', 'visibility', showSlopeDrape ? 'visible' : 'none');
    m.setPaintProperty('etx-slope', 'raster-opacity', slopeDrapeOpacity);
    if (m.getLayer('site-slope-1m')) {
      m.setLayoutProperty('site-slope-1m', 'visibility', showSlope1m ? 'visible' : 'none');
      m.setPaintProperty('site-slope-1m', 'raster-opacity', slopeDrapeOpacity);
    }
  }, [showSlopeDrape, showSlope1m, slopeDrapeOpacity, mapLoaded]);

  // Inject popup theme CSS
  useEffect(() => {
    const id = 'mapview-popup-theme';
    let style = document.getElementById(id) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = id;
      document.head.appendChild(style);
    }
    const bg = isDark ? '#3A4650' : '#D4D8DC';
    const text = isDark ? '#E4E6E8' : '#2E3840';
    const border = isDark ? 'rgba(80,98,114,0.20)' : 'rgba(62,78,92,0.15)';
    style.textContent = `
      .mapboxgl-popup-content { background:${bg}; color:${text}; border-radius:6px; padding:10px 14px; box-shadow:0 4px 12px rgba(0,0,0,0.3); border:1px solid ${border}; }
      .mapboxgl-popup-tip { border-top-color:${bg}; border-bottom-color:${bg}; }
      .mapboxgl-popup-anchor-left .mapboxgl-popup-tip { border-right-color:${bg}; }
      .mapboxgl-popup-anchor-right .mapboxgl-popup-tip { border-left-color:${bg}; }
    `;
    return () => { style?.remove(); };
  }, [isDark]);

  // Get current map bounding box as "west,south,east,north"
  const getBbox = useCallback(() => {
    if (!map.current) return undefined;
    const bounds = map.current.getBounds();
    return `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
  }, []);

  // County zoom from Counties tab
  useEffect(() => {
    if (!pendingCountyZoom || !map.current || !mapLoaded) return;
    const [west, south, east, north] = pendingCountyZoom;
    map.current.fitBounds([[west, south], [east, north]], { padding: 40, duration: 1200 });
    onCountyZoomConsumed?.();
  }, [pendingCountyZoom, mapLoaded, onCountyZoomConsumed]);

  // Fit to an extent supplied by the Operators panel ("zoom to" the focused operator). Never below
  // PIPELINE_MIN_ZOOM: a coast-to-coast company (Kinder Morgan, Energy Transfer) fits at ~z3, where no line draws.
  useEffect(() => {
    if (!pendingFitBounds || !map.current || !mapLoaded) return;
    const [west, south, east, north] = pendingFitBounds;
    const camera = map.current.cameraForBounds([[west, south], [east, north]], { padding: 40 });
    if (camera) map.current.flyTo({ ...camera, zoom: Math.max(camera.zoom ?? PIPELINE_MIN_ZOOM, PIPELINE_MIN_ZOOM), padding: 40, duration: 1200 });
    onFitBoundsConsumed?.();
  }, [pendingFitBounds, mapLoaded, onFitBoundsConsumed]);

  // Fly to the location supplied by a detail panel.
  useEffect(() => {
    if (!pendingFlyTo || !map.current || !mapLoaded) return;
    const [lng, lat] = pendingFlyTo;
    map.current.flyTo({ center: [lng, lat], zoom: 13, duration: 1200, ...(show3DTerrain ? { pitch: 60 } : {}) });
    onFlyToConsumed?.();
  }, [pendingFlyTo, mapLoaded, onFlyToConsumed, show3DTerrain]);

  // Saved locations: sync GeoJSON source with props
  useEffect(() => {
    const m = map.current;
    if (!m || !m.getSource('saved-locations')) return;
    const features = savedLocations.map(loc => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [loc.lng, loc.lat] },
      properties: {
        id: loc.id,
        name: loc.name,
        source_type: loc.source_type,
        source_id: loc.source_id,
      },
    }));
    (m.getSource('saved-locations') as mapboxgl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features,
    });
  }, [savedLocations, mapLoaded]);

  // Saved locations: toggle layer visibility
  useEffect(() => {
    const m = map.current;
    if (!m || !m.getLayer('saved-locations-circles')) return;
    const vis = showSavedLocations ? 'visible' : 'none';
    m.setLayoutProperty('saved-locations-circles', 'visibility', vis);
    if (m.getLayer('saved-locations-labels')) {
      m.setLayoutProperty('saved-locations-labels', 'visibility', vis);
    }
  }, [showSavedLocations, mapLoaded]);

  // County lines: load data + toggle visibility
  useEffect(() => {
    const m = map.current;
    if (!m || !mapLoaded || !m.getLayer('county-lines')) return;
    if (showCountyLines) {
      m.setLayoutProperty('county-lines', 'visibility', 'visible');
      m.setLayoutProperty('county-labels', 'visibility', 'visible');
      const src = m.getSource('county-boundaries') as mapboxgl.GeoJSONSource;
      const centroidSrc = m.getSource('county-centroids') as mapboxgl.GeoJSONSource;
      if (countyBoundaryCache) {
        src?.setData(countyBoundaryCache.boundaries);
        centroidSrc?.setData(countyBoundaryCache.centroids);
        return;
      }
      fetch('/data/county-boundaries.geojson')
        .then(r => r.json())
        .then((boundaries: GeoJSON.FeatureCollection) => {
          const centroids: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: boundaries.features.map(f => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: polyBboxCenter(f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon) },
              properties: f.properties,
            })),
          };
          countyBoundaryCache = { boundaries, centroids };
          src?.setData(boundaries);
          centroidSrc?.setData(centroids);
        })
        .catch(() => {});
    } else {
      m.setLayoutProperty('county-lines', 'visibility', 'none');
      m.setLayoutProperty('county-labels', 'visibility', 'none');
    }
  }, [showCountyLines, mapLoaded]);

  // Saved locations: fly to from sidebar click
  useEffect(() => {
    if (!pendingSavedFlyTo) return;
    const m = map.current;
    if (!m) return;

    m.flyTo({ center: [pendingSavedFlyTo.lng, pendingSavedFlyTo.lat], zoom: 14 });

    if (pendingSavedFlyTo.source_type === 'parcel' && pendingSavedFlyTo.source_id) {
      onSelectParcel(pendingSavedFlyTo.source_id);
    } else if (pendingSavedFlyTo.source_type === 'well' && pendingSavedFlyTo.source_id) {
      onSelectWell?.(Number(pendingSavedFlyTo.source_id));
    }

    onSavedFlyToHandled();
  }, [pendingSavedFlyTo, onSavedFlyToHandled, onSelectParcel, onSelectWell]);

  // Search pin marker
  useEffect(() => {
    if (searchPinMarkerRef.current) {
      searchPinMarkerRef.current.remove();
      searchPinMarkerRef.current = null;
    }
    if (!searchPin || !map.current || !mapLoaded) return;
    const [lng, lat] = searchPin;
    const el = document.createElement('div');
    el.style.cssText = 'width:16px;height:16px;position:relative;';
    el.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0">
      <circle cx="8" cy="8" r="7" stroke="#C4705A" stroke-width="1.5" fill="none" opacity="0.9"/>
      <circle cx="8" cy="8" r="2" fill="#C4705A"/>
    </svg>
    <div style="position:absolute;inset:-8px;border-radius:50%;border:1.5px solid #C4705A;animation:search-ping 1.4s ease-out forwards;pointer-events:none"></div>
    <style>@keyframes search-ping{0%{transform:scale(0.5);opacity:0.8}100%{transform:scale(2.5);opacity:0}}</style>`;
    el.style.cursor = 'pointer';
    el.title = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    el.addEventListener('click', () => { onSearchPinClearRef.current?.(); });
    searchPinMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([lng, lat])
      .addTo(map.current);
    return () => {
      searchPinMarkerRef.current?.remove();
      searchPinMarkerRef.current = null;
    };
  }, [searchPin, mapLoaded]);

  // Re-set parcel tile URL when min_acreage changes
  // Re-runs on mapLoaded to reapply after style switch destroys sources/layers
  useEffect(() => {
    const m = map.current;
    if (!m || !mapLoaded || !m.isStyleLoaded()) return;
    const source = m.getSource('parcels') as mapboxgl.VectorTileSource;
    if (source) {
      source.setTiles([getParcelTileUrl(filters.min_acreage)]);
    }
  }, [filters.min_acreage, mapLoaded]);

  // Land listing layer — corridor profile (hook manages load, toggle, and abort)
  const fetchListings = useCallback(
    (bbox: string, signal: AbortSignal) => fetchLandListingsGeoJSON(bbox, signal),
    [],
  );
  const { load: loadLandListings } = useListingLayer(
    map, mapLoaded, 'land-listings', fetchListings, getBbox,
    showLandListings, onListingsLoadingChange,
  );

  // Grid listing layer — 10-30 acre grid+fiber sites
  const fetchGridListing = useCallback(
    (bbox: string, signal: AbortSignal) => fetchGridListingsGeoJSON(bbox, signal),
    [],
  );
  const { load: loadGridListings } = useListingLayer(
    map, mapLoaded, 'grid-listings', fetchGridListing, getBbox,
    showGridListings, onGridListingsLoadingChange,
  );

  const loadPipelineGroup = useCallback((
    group: PipelineGroup, sourceId: string, show: boolean,
  ): void => {
    if (!map.current || !mapLoaded) return;
    const src = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
    if (!src) return;

    if (!show) {
      pipelineAbortRefs.current[group]?.abort();
      pipelineAbortRefs.current[group] = null;
      src.setData(EMPTY_FC);
      delete pipelineOpCountsRef.current[group];
      emitOperators();
      pipelineLoadingRef.current = { ...pipelineLoadingRef.current, [group]: false };
      onPipelineLoadingChange?.({ ...pipelineLoadingRef.current });
      return;
    }

    pipelineAbortRefs.current[group]?.abort();
    const controller = new AbortController();
    pipelineAbortRefs.current[group] = controller;
    pipelineLoadingRef.current = { ...pipelineLoadingRef.current, [group]: true };
    onPipelineLoadingChange?.({ ...pipelineLoadingRef.current });

    const currentZoom = map.current?.getZoom();
    fetchPipelinesGeoJSON(group, group === 'desert_sw' ? undefined : getBbox(), controller.signal, currentZoom, pipelineFilters).then(data => {
      if (controller.signal.aborted || !map.current) return;
      const s = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
      if (s) s.setData(data);
      const counts: Record<string, number> = {};
      data.features.forEach(f => {
        const op = (f.properties?.operator as string) || 'Unknown';
        counts[op] = (counts[op] || 0) + 1;
      });
      pipelineOpCountsRef.current[group] = counts;
      emitOperators();
    }).catch(err => {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error(`Failed to load ${group} pipelines:`, err);
    }).finally(() => {
      if (pipelineAbortRefs.current[group] === controller) {
        pipelineLoadingRef.current = { ...pipelineLoadingRef.current, [group]: false };
        onPipelineLoadingChange?.({ ...pipelineLoadingRef.current });
      }
    });
  }, [mapLoaded, emitOperators, onPipelineLoadingChange, getBbox, pipelineFilters]);

  // Pipeline group effects — one per group, using the shared helper
  useEffect(() => { loadPipelineGroup('gathering', 'pipelines-gathering', showGathering); return () => pipelineAbortRefs.current['gathering']?.abort(); }, [showGathering, loadPipelineGroup]);
  useEffect(() => { loadPipelineGroup('gas_transmission', 'pipelines-gas-transmission', showGasTransmission); return () => pipelineAbortRefs.current['gas_transmission']?.abort(); }, [showGasTransmission, loadPipelineGroup]);
  useEffect(() => { loadPipelineGroup('liquids_transmission', 'pipelines-liquids-transmission', showLiquidsTransmission); return () => pipelineAbortRefs.current['liquids_transmission']?.abort(); }, [showLiquidsTransmission, loadPipelineGroup]);
  // Desert Southwest — the former corridor-thesis highlight, now an ordinary toggle (off by default)
  useEffect(() => { loadPipelineGroup('desert_sw', 'pipelines-desert-sw', showDesertSw); return () => pipelineAbortRefs.current['desert_sw']?.abort(); }, [showDesertSw, loadPipelineGroup]);

  const loadElectricTerritories = useCallback(async () => {
    if (!map.current || !mapLoaded || !showElectricTerritories) return;

    if (electricTerritoriesAbortRef.current) electricTerritoriesAbortRef.current.abort();
    const controller = new AbortController();
    electricTerritoriesAbortRef.current = controller;

    try {
      const data = await fetchElectricTerritoriesGeoJSON(undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('electric-territories') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load electric territories:', err);
    }
  }, [mapLoaded, showElectricTerritories]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showElectricTerritories) {
      loadElectricTerritories();
    } else {
      const src = map.current.getSource('electric-territories') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => electricTerritoriesAbortRef.current?.abort();
  }, [showElectricTerritories, mapLoaded, loadElectricTerritories]);

  const loadFederalLands = useCallback(async () => {
    if (!map.current || !mapLoaded || !showFederalLands) return;

    if (federalLandsAbortRef.current) federalLandsAbortRef.current.abort();
    const controller = new AbortController();
    federalLandsAbortRef.current = controller;

    try {
      const data = await fetchFederalLandsGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('federal-lands') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load federal lands:', err);
    }
  }, [mapLoaded, showFederalLands, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showFederalLands) {
      loadFederalLands();
    } else {
      const src = map.current.getSource('federal-lands') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => federalLandsAbortRef.current?.abort();
  }, [showFederalLands, mapLoaded, loadFederalLands]);

  const loadCrownPineTracts = useCallback(async () => {
    if (!map.current || !mapLoaded || !showCrownPineTracts) return;

    if (crownPineTractsAbortRef.current) crownPineTractsAbortRef.current.abort();
    const controller = new AbortController();
    crownPineTractsAbortRef.current = controller;

    try {
      const data = await fetchCrownPineTractsGeoJSON(getBbox(), map.current.getZoom(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('crownpine-tracts') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load Crown Pine tracts:', err);
    }
  }, [mapLoaded, showCrownPineTracts, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showCrownPineTracts) {
      loadCrownPineTracts();
    } else {
      const src = map.current.getSource('crownpine-tracts') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => crownPineTractsAbortRef.current?.abort();
  }, [showCrownPineTracts, mapLoaded, loadCrownPineTracts]);

  // Load assembly groups when toggled on
  const loadAssemblyGroups = useCallback(async () => {
    if (!map.current || !mapLoaded || !showAssemblyGroups) return;

    if (assemblyGroupsAbortRef.current) assemblyGroupsAbortRef.current.abort();
    const controller = new AbortController();
    assemblyGroupsAbortRef.current = controller;

    try {
      const data = await fetchAssemblyGroupsGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('assembly-groups') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load assembly groups:', err);
    }
  }, [mapLoaded, showAssemblyGroups, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showAssemblyGroups) {
      loadAssemblyGroups();
    } else {
      const src = map.current.getSource('assembly-groups') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => assemblyGroupsAbortRef.current?.abort();
  }, [showAssemblyGroups, mapLoaded, loadAssemblyGroups]);

  // Load state trust lands when toggled on
  const loadStateTrustLands = useCallback(async () => {
    if (!map.current || !mapLoaded || !showStateTrustLands) return;

    if (trustLandAbortRef.current) trustLandAbortRef.current.abort();
    const controller = new AbortController();
    trustLandAbortRef.current = controller;
    onTrustLandLoadingChange?.(true);

    try {
      const data = await fetchStateTrustLandsGeoJSON(getBbox(), undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('state-trust-lands') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load state trust lands:', err);
    } finally {
      if (trustLandAbortRef.current === controller) {
        onTrustLandLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showStateTrustLands, getBbox, onTrustLandLoadingChange]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showStateTrustLands) {
      loadStateTrustLands();
    } else {
      const src = map.current.getSource('state-trust-lands') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => trustLandAbortRef.current?.abort();
  }, [showStateTrustLands, mapLoaded, loadStateTrustLands]);

  // Load protected areas when toggled on
  const loadProtectedAreas = useCallback(async () => {
    if (!map.current || !mapLoaded || !showProtectedAreas) return;

    if (protectedAreasAbortRef.current) protectedAreasAbortRef.current.abort();
    const controller = new AbortController();
    protectedAreasAbortRef.current = controller;

    try {
      const data = await fetchProtectedAreasGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('protected-areas') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load protected areas:', err);
    }
  }, [mapLoaded, showProtectedAreas, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showProtectedAreas) {
      loadProtectedAreas();
    } else {
      const src = map.current.getSource('protected-areas') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => protectedAreasAbortRef.current?.abort();
  }, [showProtectedAreas, mapLoaded, loadProtectedAreas]);

  // Operator AOIs (partner-deck supply zones). Tiny, viewport-independent
  // dataset — fetch once on first toggle-on, then just flip layer visibility.
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const layerIds = [
      'operator-aois-buffer-halo',
      'operator-aois-phase-fill',
      'operator-aois-phase-line',
      'operator-aois-outer-line',
    ];
    const vis = showOperatorAois ? 'visible' : 'none';
    layerIds.forEach(id => {
      if (map.current!.getLayer(id)) map.current!.setLayoutProperty(id, 'visibility', vis);
    });

    if (showOperatorAois && !operatorAoisLoadedRef.current) {
      operatorAoisAbortRef.current?.abort();
      const controller = new AbortController();
      operatorAoisAbortRef.current = controller;
      fetchOperatorAoisGeoJSON(undefined, controller.signal)
        .then(data => {
          if (controller.signal.aborted || !map.current) return;
          const src = map.current.getSource('operator-aois') as mapboxgl.GeoJSONSource | undefined;
          if (src) {
            src.setData(data);
            operatorAoisLoadedRef.current = true;
            // Pan/zoom to the AOI on first load — at the default state-wide zoom
            // the polygons cover a fraction of the viewport and read as invisible
            // against the parcel tints underneath.
            const coords: [number, number][] = [];
            for (const f of data.features ?? []) {
              const geom = f.geometry as GeoJSON.Geometry | undefined;
              if (!geom) continue;
              if (geom.type === 'Polygon') {
                for (const ring of geom.coordinates) coords.push(...(ring as [number, number][]));
              } else if (geom.type === 'MultiPolygon') {
                for (const poly of geom.coordinates) for (const ring of poly) coords.push(...(ring as [number, number][]));
              }
            }
            if (coords.length > 0) {
              const bounds = coords.reduce(
                (b, c) => b.extend(c),
                new mapboxgl.LngLatBounds(coords[0], coords[0])
              );
              map.current.fitBounds(bounds, { padding: 60, duration: 800 });
            }
          }
        })
        .catch(err => {
          if (err instanceof Error && err.name === 'CanceledError') return;
          console.error('Failed to load operator AOIs:', err);
        });
    }

    return () => operatorAoisAbortRef.current?.abort();
  }, [showOperatorAois, mapLoaded]);

  // Load power infrastructure and/or grid capacity — shared fetch when both are on
  const loadPowerData = useCallback(async () => {
    if (!map.current || !mapLoaded) return;
    if (!showPowerInfra && !showGridCapacity) return;

    if (powerAbortRef.current) powerAbortRef.current.abort();
    const controller = new AbortController();
    powerAbortRef.current = controller;
    if (showPowerInfra) onPowerLoadingChange?.(true);
    if (showGridCapacity) onGridCapacityLoadingChange?.(true);

    try {
      // include=overlay merges the curated grid_overlay (Revenant AOI names,
      // operators, corrected identities) over the HIFLD baseline. Additive:
      // outside the AOI the response is unchanged.
      const data = await fetchPowerGeoJSON(undefined, getBbox(), controller.signal, undefined, true);
      if (controller.signal.aborted || !map.current) return;

      if (showPowerInfra) {
        const src = map.current.getSource('power-infra') as mapboxgl.GeoJSONSource;
        if (src) src.setData(data);
      }
      if (showGridCapacity) {
        const filtered = {
          ...data,
          features: data.features.filter((f: any) => f.properties?.capacity_tier),
        };
        const src = map.current.getSource('grid-capacity') as mapboxgl.GeoJSONSource;
        if (src) src.setData(filtered);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load power data:', err);
    } finally {
      if (powerAbortRef.current === controller) {
        onPowerLoadingChange?.(false);
        onGridCapacityLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showPowerInfra, showGridCapacity, getBbox, onPowerLoadingChange, onGridCapacityLoadingChange]);

  // Aliases for backward-compatible references in toggle effects and moveend
  const loadPowerInfra = loadPowerData;
  const loadGridCapacity = loadPowerData;

  // Load OSM switchyards — separate source/layer; optional hifld-new-only filter
  const loadOsmSwitchyards = useCallback(async () => {
    if (!map.current || !mapLoaded || !showOsmSwitchyards) return;
    if (osmSwitchyardsAbortRef.current) osmSwitchyardsAbortRef.current.abort();
    const controller = new AbortController();
    osmSwitchyardsAbortRef.current = controller;
    onOsmSwitchyardsLoadingChange?.(true);
    try {
      const data = await fetchOsmSwitchyardsGeoJSON(
        { bbox: getBbox(), hifldNewOnly: osmSwitchyardsHifldNewOnly },
        controller.signal,
      );
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('osm-switchyards') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load OSM switchyards:', err);
    } finally {
      if (osmSwitchyardsAbortRef.current === controller) {
        onOsmSwitchyardsLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showOsmSwitchyards, osmSwitchyardsHifldNewOnly, getBbox, onOsmSwitchyardsLoadingChange]);

  // Load ERCOT queue heatmap — fetch once (157 TX counties), no re-fetch on pan
  const queueHeatmapDataRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const loadQueueHeatmap = useCallback(async () => {
    if (!map.current || !mapLoaded || !showQueueHeatmap) return;

    // Use cached data if available
    if (queueHeatmapDataRef.current) {
      const src = map.current.getSource('queue-heatmap') as mapboxgl.GeoJSONSource;
      if (src) src.setData(queueHeatmapDataRef.current);
      return;
    }

    if (queueHeatmapAbortRef.current) queueHeatmapAbortRef.current.abort();
    const controller = new AbortController();
    queueHeatmapAbortRef.current = controller;
    onQueueHeatmapLoadingChange?.(true);

    try {
      const data = await fetchQueueHeatmapGeoJSON(undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      queueHeatmapDataRef.current = data;
      const src = map.current.getSource('queue-heatmap') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load queue heatmap:', err);
    } finally {
      if (queueHeatmapAbortRef.current === controller) {
        onQueueHeatmapLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showQueueHeatmap, onQueueHeatmapLoadingChange]);

  // Load congestion heatmap — fetch once, cache in ref
  const congestionHeatmapDataRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const loadCongestionHeatmap = useCallback(async () => {
    if (!map.current || !mapLoaded || !showCongestionHeatmap) return;

    if (congestionHeatmapDataRef.current) {
      const src = map.current.getSource('congestion-heatmap') as mapboxgl.GeoJSONSource;
      if (src) src.setData(congestionHeatmapDataRef.current);
      return;
    }

    if (congestionHeatmapAbortRef.current) congestionHeatmapAbortRef.current.abort();
    const controller = new AbortController();
    congestionHeatmapAbortRef.current = controller;
    onCongestionHeatmapLoadingChange?.(true);

    try {
      const data = await fetchCongestionHeatmapGeoJSON(controller.signal);
      if (controller.signal.aborted || !map.current) return;
      congestionHeatmapDataRef.current = data;
      const src = map.current.getSource('congestion-heatmap') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load congestion heatmap:', err);
    } finally {
      if (congestionHeatmapAbortRef.current === controller) {
        onCongestionHeatmapLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showCongestionHeatmap, onCongestionHeatmapLoadingChange]);

  // Load airports when toggled on
  const loadAirports = useCallback(async () => {
    if (!map.current || !mapLoaded || !showAirportZones) return;

    if (airportAbortRef.current) airportAbortRef.current.abort();
    const controller = new AbortController();
    airportAbortRef.current = controller;
    onAirportLoadingChange?.(true);

    try {
      const data = await fetchAirportsGeoJSON(undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;

      // Filter by airport_class if commercial-only is selected
      const filtered = airportFilter === 'commercial'
        ? {
            ...data,
            features: data.features.filter(f => {
              const meta = typeof f.properties?.metadata === 'string'
                ? JSON.parse(f.properties.metadata) : (f.properties?.metadata || {});
              return meta.airport_class === 'commercial';
            }),
          }
        : data;

      // Set airport points
      const airportSrc = map.current.getSource('airports') as mapboxgl.GeoJSONSource;
      if (airportSrc) airportSrc.setData(filtered);

      // Generate 100-mile radius circle polygons (public-use airports only)
      const zoneFeatures = filtered.features
        .filter(f => {
          if (f.geometry?.type !== 'Point') return false;
          const meta = typeof f.properties?.metadata === 'string'
            ? JSON.parse(f.properties.metadata) : (f.properties?.metadata || {});
          return meta.facility_use === 'PU' && meta.site_type === 'A';
        })
        .map(f => {
          const circle = turfCircle(
            (f.geometry as GeoJSON.Point).coordinates,
            100,
            { steps: 32, units: 'miles' }
          );
          circle.properties = f.properties;
          return circle;
        });

      const zonesFC: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: zoneFeatures,
      };

      const zoneSrc = map.current.getSource('airport-zones') as mapboxgl.GeoJSONSource;
      if (zoneSrc) zoneSrc.setData(zonesFC);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load airports:', err);
    } finally {
      if (airportAbortRef.current === controller) {
        onAirportLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showAirportZones, onAirportLoadingChange, airportFilter]);

  // Load fiber coverage when toggled on
  const loadFiberInfra = useCallback(async () => {
    if (!map.current || !mapLoaded || !showFiber) return;

    if (fiberAbortRef.current) fiberAbortRef.current.abort();
    const controller = new AbortController();
    fiberAbortRef.current = controller;
    onFiberLoadingChange?.(true);

    try {
      const data = await fetchFiberGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('fiber-coverage') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
      onFiberCountChange?.(data.features?.length ?? 0);
      setFiberTruncated(data.truncated ?? false);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load fiber coverage:', err);
    } finally {
      if (fiberAbortRef.current === controller) {
        onFiberLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showFiber, getBbox, onFiberLoadingChange, onFiberCountChange]);

  // Load carrier backbone fiber routes — server-side filtered by visible carriers
  const NTIA_CARRIERS = NTIA_CARRIER_NAMES;
  const anyCarrierActive = showSegra || showZayo || showFiberLight || showNtiaMiddleMile;
  const visibleCarriers = useMemo(() => {
    const c: string[] = [];
    if (showSegra) c.push('Segra');
    if (showZayo) c.push('Zayo');
    if (showFiberLight) c.push('FiberLight');
    if (showNtiaMiddleMile) c.push(...NTIA_CARRIERS);
    return c;
  }, [showSegra, showZayo, showFiberLight, showNtiaMiddleMile]);

  const loadCarrierRoutes = useCallback(async () => {
    if (!map.current || !mapLoaded || visibleCarriers.length === 0) return;

    if (carrierRoutesAbortRef.current) carrierRoutesAbortRef.current.abort();
    const controller = new AbortController();
    carrierRoutesAbortRef.current = controller;

    try {
      const data = await fetchCarrierRoutesGeoJSON(getBbox(), controller.signal, map.current?.getZoom(), visibleCarriers);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('carrier-routes') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load carrier routes:', err);
    }
  }, [mapLoaded, visibleCarriers, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (anyCarrierActive) {
      loadCarrierRoutes();
    } else {
      const src = map.current.getSource('carrier-routes') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => carrierRoutesAbortRef.current?.abort();
  }, [anyCarrierActive, mapLoaded, loadCarrierRoutes]);

  // Load fiber-ROW pipelines (KMI/Williams/Transwestern subset) when toggled on
  const loadFiberRowPipelines = useCallback(async () => {
    if (!map.current || !mapLoaded || !showFiberRowPipelines) return;

    if (fiberRowPipelinesAbortRef.current) fiberRowPipelinesAbortRef.current.abort();
    const controller = new AbortController();
    fiberRowPipelinesAbortRef.current = controller;

    try {
      const data = await fetchFiberRowPipelinesGeoJSON(getBbox(), controller.signal, map.current?.getZoom());
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('fiber-row-pipelines') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load fiber-ROW pipelines:', err);
    }
  }, [mapLoaded, showFiberRowPipelines, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showFiberRowPipelines) {
      loadFiberRowPipelines();
    } else {
      const src = map.current.getSource('fiber-row-pipelines') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => fiberRowPipelinesAbortRef.current?.abort();
  }, [showFiberRowPipelines, mapLoaded, loadFiberRowPipelines]);

  // Load IXP & colo facilities when toggled on
  const loadIxpFacilities = useCallback(async () => {
    if (!map.current || !mapLoaded || !showIxpFacilities) return;

    if (ixpAbortRef.current) ixpAbortRef.current.abort();
    const controller = new AbortController();
    ixpAbortRef.current = controller;

    try {
      const data = await fetchIxpFacilitiesGeoJSON(undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('ixp-facilities') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load IXP facilities:', err);
    }
  }, [mapLoaded, showIxpFacilities]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showIxpFacilities) {
      loadIxpFacilities();
    } else {
      const src = map.current.getSource('ixp-facilities') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => ixpAbortRef.current?.abort();
  }, [showIxpFacilities, mapLoaded, loadIxpFacilities]);

  // Load water infrastructure when toggled on
  const loadWaterInfra = useCallback(async () => {
    if (!map.current || !mapLoaded || !showWater) return;

    if (waterAbortRef.current) waterAbortRef.current.abort();
    const controller = new AbortController();
    waterAbortRef.current = controller;
    onWaterLoadingChange?.(true);

    try {
      const bbox = getBbox();
      // Fetch wells, service areas, and aquifers in parallel
      const [waterData, aquiferData] = await Promise.all([
        fetchWaterGeoJSON(undefined, bbox, controller.signal),
        fetchAquifersGeoJSON(bbox, controller.signal),
      ]);
      if (controller.signal.aborted || !map.current) return;

      const waterSrc = map.current.getSource('water-infra') as mapboxgl.GeoJSONSource;
      if (waterSrc) waterSrc.setData(waterData);

      const aquiferSrc = map.current.getSource('aquifer-boundaries') as mapboxgl.GeoJSONSource;
      if (aquiferSrc) aquiferSrc.setData(aquiferData);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load water infrastructure:', err);
    } finally {
      if (waterAbortRef.current === controller) {
        onWaterLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showWater, getBbox, onWaterLoadingChange]);

  // Load flood zones when toggled on
  const loadFloodZones = useCallback(async () => {
    if (!map.current || !mapLoaded || !showFloodZones) return;

    if (floodZoneAbortRef.current) floodZoneAbortRef.current.abort();
    const controller = new AbortController();
    floodZoneAbortRef.current = controller;
    onFloodZoneLoadingChange?.(true);

    try {
      const z = map.current.getZoom();
      const bbox = getBbox();
      if (!bbox) return;
      const [effective, pending] = await Promise.allSettled([
        fetchFloodZonesGeoJSON(bbox, z, controller.signal),
        fetchPendingFloodZonesGeoJSON(bbox, z, controller.signal),
      ]);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('flood-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(effective.status === 'fulfilled' ? effective.value : EMPTY_FC);
      const pendingSrc = map.current.getSource('pending-flood-zones') as mapboxgl.GeoJSONSource;
      const pendingData = pending.status === 'fulfilled' ? pending.value : EMPTY_FC;
      if (pendingSrc) pendingSrc.setData(pendingData);
      setPendingFloodLabels(pendingFemaLabels(pendingData));
      setFloodTruncated((effective.status === 'fulfilled' && effective.value.truncated === true)
        || (pending.status === 'fulfilled' && pending.value.truncated === true));
      for (const result of [effective, pending]) if (result.status === 'rejected') console.error('Failed to load FEMA map evidence:', result.reason);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load flood zones:', err);
    } finally {
      if (floodZoneAbortRef.current === controller) {
        onFloodZoneLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showFloodZones, getBbox, onFloodZoneLoadingChange]);

  const loadTwdbFloodHazards = useCallback(async () => {
    if (!map.current || !mapLoaded || !showTwdbFloodHazards) return;

    if (twdbFloodHazardsAbortRef.current) twdbFloodHazardsAbortRef.current.abort();
    const controller = new AbortController();
    twdbFloodHazardsAbortRef.current = controller;
    onTwdbFloodHazardsLoadingChange?.(true);

    try {
      const z = map.current.getZoom();
      const data = await fetchTwdbFloodHazardsGeoJSON(getBbox(), z, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('twdb-flood-hazards') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load TWDB flood hazards:', err);
    } finally {
      if (twdbFloodHazardsAbortRef.current === controller) {
        onTwdbFloodHazardsLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showTwdbFloodHazards, getBbox, onTwdbFloodHazardsLoadingChange]);

  // Load highways when toggled on
  const loadHighways = useCallback(async () => {
    if (!map.current || !mapLoaded || !showHighways) return;

    if (highwayAbortRef.current) highwayAbortRef.current.abort();
    const controller = new AbortController();
    highwayAbortRef.current = controller;
    onHighwayLoadingChange?.(true);

    try {
      const data = await fetchHighwaysGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('highways') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load highways:', err);
    } finally {
      if (highwayAbortRef.current === controller) {
        onHighwayLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showHighways, getBbox, onHighwayLoadingChange]);

  // Load roads when toggled on. Dense data, so nothing is fetched below zoom 9.
  const loadRoads = useCallback(async () => {
    if (!map.current || !mapLoaded || !showRoads) return;

    if (roadsAbortRef.current) roadsAbortRef.current.abort();
    const controller = new AbortController();
    roadsAbortRef.current = controller;

    if (map.current.getZoom() < 9) {
      const zoomedOut = map.current.getSource('roads') as mapboxgl.GeoJSONSource;
      if (zoomedOut) zoomedOut.setData(EMPTY_FC);
      // Zooming out mid-fetch aborts the request, whose finally block no longer owns the
      // ref — clear the spinner here or it sticks on.
      onRoadsLoadingChange?.(false);
      return;
    }
    onRoadsLoadingChange?.(true);

    try {
      const data = await fetchRoadsGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('roads') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load roads:', err);
    } finally {
      if (roadsAbortRef.current === controller) {
        onRoadsLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showRoads, getBbox, onRoadsLoadingChange]);

  // Load OSM pipeline candidates when toggled on — corridors draw from zoom 7, so the
  // request is skipped and the source emptied below that.
  const loadOsmPipelineCandidates = useCallback(async () => {
    if (!map.current || !mapLoaded || !showOsmPipelineCandidates) return;

    if (osmPipelineCandidatesAbortRef.current) osmPipelineCandidatesAbortRef.current.abort();
    const controller = new AbortController();
    osmPipelineCandidatesAbortRef.current = controller;

    if (map.current.getZoom() < 7) {
      const zoomedOut = map.current.getSource('osm-pipeline-candidates') as mapboxgl.GeoJSONSource;
      if (zoomedOut) zoomedOut.setData(EMPTY_FC);
      onOsmPipelineCandidatesLoadingChange?.(false);
      return;
    }
    onOsmPipelineCandidatesLoadingChange?.(true);

    try {
      const data = await fetchOsmPipelineCandidatesGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('osm-pipeline-candidates') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load OSM pipeline candidates:', err);
    } finally {
      if (osmPipelineCandidatesAbortRef.current === controller) {
        onOsmPipelineCandidatesLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showOsmPipelineCandidates, getBbox, onOsmPipelineCandidatesLoadingChange]);

  // Load railroads when toggled on
  const loadRailroads = useCallback(async () => {
    if (!map.current || !mapLoaded || !showRailroads) return;

    if (railroadAbortRef.current) railroadAbortRef.current.abort();
    const controller = new AbortController();
    railroadAbortRef.current = controller;
    onRailroadLoadingChange?.(true);

    try {
      const data = await fetchRailroadsGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('railroads') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load railroads:', err);
    } finally {
      if (railroadAbortRef.current === controller) {
        onRailroadLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showRailroads, getBbox, onRailroadLoadingChange]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showPowerInfra) {
      loadPowerInfra();
    } else {
      const src = map.current.getSource('power-infra') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => powerAbortRef.current?.abort();
  }, [showPowerInfra, mapLoaded, loadPowerInfra]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showOsmSwitchyards) {
      loadOsmSwitchyards();
    } else {
      const src = map.current.getSource('osm-switchyards') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => osmSwitchyardsAbortRef.current?.abort();
  }, [showOsmSwitchyards, mapLoaded, loadOsmSwitchyards]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showGridCapacity) {
      loadGridCapacity();
    } else {
      const src = map.current.getSource('grid-capacity') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => gridCapacityAbortRef.current?.abort();
  }, [showGridCapacity, mapLoaded, loadGridCapacity]);

  // Update grid capacity layer filters when tiers change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const tiers = Array.from(gridCapacityTiers);
    const tierFilter: mapboxgl.FilterSpecification = ['in', ['get', 'capacity_tier'], ['literal', tiers]];
    const lineFilter: mapboxgl.FilterSpecification = ['all', ['==', ['get', 'type'], 'transmission_line'], tierFilter];
    const circleFilter: mapboxgl.FilterSpecification = ['all', ['==', ['get', 'type'], 'substation'], tierFilter];
    if (map.current.getLayer('grid-capacity-lines')) map.current.setFilter('grid-capacity-lines', lineFilter);
    if (map.current.getLayer('grid-capacity-circles')) map.current.setFilter('grid-capacity-circles', circleFilter);
    if (map.current.getLayer('grid-capacity-labels')) map.current.setFilter('grid-capacity-labels', circleFilter);
  }, [gridCapacityTiers, mapLoaded]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showQueueHeatmap) {
      loadQueueHeatmap();
    } else {
      const src = map.current.getSource('queue-heatmap') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => queueHeatmapAbortRef.current?.abort();
  }, [showQueueHeatmap, mapLoaded, loadQueueHeatmap]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showCongestionHeatmap) {
      loadCongestionHeatmap();
    } else {
      const src = map.current.getSource('congestion-heatmap') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => congestionHeatmapAbortRef.current?.abort();
  }, [showCongestionHeatmap, mapLoaded, loadCongestionHeatmap]);

  // Toggle airport zones
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showAirportZones) {
      loadAirports();
    } else {
      const airportSrc = map.current.getSource('airports') as mapboxgl.GeoJSONSource;
      if (airportSrc) airportSrc.setData(EMPTY_FC);
      const zoneSrc = map.current.getSource('airport-zones') as mapboxgl.GeoJSONSource;
      if (zoneSrc) zoneSrc.setData(EMPTY_FC);
    }
    return () => airportAbortRef.current?.abort();
  }, [showAirportZones, mapLoaded, loadAirports]);

  // Toggle fiber coverage
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showFiber) {
      loadFiberInfra();
    } else {
      const src = map.current.getSource('fiber-coverage') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
      onFiberCountChange?.(0);
      setFiberTruncated(false);
    }
    return () => fiberAbortRef.current?.abort();
  }, [showFiber, mapLoaded, loadFiberInfra, onFiberCountChange]);

  // Toggle water infrastructure
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showWater) {
      loadWaterInfra();
    } else {
      const waterSrc = map.current.getSource('water-infra') as mapboxgl.GeoJSONSource;
      if (waterSrc) waterSrc.setData(EMPTY_FC);
      const aquiferSrc = map.current.getSource('aquifer-boundaries') as mapboxgl.GeoJSONSource;
      if (aquiferSrc) aquiferSrc.setData(EMPTY_FC);
    }
    return () => waterAbortRef.current?.abort();
  }, [showWater, mapLoaded, loadWaterInfra]);

  // Toggle flood zones
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showFloodZones) {
      loadFloodZones();
    } else {
      const src = map.current.getSource('flood-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
      const pendingSrc = map.current.getSource('pending-flood-zones') as mapboxgl.GeoJSONSource;
      if (pendingSrc) pendingSrc.setData(EMPTY_FC);
      setPendingFloodLabels([]);
      setFloodTruncated(false);
    }
    return () => floodZoneAbortRef.current?.abort();
  }, [showFloodZones, mapLoaded, loadFloodZones]);

  // Toggle TWDB existing flood hazards
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showTwdbFloodHazards) {
      loadTwdbFloodHazards();
    } else {
      const src = map.current.getSource('twdb-flood-hazards') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => twdbFloodHazardsAbortRef.current?.abort();
  }, [showTwdbFloodHazards, mapLoaded, loadTwdbFloodHazards]);

  // Toggle highways
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showHighways) {
      loadHighways();
    } else {
      const src = map.current.getSource('highways') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => highwayAbortRef.current?.abort();
  }, [showHighways, mapLoaded, loadHighways]);

  // Toggle roads
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const m = map.current;
    for (const id of ROADS_LAYERS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', showRoads ? 'visible' : 'none');
    }
    if (showRoads) {
      loadRoads();
    } else {
      const src = m.getSource('roads') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => roadsAbortRef.current?.abort();
  }, [showRoads, mapLoaded, loadRoads]);

  // Toggle OSM pipeline candidates
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const m = map.current;
    for (const id of OSM_PIPELINE_CANDIDATE_LAYERS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', showOsmPipelineCandidates ? 'visible' : 'none');
    }
    if (showOsmPipelineCandidates) {
      loadOsmPipelineCandidates();
    } else {
      const src = m.getSource('osm-pipeline-candidates') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => osmPipelineCandidatesAbortRef.current?.abort();
  }, [showOsmPipelineCandidates, mapLoaded, loadOsmPipelineCandidates]);

  // Toggle the Georgia site outline (static source; nothing to fetch)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    for (const id of GEORGIA_SITE_LAYERS) {
      if (map.current.getLayer(id)) map.current.setLayoutProperty(id, 'visibility', showGeorgiaSites ? 'visible' : 'none');
    }
  }, [showGeorgiaSites, mapLoaded]);

  // Toggle the Georgia evidence pins (static source; nothing to fetch)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    for (const id of GEORGIA_EVIDENCE_LAYERS) {
      if (map.current.getLayer(id)) map.current.setLayoutProperty(id, 'visibility', showGeorgiaEvidence ? 'visible' : 'none');
    }
  }, [showGeorgiaEvidence, mapLoaded]);

  // Toggle railroads
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showRailroads) {
      loadRailroads();
    } else {
      const src = map.current.getSource('railroads') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => railroadAbortRef.current?.abort();
  }, [showRailroads, mapLoaded, loadRailroads]);

  // Load gas plants (toggle-only, no bbox — sparse dataset)
  const loadGasPlants = useCallback(async () => {
    if (!map.current || !mapLoaded || !showGasPlants) return;

    if (gasPlantAbortRef.current) gasPlantAbortRef.current.abort();
    const controller = new AbortController();
    gasPlantAbortRef.current = controller;
    onGasPlantLoadingChange?.(true);

    try {
      const data = await fetchGasPlantsGeoJSON(controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('gas-plants') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load gas plants:', err);
    } finally {
      if (gasPlantAbortRef.current === controller) {
        onGasPlantLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showGasPlants, onGasPlantLoadingChange]);

  // Toggle gas plants
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showGasPlants) {
      loadGasPlants();
    } else {
      const src = map.current.getSource('gas-plants') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => gasPlantAbortRef.current?.abort();
  }, [showGasPlants, mapLoaded, loadGasPlants]);

  // Load gas infrastructure — each type gets its own abort controller
  const loadGasInfraType = useCallback(async (type: string, sourceId: string) => {
    if (!map.current || !mapLoaded) return;

    if (gasInfraAbortRefs.current[type]) gasInfraAbortRefs.current[type]!.abort();
    const controller = new AbortController();
    gasInfraAbortRefs.current[type] = controller;
    gasInfraLoadingCount.current++;
    onGasInfraLoadingChange?.(true);

    try {
      const data = await fetchGasInfraGeoJSON(type, undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error(`Failed to load gas infra ${type}:`, err);
    } finally {
      if (gasInfraAbortRefs.current[type] === controller) {
        gasInfraLoadingCount.current--;
        onGasInfraLoadingChange?.(gasInfraLoadingCount.current > 0);
      }
    }
  }, [mapLoaded, onGasInfraLoadingChange]);

  // Each gas infra type toggles independently
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showCompressors) { loadGasInfraType('compressor', 'gas-infra-compressors'); }
    else { const s = map.current.getSource('gas-infra-compressors') as mapboxgl.GeoJSONSource; if (s) s.setData(EMPTY_FC); }
    return () => gasInfraAbortRefs.current['compressor']?.abort();
  }, [showCompressors, mapLoaded, loadGasInfraType]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showReceiptDelivery) { loadGasInfraType('receipt_delivery', 'gas-infra-receipt-delivery'); }
    else { const s = map.current.getSource('gas-infra-receipt-delivery') as mapboxgl.GeoJSONSource; if (s) s.setData(EMPTY_FC); }
    return () => gasInfraAbortRefs.current['receipt_delivery']?.abort();
  }, [showReceiptDelivery, mapLoaded, loadGasInfraType]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showInterconnects) { loadGasInfraType('interconnect', 'gas-infra-interconnects'); }
    else { const s = map.current.getSource('gas-infra-interconnects') as mapboxgl.GeoJSONSource; if (s) s.setData(EMPTY_FC); }
    return () => gasInfraAbortRefs.current['interconnect']?.abort();
  }, [showInterconnects, mapLoaded, loadGasInfraType]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showGasStorage) { loadGasInfraType('gas_storage', 'gas-infra-storage'); }
    else { const s = map.current.getSource('gas-infra-storage') as mapboxgl.GeoJSONSource; if (s) s.setData(EMPTY_FC); }
    return () => gasInfraAbortRefs.current['gas_storage']?.abort();
  }, [showGasStorage, mapLoaded, loadGasInfraType]);

  // Load EPA zones (bbox-based — large polygons)
  const loadEpaZones = useCallback(async () => {
    if (!map.current || !mapLoaded || !showEpaZones) return;

    if (epaZoneAbortRef.current) epaZoneAbortRef.current.abort();
    const controller = new AbortController();
    epaZoneAbortRef.current = controller;
    onEpaZoneLoadingChange?.(true);

    try {
      const zoom = Math.round(map.current.getZoom());
      const data = await fetchEpaZonesGeoJSON(getBbox(), zoom, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('epa-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load EPA zones:', err);
    } finally {
      if (epaZoneAbortRef.current === controller) {
        onEpaZoneLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showEpaZones, getBbox, onEpaZoneLoadingChange]);

  // Toggle EPA zones
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showEpaZones) {
      loadEpaZones();
    } else {
      const src = map.current.getSource('epa-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => epaZoneAbortRef.current?.abort();
  }, [showEpaZones, mapLoaded, loadEpaZones]);

  // Load wildfire county choropleth (bbox + zoom)
  const loadWildfire = useCallback(async () => {
    if (!map.current || !mapLoaded || !showWildfire) return;

    if (wildfireAbortRef.current) wildfireAbortRef.current.abort();
    const controller = new AbortController();
    wildfireAbortRef.current = controller;
    onWildfireLoadingChange?.(true);

    try {
      const zoom = Math.round(map.current.getZoom());
      const data = await fetchWildfireGeoJSON(getBbox(), zoom, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('wildfire') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load wildfire:', err);
    } finally {
      if (wildfireAbortRef.current === controller) {
        onWildfireLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showWildfire, getBbox, onWildfireLoadingChange]);

  // Toggle wildfire
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showWildfire) {
      loadWildfire();
    } else {
      const src = map.current.getSource('wildfire') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => wildfireAbortRef.current?.abort();
  }, [showWildfire, mapLoaded, loadWildfire]);

  // Load EJScreen data (bbox-based)
  const loadEjScreen = useCallback(async () => {
    if (!map.current || !mapLoaded || !showEjScreen) return;

    if (ejScreenAbortRef.current) ejScreenAbortRef.current.abort();
    const controller = new AbortController();
    ejScreenAbortRef.current = controller;
    onEjScreenLoadingChange?.(true);

    try {
      const data = await fetchEjScreenGeoJSON(getBbox(), Math.round(map.current.getZoom()), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('ejscreen') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load EJScreen:', err);
    } finally {
      if (ejScreenAbortRef.current === controller) {
        onEjScreenLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showEjScreen, getBbox, onEjScreenLoadingChange]);

  // Toggle EJScreen layer
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showEjScreen) {
      loadEjScreen();
    } else {
      const src = map.current.getSource('ejscreen') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
      onEjScreenLoadingChange?.(false);
    }
    return () => ejScreenAbortRef.current?.abort();
  }, [showEjScreen, mapLoaded, loadEjScreen, onEjScreenLoadingChange]);

  // Load Critical Habitat
  const loadCriticalHabitat = useCallback(async () => {
    if (!map.current || !mapLoaded || !showCriticalHabitat) return;
    if (criticalHabitatAbortRef.current) criticalHabitatAbortRef.current.abort();
    const controller = new AbortController();
    criticalHabitatAbortRef.current = controller;
    try {
      const data = await fetchCriticalHabitatGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('critical-habitat') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load critical habitat:', err);
    }
  }, [mapLoaded, showCriticalHabitat, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showCriticalHabitat) {
      loadCriticalHabitat();
    } else {
      const src = map.current.getSource('critical-habitat') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => criticalHabitatAbortRef.current?.abort();
  }, [showCriticalHabitat, mapLoaded, loadCriticalHabitat]);

  // Load Mine Subsidence
  const loadMineSubsidence = useCallback(async () => {
    if (!map.current || !mapLoaded || !showMineSubsidence) return;
    if (mineSubsidenceAbortRef.current) mineSubsidenceAbortRef.current.abort();
    const controller = new AbortController();
    mineSubsidenceAbortRef.current = controller;
    try {
      const data = await fetchMineSubsidenceGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('mine-subsidence') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load mine subsidence:', err);
    }
  }, [mapLoaded, showMineSubsidence, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showMineSubsidence) {
      loadMineSubsidence();
    } else {
      const src = map.current.getSource('mine-subsidence') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => mineSubsidenceAbortRef.current?.abort();
  }, [showMineSubsidence, mapLoaded, loadMineSubsidence]);

  // Load AML Sites
  const loadAmlSites = useCallback(async () => {
    if (!map.current || !mapLoaded || !showAmlSites) return;
    if (amlSitesAbortRef.current) amlSitesAbortRef.current.abort();
    const controller = new AbortController();
    amlSitesAbortRef.current = controller;
    try {
      const data = await fetchAmlSitesGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('aml-sites') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load AML sites:', err);
    }
  }, [mapLoaded, showAmlSites, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showAmlSites) {
      loadAmlSites();
    } else {
      const src = map.current.getSource('aml-sites') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => amlSitesAbortRef.current?.abort();
  }, [showAmlSites, mapLoaded, loadAmlSites]);

  // Wetlands visibility toggle (vector tiles load automatically)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const vis = showWetlands ? 'visible' : 'none';
    ['wetlands-fill', 'wetlands-border'].forEach(id => {
      if (map.current!.getLayer(id)) map.current!.setLayoutProperty(id, 'visibility', vis);
    });
  }, [showWetlands, mapLoaded]);

  // Load Water Regulatory Zones
  const loadWaterRegZones = useCallback(async () => {
    if (!map.current || !mapLoaded || !showWaterRegulatoryZones) return;
    if (waterRegZonesAbortRef.current) waterRegZonesAbortRef.current.abort();
    const controller = new AbortController();
    waterRegZonesAbortRef.current = controller;
    try {
      const data = await fetchWaterRegulatoryZonesGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('water-regulatory-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load water regulatory zones:', err);
    }
  }, [mapLoaded, showWaterRegulatoryZones, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showWaterRegulatoryZones) {
      loadWaterRegZones();
    } else {
      const src = map.current.getSource('water-regulatory-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => waterRegZonesAbortRef.current?.abort();
  }, [showWaterRegulatoryZones, mapLoaded, loadWaterRegZones]);

  // Load TWDB Water Wells
  const loadTwdbWells = useCallback(async () => {
    if (!map.current || !mapLoaded || !showTwdbWells) return;
    if (twdbWellsAbortRef.current) twdbWellsAbortRef.current.abort();
    const controller = new AbortController();
    twdbWellsAbortRef.current = controller;
    try {
      const data = await fetchTwdbWellsGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('twdb-wells') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load TWDB wells:', err);
    }
  }, [mapLoaded, showTwdbWells, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showTwdbWells) {
      loadTwdbWells();
    } else {
      const src = map.current.getSource('twdb-wells') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => twdbWellsAbortRef.current?.abort();
  }, [showTwdbWells, mapLoaded, loadTwdbWells]);

  // Load Brackish aquifer extent polygons (TWDB BRACS)
  const loadBrackish = useCallback(async () => {
    if (!map.current || !mapLoaded || !showBrackish) return;
    if (brackishAbortRef.current) brackishAbortRef.current.abort();
    const controller = new AbortController();
    brackishAbortRef.current = controller;
    try {
      const data = await fetchBrackishZonesGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('brackish-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load brackish zones:', err);
    }
  }, [mapLoaded, showBrackish, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showBrackish) {
      loadBrackish();
    } else {
      const src = map.current.getSource('brackish-zones') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => brackishAbortRef.current?.abort();
  }, [showBrackish, mapLoaded, loadBrackish]);

  // Load TDS samples (TWDB GWDB measured TDS at well points)
  const loadTdsSamples = useCallback(async () => {
    if (!map.current || !mapLoaded || !showTdsSamples) return;
    if (tdsSamplesAbortRef.current) tdsSamplesAbortRef.current.abort();
    const controller = new AbortController();
    tdsSamplesAbortRef.current = controller;
    try {
      const data = await fetchTdsSamplesGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('tds-samples') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load TDS samples:', err);
    }
  }, [mapLoaded, showTdsSamples, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showTdsSamples) {
      loadTdsSamples();
    } else {
      const src = map.current.getSource('tds-samples') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => tdsSamplesAbortRef.current?.abort();
  }, [showTdsSamples, mapLoaded, loadTdsSamples]);

  // Load Seismic Risk
  const loadSeismicRisk = useCallback(async () => {
    if (!map.current || !mapLoaded || !showSeismicRisk) return;
    if (seismicRiskAbortRef.current) seismicRiskAbortRef.current.abort();
    const controller = new AbortController();
    seismicRiskAbortRef.current = controller;
    try {
      const data = await fetchSeismicRiskGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('seismic-risk') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load seismic risk:', err);
    }
  }, [mapLoaded, showSeismicRisk, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showSeismicRisk) {
      loadSeismicRisk();
    } else {
      const src = map.current.getSource('seismic-risk') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => seismicRiskAbortRef.current?.abort();
  }, [showSeismicRisk, mapLoaded, loadSeismicRisk]);

  // Load Urban Areas
  const loadUrbanAreas = useCallback(async () => {
    if (!map.current || !mapLoaded || !showUrbanAreas) return;
    if (urbanAreasAbortRef.current) urbanAreasAbortRef.current.abort();
    const controller = new AbortController();
    urbanAreasAbortRef.current = controller;
    try {
      const data = await fetchUrbanAreasGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('urban-areas') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load urban areas:', err);
    }
  }, [mapLoaded, showUrbanAreas, getBbox]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showUrbanAreas) {
      loadUrbanAreas();
    } else {
      const src = map.current.getSource('urban-areas') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => urbanAreasAbortRef.current?.abort();
  }, [showUrbanAreas, mapLoaded, loadUrbanAreas]);

  // Load data centers
  const loadDataCenters = useCallback(async () => {
    if (!map.current || !mapLoaded || !showDataCenters) return;

    if (dcAbortRef.current) dcAbortRef.current.abort();
    const controller = new AbortController();
    dcAbortRef.current = controller;
    onDataCenterLoadingChange?.(true);

    try {
      const data = await fetchDataCentersGeoJSON(getBbox(), controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const src = map.current.getSource('data-centers') as mapboxgl.GeoJSONSource;
      if (src) src.setData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load data centers:', err);
    } finally {
      if (dcAbortRef.current === controller) {
        onDataCenterLoadingChange?.(false);
      }
    }
  }, [mapLoaded, showDataCenters, getBbox, onDataCenterLoadingChange]);

  // Toggle data centers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (showDataCenters) {
      loadDataCenters();
    } else {
      const src = map.current.getSource('data-centers') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
    }
    return () => dcAbortRef.current?.abort();
  }, [showDataCenters, mapLoaded, loadDataCenters]);

  // Load wells (bbox-based) — fetch when either gas or oil toggle is on
  const anyWellsActive = showGasWells || showOilWells;
  const loadWells = useCallback(async () => {
    if (!map.current || !mapLoaded || !anyWellsActive) return;

    const zoom = map.current.getZoom();
    const src = map.current.getSource('wells-data') as mapboxgl.GeoJSONSource;
    if (!src) return;

    if (zoom < MIN_WELLS_ZOOM) {
      src.setData(EMPTY_FC);
      onWellsCountChange?.(0);
      setWellsTruncated(false);
      return;
    }

    if (wellsAbortRef.current) wellsAbortRef.current.abort();
    const controller = new AbortController();
    wellsAbortRef.current = controller;
    onWellsLoadingChange?.(true);

    try {
      const data = await fetchWellsGeoJSON(getBbox(), undefined, controller.signal);
      if (controller.signal.aborted || !map.current) return;
      const s = map.current.getSource('wells-data') as mapboxgl.GeoJSONSource;
      if (s) s.setData(data);

      const lateralFeatures = data.features
        .filter((f: GeoJSON.Feature) => f.properties?.latitude_bh && f.properties?.longitude_bh && f.geometry?.type === 'Point')
        .map((f: GeoJSON.Feature) => ({
          type: 'Feature' as const,
          properties: { well_name: f.properties!.well_name, highlighted: f.properties!.highlighted },
          geometry: {
            type: 'LineString' as const,
            coordinates: [
              (f.geometry as GeoJSON.Point).coordinates,
              [f.properties!.longitude_bh, f.properties!.latitude_bh],
            ],
          },
        }));
      const latSrc = map.current.getSource('well-laterals') as mapboxgl.GeoJSONSource;
      if (latSrc) latSrc.setData({ type: 'FeatureCollection', features: lateralFeatures });

      onWellsCountChange?.(data.features.length);
      setWellsTruncated(data.truncated ?? false);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'CanceledError') return;
      console.error('Failed to load wells:', err);
    } finally {
      if (wellsAbortRef.current === controller) {
        onWellsLoadingChange?.(false);
      }
    }
  }, [mapLoaded, anyWellsActive, getBbox, onWellsLoadingChange, onWellsCountChange]);

  // Toggle wells data + layer visibility
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    // Toggle gas layer visibility
    for (const id of ['gas-wells-circles', 'gas-wells-labels']) {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', showGasWells ? 'visible' : 'none');
      }
    }
    // Toggle oil layer visibility
    for (const id of ['oil-wells-circles', 'oil-wells-labels']) {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', showOilWells ? 'visible' : 'none');
      }
    }
    const wellsVis = anyWellsActive ? 'visible' : 'none';
    for (const id of ['wells-highlight-ring', 'well-laterals-line']) {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', wellsVis);
      }
    }
    if (anyWellsActive) {
      loadWells();
    } else {
      const src = map.current.getSource('wells-data') as mapboxgl.GeoJSONSource;
      if (src) src.setData(EMPTY_FC);
      const latSrc = map.current.getSource('well-laterals') as mapboxgl.GeoJSONSource;
      if (latSrc) latSrc.setData(EMPTY_FC);
      onWellsCountChange?.(0);
      setWellsTruncated(false);
    }
    return () => wellsAbortRef.current?.abort();
  }, [showGasWells, showOilWells, anyWellsActive, mapLoaded, loadWells, onWellsCountChange]);

  // Apply operator filter to all pipeline + corridor layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const filterExpr = hiddenOperators.length === 0
      ? null
      : ['!', ['in', ['get', 'operator'], ['literal', hiddenOperators]]] as mapboxgl.FilterSpecification;
    PIPELINE_LAYERS.forEach(pl => {
      map.current!.setFilter(pl.layerId, filterExpr);
    });
  }, [hiddenOperators, mapLoaded]);

  // Toggle parcel layer visibility
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const visibility = showParcels ? 'visible' : 'none';
    for (const id of ['parcels-fill', 'parcels-outline', 'parcels-selected', 'parcels-owner-labels', 'parcel-centroid-dots']) {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', visibility);
      }
    }
    if (!showParcels) onParcelCountChange(0);
  }, [showParcels, mapLoaded, onParcelCountChange]);

  // Count visible parcels (deduped across tile boundaries)
  useEffect(() => {
    if (!map.current || !mapLoaded || !showParcels) return;
    const m = map.current;
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const updateCount = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        const parcelLayers = ['parcels-fill', 'parcel-centroid-dots'].filter(id => m.getLayer(id));
        if (parcelLayers.length === 0) return;
        const features = m.queryRenderedFeatures(undefined, { layers: parcelLayers });
        const uniqueIds = new Set(features.map(f => f.properties?.id));
        onParcelCountChange(uniqueIds.size);
      }, 300);
    };
    m.on('moveend', updateCount);
    m.on('idle', updateCount);
    updateCount();
    return () => {
      m.off('moveend', updateCount);
      m.off('idle', updateCount);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [mapLoaded, showParcels, onParcelCountChange]);

  // Client-side parcel filters via Mapbox GL expressions
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const conditions: any[] = [];
    if (filters.min_score) {
      conditions.push(['>=', ['get', 'acquisition_score'], filters.min_score]);
    }
    if (filters.max_score) {
      conditions.push(['<=', ['get', 'acquisition_score'], filters.max_score]);
    }
    if (filters.min_acreage && filters.min_acreage > 200) {
      conditions.push(['>=', ['get', 'acreage'], filters.min_acreage]);
    }
    if (filters.max_acreage) {
      conditions.push(['<=', ['get', 'acreage'], filters.max_acreage]);
    }
    if (filters.min_dc_score) {
      conditions.push(['>=', ['coalesce', ['get', 'dc_suitability_score'], 0], filters.min_dc_score]);
    }
    if (filters.max_dc_score) {
      conditions.push(['<=', ['coalesce', ['get', 'dc_suitability_score'], 0], filters.max_dc_score]);
    }
    const filter = conditions.length > 0 ? ['all', ...conditions] : null;
    for (const layerId of ['parcels-fill', 'parcels-outline', 'parcels-owner-labels', 'parcel-centroid-dots']) {
      if (map.current.getLayer(layerId)) {
        map.current.setFilter(layerId, filter);
      }
    }
  }, [filters, mapLoaded]);

  // Reload visible layers when map is panned/zoomed (debounced)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const m = map.current;
    let debounceTimer: ReturnType<typeof setTimeout>;
    const onMoveEnd = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (showFederalLands) loadFederalLands();
        if (showCrownPineTracts) loadCrownPineTracts();
        if (showAssemblyGroups) loadAssemblyGroups();
        if (showStateTrustLands) loadStateTrustLands();
        if (showProtectedAreas) loadProtectedAreas();
        // Reload visible pipeline groups (desert_sw omitted — fetched without bbox, always returns full pipeline)
        if (showGathering) loadPipelineGroup('gathering', 'pipelines-gathering', true);
        if (showGasTransmission) loadPipelineGroup('gas_transmission', 'pipelines-gas-transmission', true);
        if (showLiquidsTransmission) loadPipelineGroup('liquids_transmission', 'pipelines-liquids-transmission', true);
        if (showPowerInfra) loadPowerInfra();
        if (showOsmSwitchyards) loadOsmSwitchyards();
        if (showGridCapacity) loadGridCapacity();
        if (showFiber) loadFiberInfra();
        if (showWater) loadWaterInfra();
        if (showFloodZones) loadFloodZones();
        if (showTwdbFloodHazards) loadTwdbFloodHazards();
        if (showHighways) loadHighways();
        if (showRoads) loadRoads();
        if (showOsmPipelineCandidates) loadOsmPipelineCandidates();
        if (showRailroads) loadRailroads();
        if (showEpaZones) loadEpaZones();
        if (showEjScreen) loadEjScreen();
        if (showCriticalHabitat) loadCriticalHabitat();
        if (showSeismicRisk) loadSeismicRisk();
        if (showUrbanAreas) loadUrbanAreas();
        if (showWildfire) loadWildfire();
        if (showMineSubsidence) loadMineSubsidence();
        if (showAmlSites) loadAmlSites();
        if (showDataCenters) loadDataCenters();
        if (showLandListings) loadLandListings();
        if (showGridListings) loadGridListings();
        if (anyWellsActive) loadWells();
        if (showBrackish) loadBrackish();
        if (showTdsSamples) loadTdsSamples();
        if (anyCarrierActive) loadCarrierRoutes();
        if (showFiberRowPipelines) loadFiberRowPipelines();
      }, 500);
    };
    m.on('moveend', onMoveEnd);
    return () => { m.off('moveend', onMoveEnd); clearTimeout(debounceTimer); };
  }, [mapLoaded, loadFederalLands, showFederalLands, loadCrownPineTracts, showCrownPineTracts, loadAssemblyGroups, showAssemblyGroups, loadStateTrustLands, showStateTrustLands, loadProtectedAreas, showProtectedAreas,
      loadPipelineGroup, showGathering, showGasTransmission, showLiquidsTransmission,
      loadPowerInfra, showPowerInfra, loadOsmSwitchyards, showOsmSwitchyards, loadGridCapacity, showGridCapacity, loadFiberInfra, showFiber, loadWaterInfra, showWater,
      loadFloodZones, showFloodZones, loadTwdbFloodHazards, showTwdbFloodHazards, loadHighways, showHighways, loadRoads, showRoads, loadOsmPipelineCandidates, showOsmPipelineCandidates, loadRailroads, showRailroads,
      loadEpaZones, showEpaZones,
      loadEjScreen, showEjScreen,
      loadCriticalHabitat, showCriticalHabitat,
      loadSeismicRisk, showSeismicRisk,
      loadUrbanAreas, showUrbanAreas,
      loadWildfire, showWildfire,
      loadMineSubsidence, showMineSubsidence,
      loadAmlSites, showAmlSites,
      loadDataCenters, showDataCenters,
      loadLandListings, showLandListings, loadGridListings, showGridListings,
      loadWells, anyWellsActive,
      loadBrackish, showBrackish, loadTdsSamples, showTdsSamples,
      loadCarrierRoutes, anyCarrierActive,
      loadFiberRowPipelines, showFiberRowPipelines]);

  // Highlight selected parcel
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    map.current.setFilter('parcels-selected', ['==', ['get', 'id'], selectedParcelId || '']);
  }, [selectedParcelId, mapLoaded]);

  // Measurement: disable double-click zoom when active. mapLoaded is a dep
  // because the measurement hook re-enables it on every style-swap re-attach.
  useEffect(() => {
    if (!map.current) return;
    if (measurementActive) {
      map.current.doubleClickZoom.disable();
    } else {
      map.current.doubleClickZoom.enable();
    }
  }, [measurementActive, mapLoaded]);

  // Measurement: cursor change when either tool is armed. Runs after the hook's
  // own cursor effect, so it has the last word on a mode swap or style swap.
  useEffect(() => {
    if (!map.current) return;
    map.current.getCanvas().style.cursor = (measurementActive || areaActive) ? 'crosshair' : '';
  }, [measurementActive, areaActive, mapLoaded]);

  // Measurement: keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';

      // 'M' to toggle measurement (only when not in a text field)
      if (e.key === 'm' && !e.ctrlKey && !e.metaKey && !isInput) {
        e.preventDefault();
        onMeasurementActiveChange(!distanceActiveRef.current);
        return;
      }

      // 'A' to toggle the acreage rectangle
      if (e.key === 'a' && !e.ctrlKey && !e.metaKey && !isInput) {
        e.preventDefault();
        onAreaActiveChange(!areaActiveRef.current);
        return;
      }

      // Everything below is distance-tool only — inert while area mode is armed.
      if (!distanceActiveRef.current || !map.current) return;

      // Ctrl+Z / Cmd+Z to undo last point
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !isInput) {
        e.preventDefault();
        mUndo();
        // Recalculate cached segment total from remaining points
        const pts = mCurrentPointsRef.current;
        let total = 0;
        for (let i = 1; i < pts.length; i++) total += turfDistance(pts[i - 1], pts[i], { units: 'miles' });
        measurementSegmentTotalRef.current = total;
        updateMeasurementSources(map.current);
        if (measurementCursorMarkerRef.current) {
          measurementCursorMarkerRef.current.remove();
          measurementCursorMarkerRef.current = null;
        }
        return;
      }

      // Escape: undo last point, or exit measure mode if nothing to undo
      if (e.key === 'Escape') {
        if (mCurrentPointsRef.current.length > 0) {
          mUndo();
          const pts = mCurrentPointsRef.current;
          let total = 0;
          for (let i = 1; i < pts.length; i++) total += turfDistance(pts[i - 1], pts[i], { units: 'miles' });
          measurementSegmentTotalRef.current = total;
          updateMeasurementSources(map.current);
          if (measurementCursorMarkerRef.current) {
            measurementCursorMarkerRef.current.remove();
            measurementCursorMarkerRef.current = null;
          }
        } else {
          onMeasurementActiveChange(false);
        }
        return;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mUndo, onMeasurementActiveChange, onAreaActiveChange, updateMeasurementSources]);

  // Measurement: clear sources when mode deactivated
  useEffect(() => {
    if (!measurementActive && map.current) {
      const previewSrc = map.current.getSource('measurement-preview') as mapboxgl.GeoJSONSource;
      if (previewSrc) previewSrc.setData(EMPTY_FC);
      if (measurementCursorMarkerRef.current) {
        measurementCursorMarkerRef.current.remove();
        measurementCursorMarkerRef.current = null;
      }
    }
  }, [measurementActive]);

  return (
    <div style={{ flex: 1, width: '100%', position: 'relative' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      {Object.values(memphisLayers).some(Boolean) && <div data-testid="memphis-map-notice" style={{ position: 'absolute', top: 12, left: 12, maxWidth: 'min(290px, 70%)', padding: '8px 10px', background: 'rgba(30,40,48,0.94)', color: '#eee3cb', border: '1px solid #e8b878', borderRadius: 4, fontSize: 11, lineHeight: 1.5, pointerEvents: 'none' }}>
        <strong>Memphis · provisional CAD outline</strong><br />Gas availability unconfirmed · October 21, 2026
      </div>}
      {showFloodZones && (pendingFloodLabels.length > 0 || floodTruncated) && (
        <div style={{ position: 'absolute', bottom: 30, left: 12, background: '#fff', color: PENDING_FEMA_COLOR, border: `1px dashed ${PENDING_FEMA_COLOR}`, padding: '6px 9px', fontSize: 11, pointerEvents: 'none' }}>
          {pendingFloodLabels.map(label => <div key={label}>{label}</div>)}
          {floodTruncated && <div>FEMA overlay is incomplete at this extent.</div>}
        </div>
      )}
      {showParcels && zoomLevel < MIN_PARCEL_ZOOM && (
        <div style={{
          position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(46,56,64,0.85)', color: '#E4E6E8', padding: '6px 16px',
          borderRadius: '20px', fontSize: '13px', pointerEvents: 'none',
          fontFamily: designTokens.typography.fontFamily.display,
        }}>
          Zoom in to see parcels
        </div>
      )}
      {anyWellsActive && zoomLevel < MIN_WELLS_ZOOM && (
        <div style={{
          position: 'absolute', top: showParcels && zoomLevel < MIN_PARCEL_ZOOM ? '48px' : '12px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(46,56,64,0.85)', color: '#E4E6E8', padding: '6px 16px',
          borderRadius: '20px', fontSize: '13px', pointerEvents: 'none',
          fontFamily: designTokens.typography.fontFamily.display,
        }}>
          Zoom in to see wells
        </div>
      )}

      {/* Measurement toolbar - top-right, below NavigationControl */}
      <div style={{
        position: 'absolute',
        top: '120px',
        right: '10px',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '4px',
      }}>
        <button
          onClick={() => onMeasurementActiveChange(!measurementActive)}
          title={measurementActive ? 'Exit measure mode (M)' : 'Measure distance (M)'}
          style={{
            width: '36px', height: '36px',
            background: measurementActive ? designTokens.colors.measurement : '#3A4650',
            color: '#E4E6E8',
            border: measurementActive ? 'none' : '1px solid rgba(80,98,114,0.20)',
            borderRadius: designTokens.borderRadius.md,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: measurementActive
              ? `0 0 0 2px ${designTokens.colors.measurement}44, 0 2px 6px rgba(0,0,0,0.2)`
              : '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          <Ruler size={18} />
        </button>
        <button
          onClick={() => onAreaActiveChange(!areaActive)}
          title={areaActive ? 'Exit area mode (A)' : 'Measure acreage — drag a rectangle; Esc clears (A)'}
          style={{
            width: '36px', height: '36px',
            background: areaActive ? designTokens.colors.measurement : '#3A4650',
            color: '#E4E6E8',
            border: areaActive ? 'none' : '1px solid rgba(80,98,114,0.20)',
            borderRadius: designTokens.borderRadius.md,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: areaActive
              ? `0 0 0 2px ${designTokens.colors.measurement}44, 0 2px 6px rgba(0,0,0,0.2)`
              : '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          <SquareDashed size={18} />
        </button>
        {measurementCount > 0 && (
          <button
            onClick={() => {
              mClearAll();
              measurementSegmentTotalRef.current = 0;
              if (map.current) updateMeasurementSources(map.current);
            }}
            title="Clear all measurements"
            style={{
              width: '36px', height: '36px',
              background: '#3A4650', color: designTokens.colors.error,
              border: '1px solid rgba(80,98,114,0.20)',
              borderRadius: designTokens.borderRadius.md,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}
          >
            <X size={14} />
          </button>
        )}
        <button
          onClick={onToggleSatellite}
          title={isSatellite ? 'Switch to map view' : 'Switch to satellite view'}
          style={{
            width: '36px', height: '36px',
            background: isSatellite ? designTokens.colors.brand : '#3A4650',
            color: '#E4E6E8',
            border: isSatellite ? 'none' : '1px solid rgba(80,98,114,0.20)',
            borderRadius: designTokens.borderRadius.md,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isSatellite
              ? `0 0 0 2px ${designTokens.colors.brand}44, 0 2px 6px rgba(0,0,0,0.2)`
              : '0 2px 6px rgba(0,0,0,0.2)',
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: '13px', fontWeight: 600,
          }}
        >
          {isSatellite ? '\u{1F30D}' : '\u{1F5FA}'}
        </button>
        <button
          onClick={onToggleCountyLines}
          title={showCountyLines ? 'Hide county boundaries' : 'Show county boundaries'}
          style={{
            width: '36px', height: '36px',
            background: showCountyLines ? designTokens.colors.brand : '#3A4650',
            color: '#E4E6E8',
            border: showCountyLines ? 'none' : '1px solid rgba(80,98,114,0.20)',
            borderRadius: designTokens.borderRadius.md,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: showCountyLines
              ? `0 0 0 2px ${designTokens.colors.brand}44, 0 2px 6px rgba(0,0,0,0.2)`
              : '0 2px 6px rgba(0,0,0,0.2)',
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: '11px', fontWeight: 700, letterSpacing: '-0.5px',
          }}
        >
          Co
        </button>
        <button
          onClick={() => onTerrainChange(!show3DTerrain)}
          aria-pressed={show3DTerrain}
          title={show3DTerrain ? 'Exit 3D terrain' : '3D terrain · visual context (right-click drag to change view)'}
          style={{
            width: '36px', height: '36px',
            background: show3DTerrain ? designTokens.colors.brand : '#3A4650',
            color: '#E4E6E8',
            border: show3DTerrain ? 'none' : '1px solid rgba(80,98,114,0.20)',
            borderRadius: designTokens.borderRadius.md,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: show3DTerrain
              ? `0 0 0 2px ${designTokens.colors.brand}44, 0 2px 6px rgba(0,0,0,0.2)`
              : '0 2px 6px rgba(0,0,0,0.2)',
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: '11px', fontWeight: 700, letterSpacing: '-0.5px',
          }}
        >
          3D
        </button>
        {show3DTerrain && (
          <div style={{
            background: '#3A4650',
            border: '1px solid rgba(80,98,114,0.20)',
            borderRadius: designTokens.borderRadius.md,
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            padding: '5px 8px 3px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
          }}>
            <span style={{
              fontFamily: designTokens.typography.fontFamily.mono,
              fontSize: '10px', color: '#8A9CAA',
            }}>
              {terrainExaggeration}&times;
            </span>
            <input
              type="range" min={1} max={10} step={1}
              value={terrainExaggeration}
              onChange={e => setTerrainExaggeration(Number(e.target.value))}
              title="Vertical exaggeration"
              style={{ width: '76px', accentColor: designTokens.colors.brand, cursor: 'pointer' }}
            />
          </div>
        )}
        {areaActive && (
          <div style={{
            background: 'rgba(46,56,64,0.88)', color: '#F4ECD8',
            padding: '4px 10px', borderRadius: designTokens.borderRadius.md,
            fontFamily: designTokens.typography.fontFamily.display,
            fontSize: '10px', fontWeight: 600, letterSpacing: '0.3px',
            maxWidth: '200px', textAlign: 'right', pointerEvents: 'none',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}>
            Area — drag a rectangle for acreage · Esc clears
          </div>
        )}
      </div>

      {/* Floating map status bar */}
      <div style={{
        position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(46,56,64,0.85)', borderRadius: '9999px',
        padding: '6px 16px', pointerEvents: 'none',
        display: 'flex', alignItems: 'center', gap: '0',
        fontFamily: designTokens.typography.fontFamily.display,
        fontSize: '12px', color: '#E4E6E8',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      }}>
        {[
          showParcels && (
            <span key="parcels" style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: designTokens.colors.brand, marginRight: 6, flexShrink: 0 }} />
              {parcelCount.toLocaleString()} parcel{parcelCount !== 1 ? 's' : ''}
            </span>
          ),
          pipelineCount > 0 && <span key="pipelines">{pipelineCount.toLocaleString()} pipeline{pipelineCount !== 1 ? 's' : ''}</span>,
          anyWellsActive && wellCount > 0 && <span key="wells">{wellCount.toLocaleString()}{wellsTruncated ? '+' : ''} well{wellCount !== 1 ? 's' : ''}</span>,
          showFiber && fiberCount > 0 && <span key="fiber">{fiberCount.toLocaleString()}{fiberTruncated ? '+' : ''} fiber hex{fiberCount !== 1 ? 'es' : ''}</span>,
          (countyLabel || selectedParcelCounty) && <span key="county">{countyLabel || selectedParcelCounty}</span>,
        ].filter(Boolean).map((item, i, arr) => (
          <React.Fragment key={(item as React.ReactElement).key}>
            {i > 0 && <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.2)', margin: '0 12px', flexShrink: 0 }} />}
            {item}
          </React.Fragment>
        ))}
      </div>

    </div>
  );
};
